// ── BeeLove Admin Panel ───────────────────────────────────────────────────
// Industry-standard SaaS admin: sidebar nav + content area + data tables
// Every section reads/writes from Supabase — no mock data

import React, { useState, useEffect, useRef } from 'react';
import { useApp } from '../contexts/AppContext';
import { useTr } from '../utils/useTr';
import { db, dbUpdate, supabase } from '../utils/supabase';
import { PLANS, ALA_CARTE, FEATURE_LABELS, DEFAULT_FEATURE_MATRIX } from '../utils/monetization';
import { TranslationsTab, StaticPagesTab } from '../components/AdminCMS';
import CategoryTreePicker from '../components/CategoryTreePicker';
import { loadHiveGroups, saveHiveGroups, getUsersForGroup } from '../utils/hiveGroups';
import { getNodeById, getLabel, countMatches } from '../utils/categoryTree';
import { DEFAULT_COMPAT_WEIGHTS, AXIS_META, getCompatWeights, saveCompatWeights } from '../utils/matching';
import { trialPhrase } from '../utils/formatTrial';
import { getBadges } from '../utils/badges';
import { getCompletionSteps } from '../utils/profileCompletion';
import { BOOKING_PROVIDERS, triggerBooking, upsertScoutBooking } from '../utils/bookingProviders';
import { TabMarketing as CMLTabMarketing } from './CMLDashboard';
import { EventModal } from './Events';

const QUIZ_AXES = ['repair','conflict','accountability','generosity','presence','narrative','stability','boredom','social','stakes'];

// ── Constants ─────────────────────────────────────────────────────────────
const ADMIN_PIN = process.env.REACT_APP_ADMIN_PIN || null;

// ── Helpers ───────────────────────────────────────────────────────────────
const pct  = (a, b) => b ? Math.round(a / b * 100) + '%' : '0%';
const fmt  = (n) => Number(n || 0).toLocaleString();
const fmtC = (n, cur = 'CAD') => { const sym = cur === 'USD' ? 'US$' : '$'; return `${sym}${Number(n||0).toLocaleString('en-CA', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`; };
const ago  = (d) => { if (!d) return '—'; const s = Math.floor((Date.now() - new Date(d)) / 1000); if (s < 60) return 'just now'; if (s < 3600) return Math.floor(s/60) + 'm ago'; if (s < 86400) return Math.floor(s/3600) + 'h ago'; return Math.floor(s/86400) + 'd ago'; };

// CSV export — columns is [{ key, label, map? }]. Values are RFC-4180 quoted.
function exportCSV(filename, rows, columns) {
  const esc = (v) => {
    if (v == null) return '';
    const s = String(v);
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const header = columns.map(c => esc(c.label || c.key)).join(',');
  const body = rows.map(r => columns.map(c => {
    const raw = c.map ? c.map(r) : r[c.key];
    return esc(raw instanceof Date ? raw.toISOString() : raw);
  }).join(',')).join('\n');
  const csv = '﻿' + header + '\n' + body; // BOM for Excel UTF-8
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url;
  a.download = `${filename}_${new Date().toISOString().slice(0,10)}.csv`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ── Design system — hardcoded light admin (no CSS var deps) ───────────────
const C = {
  bg:       '#F4F2EF',
  surface:  '#FFFFFF',
  border:   '#E5E2DC',
  borderMd: '#D0CCC4',
  text:     '#1A1714',
  sub:      '#5C5650',
  muted:    '#8C8680',
  accent:   '#C9A84C',
  green:    '#1A7A4A',
  greenBg:  '#E8F5EE',
  red:      '#C0392B',
  redBg:    '#FDECEA',
  blue:     '#1B5FAA',
  blueBg:   '#E8F0FA',
  warn:     '#92600A',
  warnBg:   '#FEF6E4',
  radius:   '6px',
  radiusLg: '10px',
};

// ── Reusable layout components ────────────────────────────────────────────
function PageHeader({ title, sub, action }) {
  return (
    <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:24, paddingBottom:16, borderBottom:`0.5px solid ${C.border}` }}>
      <div>
        <h1 style={{ fontSize:20, fontWeight:500, color:C.text, margin:0 }}>{title}</h1>
        {sub && <p style={{ fontSize:13, color:C.sub, margin:'4px 0 0' }}>{sub}</p>}
      </div>
      {action}
    </div>
  );
}

function Btn({ children, onClick, variant = 'default', size = 'md', disabled }) {
  const styles = {
    default: { background:C.surface, border:`0.5px solid ${C.borderMd}`, color:C.text },
    primary: { background:C.accent,  border:'none', color:'#0F0D1A' },
    danger:  { background:C.redBg,   border:`0.5px solid ${C.red}`, color:C.red },
    ghost:   { background:'transparent', border:'none', color:C.sub },
  };
  const pads = { sm:'4px 10px', md:'7px 14px', lg:'9px 18px' };
  return (
    <button onClick={onClick} disabled={disabled} style={{
      ...styles[variant], padding:pads[size], borderRadius:C.radius,
      fontSize:13, fontWeight:500, cursor:disabled?'not-allowed':'pointer',
      opacity:disabled?0.5:1, display:'inline-flex', alignItems:'center', gap:6,
      transition:'opacity 0.15s', lineHeight:1,
    }}>{children}</button>
  );
}

function Badge({ children, color = 'default' }) {
  const colors = {
    default: { bg:C.bg, color:C.sub },
    green:   { bg:C.greenBg, color:C.green },
    red:     { bg:C.redBg,   color:C.red },
    blue:    { bg:C.blueBg,  color:C.blue },
    amber:   { bg:C.warnBg,  color:C.warn },
    gold:    { bg:'rgba(201,168,76,0.15)', color:C.accent },
  };
  const s = colors[color] || colors.default;
  return (
    <span style={{ background:s.bg, color:s.color, fontSize:11, padding:'2px 8px', borderRadius:99, fontWeight:500, display:'inline-block' }}>
      {children}
    </span>
  );
}

function KpiCard({ label, value, sub, color, spark }) {
  return (
    <div style={{ background:C.surface, border:`0.5px solid ${C.border}`, borderRadius:C.radiusLg, padding:'14px 16px', position:'relative', overflow:'hidden' }}>
      {spark && (
        <div style={{ position:'absolute', bottom:8, right:10, opacity:0.5 }}>
          <Sparkline data={spark} color={color||C.accent} width={60} height={22}/>
        </div>
      )}
      <p style={{ fontSize:12, color:C.sub, margin:'0 0 6px', fontWeight:500 }}>{label}</p>
      <p style={{ fontSize:24, fontWeight:500, color:color||C.text, margin:0, lineHeight:1 }}>{value}</p>
      {sub && <p style={{ fontSize:12, color:C.muted, margin:'4px 0 0' }}>{sub}</p>}
    </div>
  );
}

function DataTable({ columns, rows, onRowClick, emptyMsg = 'No data', selectable = false, selectedIds, onSelectionChange }) {
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  const handleSort = (key) => {
    if (!key) return;
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const sorted = sortKey ? [...rows].sort((a, b) => {
    const av = a[sortKey] ?? ''; const bv = b[sortKey] ?? '';
    const cmp = typeof av === 'number' ? av - bv : String(av).localeCompare(String(bv));
    return sortDir === 'asc' ? cmp : -cmp;
  }) : rows;

  const allSelected = selectable && rows.length > 0 && rows.every(r => selectedIds?.has(r.id));
  const someSelected = selectable && rows.some(r => selectedIds?.has(r.id));

  const toggleAll = () => {
    if (allSelected) onSelectionChange?.(new Set());
    else onSelectionChange?.(new Set(rows.map(r => r.id)));
  };
  const toggleOne = (id, e) => {
    e.stopPropagation();
    const next = new Set(selectedIds || []);
    next.has(id) ? next.delete(id) : next.add(id);
    onSelectionChange?.(next);
  };

  const colCount = columns.length + (selectable ? 1 : 0);

  return (
    <div style={{ border:`0.5px solid ${C.border}`, borderRadius:C.radiusLg, overflow:'hidden' }}>
      <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
        <thead>
          <tr style={{ background:C.bg }}>
            {selectable && (
              <th style={{ width:36, padding:'9px 8px 9px 14px', borderBottom:`0.5px solid ${C.border}` }}>
                <input type="checkbox" checked={allSelected}
                  ref={el => { if (el) el.indeterminate = someSelected && !allSelected; }}
                  onChange={toggleAll}
                  style={{ accentColor:C.accent, cursor:'pointer', width:14, height:14 }}/>
              </th>
            )}
            {columns.map(col => (
              <th key={col.key}
                onClick={() => col.sortable !== false && col.label && handleSort(col.key)}
                style={{ padding:'9px 14px', textAlign:'left', fontWeight:500, color:C.sub, fontSize:12, borderBottom:`0.5px solid ${C.border}`, whiteSpace:'nowrap', cursor: col.label ? 'pointer' : 'default', userSelect:'none' }}>
                <span style={{ display:'inline-flex', alignItems:'center', gap:4 }}>
                  {col.label}
                  {col.label && sortKey === col.key && (
                    <span style={{ fontSize:9, color:C.accent }}>{sortDir === 'asc' ? '▲' : '▼'}</span>
                  )}
                  {col.label && sortKey !== col.key && (
                    <span style={{ fontSize:9, color:C.border }}>⇅</span>
                  )}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.length === 0 ? (
            <tr><td colSpan={colCount} style={{ padding:32, textAlign:'center', color:C.muted }}>{emptyMsg}</td></tr>
          ) : sorted.map((row, i) => (
            <tr key={row.id || i}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              style={{ borderBottom:`0.5px solid ${C.border}`, cursor:onRowClick?'pointer':'default', transition:'background 0.1s', background: selectable && selectedIds?.has(row.id) ? 'rgba(201,168,76,0.05)' : '' }}
              onMouseEnter={e => { if (onRowClick && !selectedIds?.has(row.id)) e.currentTarget.style.background = C.bg; }}
              onMouseLeave={e => { e.currentTarget.style.background = selectable && selectedIds?.has(row.id) ? 'rgba(201,168,76,0.05)' : ''; }}
            >
              {selectable && (
                <td style={{ width:36, padding:'10px 8px 10px 14px', verticalAlign:'middle' }}>
                  <input type="checkbox" checked={selectedIds?.has(row.id)||false}
                    onChange={e => toggleOne(row.id, e)}
                    onClick={e=>e.stopPropagation()}
                    style={{ accentColor:C.accent, cursor:'pointer', width:14, height:14 }}/>
                </td>
              )}
              {columns.map(col => (
                <td key={col.key} style={{ padding:'10px 14px', color:C.text, verticalAlign:'middle' }}>
                  {col.render ? col.render(row[col.key], row) : (row[col.key] ?? '—')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Modal({ title, onClose, children, width = 520 }) {
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:24 }}
         onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background:C.surface, borderRadius:C.radiusLg, border:`0.5px solid ${C.border}`, width:'100%', maxWidth:width, maxHeight:'90vh', overflow:'auto' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'16px 20px', borderBottom:`0.5px solid ${C.border}` }}>
          <h2 style={{ margin:0, fontSize:16, fontWeight:500, color:C.text }}>{title}</h2>
          <button onClick={onClose} style={{ background:'none', border:'none', color:C.muted, cursor:'pointer', fontSize:20, lineHeight:1, padding:4 }}>×</button>
        </div>
        <div style={{ padding:'20px' }}>{children}</div>
      </div>
    </div>
  );
}

function FormField({ label, children, hint }) {
  return (
    <div style={{ marginBottom:16 }}>
      <label style={{ display:'block', fontSize:12, fontWeight:500, color:C.sub, marginBottom:6 }}>{label}</label>
      {children}
      {hint && <p style={{ fontSize:11, color:C.muted, margin:'4px 0 0' }}>{hint}</p>}
    </div>
  );
}

function Input({ value, onChange, placeholder, type = 'text', style: s }) {
  return (
    <input type={type} value={value} onChange={onChange} placeholder={placeholder}
      style={{ width:'100%', padding:'8px 10px', border:`0.5px solid ${C.borderMd}`, borderRadius:C.radius, fontSize:13, color:C.text, background:C.surface, outline:'none', boxSizing:'border-box', ...s }} />
  );
}

function Select({ value, onChange, options }) {
  return (
    <select value={value} onChange={onChange} style={{ width:'100%', padding:'8px 10px', border:`0.5px solid ${C.borderMd}`, borderRadius:C.radius, fontSize:13, color:C.text, background:C.surface, outline:'none' }}>
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

function SearchBar({ value, onChange, placeholder = 'Search…' }) {
  return (
    <div style={{ position:'relative', width:260 }}>
      <svg style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', pointerEvents:'none' }} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={C.muted} strokeWidth="2">
        <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
      </svg>
      <input value={value} onChange={onChange} placeholder={placeholder}
        style={{ width:'100%', padding:'7px 10px 7px 30px', border:`0.5px solid ${C.border}`, borderRadius:C.radius, fontSize:13, color:C.text, background:C.surface, outline:'none', boxSizing:'border-box' }} />
    </div>
  );
}

function IconBtn({ title, onClick, variant, danger, color, children }) {
  const rv = variant || (danger ? 'delete' : 'neutral');
  const V = {
    view:    { bg:'#3B82F6', hbg:'#2563EB', col:'#fff' },
    confirm: { bg:'#22C55E', hbg:'#16A34A', col:'#fff' },
    edit:    { bg:C.accent,  hbg:'#B8953F', col:'#0A0A0A' },
    delete:  { bg:'#EF4444', hbg:'#DC2626', col:'#fff' },
    warn:    { bg:'#F59E0B', hbg:'#D97706', col:'#0A0A0A' },
    neutral: { bg: color ? color+'22' : C.surface, hbg: color ? color+'44' : C.bg, col: color || C.muted },
  };
  const ICO = {
    view:    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z"/><circle cx="12" cy="12" r="3"/></svg>,
    confirm: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>,
    edit:    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>,
    delete:  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  };
  const v = V[rv] || V.neutral;
  return (
    <button title={title} onClick={e => { e.stopPropagation(); onClick?.(); }}
      style={{ width:30, height:30, display:'inline-flex', alignItems:'center', justifyContent:'center',
        border:'none', borderRadius:'50%', background:v.bg,
        cursor:'pointer', color:v.col, flexShrink:0, transition:'background 0.15s', fontSize:13 }}
      onMouseEnter={e => e.currentTarget.style.background = v.hbg}
      onMouseLeave={e => e.currentTarget.style.background = v.bg}>
      {ICO[rv] || children}
    </button>
  );
}

function BatchBar({ count, onClear, children }) {
  if (!count) return null;
  return (
    <div style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 14px', marginBottom:12,
      background:C.bg, border:`0.5px solid ${C.borderMd}`, borderRadius:C.radius, flexWrap:'wrap' }}>
      <span style={{ fontSize:13, fontWeight:500, color:C.text, marginRight:4 }}>{count} sélectionné{count > 1 ? 's' : ''}</span>
      {children}
      <button onClick={onClear} style={{ marginLeft:'auto', background:'none', border:'none', color:C.muted, cursor:'pointer', fontSize:20, lineHeight:1, padding:'0 2px' }}>×</button>
    </div>
  );
}

function EmptyState({ icon, title, sub, action }) {
  return (
    <div style={{ textAlign:'center', padding:'48px 24px' }}>
      <div style={{ fontSize:32, marginBottom:12 }}>{icon}</div>
      <p style={{ fontSize:15, fontWeight:500, color:C.text, margin:'0 0 6px' }}>{title}</p>
      <p style={{ fontSize:13, color:C.muted, margin:'0 0 16px' }}>{sub}</p>
      {action}
    </div>
  );
}

// ── Skeleton loader ───────────────────────────────────────────────────────
function Skeleton({ width = '100%', height = 14, radius = 4, style: s }) {
  return (
    <div style={{ width, height, borderRadius:radius, background:`linear-gradient(90deg,${C.border} 25%,${C.bg} 50%,${C.border} 75%)`, backgroundSize:'200% 100%', animation:'shimmer 1.4s infinite', ...s }}/>
  );
}
function SkeletonTable({ cols = 5, rows = 6 }) {
  return (
    <div style={{ background:C.surface, border:`0.5px solid ${C.border}`, borderRadius:C.radiusLg, overflow:'hidden' }}>
      <style>{`@keyframes shimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}`}</style>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} style={{ display:'grid', gridTemplateColumns:`repeat(${cols},1fr)`, gap:12, padding:'12px 16px', borderBottom:`0.5px solid ${C.border}` }}>
          {Array.from({ length: cols }).map((_, j) => (
            <Skeleton key={j} height={12} width={j === 0 ? '70%' : '50%'} />
          ))}
        </div>
      ))}
    </div>
  );
}

// ── Sparkline (SVG) ───────────────────────────────────────────────────────
function Sparkline({ data = [], color = C.accent, width = 80, height = 28 }) {
  if (data.length < 2) return null;
  const max = Math.max(...data, 1);
  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * width;
    const y = height - (v / max) * (height - 2) - 1;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
  const fill = `0,${height} ${pts} ${width},${height}`;
  return (
    <svg width={width} height={height} style={{ display:'block', overflow:'visible' }}>
      <polyline points={fill} fill={color} fillOpacity="0.1" stroke="none"/>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round"/>
    </svg>
  );
}

// ── Inline confirm button ─────────────────────────────────────────────────
function ConfirmBtn({ children, onConfirm, variant = 'danger', size = 'sm', confirmLabel = 'Confirmer ?' }) {
  const [confirming, setConfirming] = useState(false);
  if (confirming) return (
    <span style={{ display:'inline-flex', alignItems:'center', gap:4 }}>
      <Btn size={size} variant={variant} onClick={() => { setConfirming(false); onConfirm(); }}>{confirmLabel}</Btn>
      <Btn size={size} onClick={() => setConfirming(false)}>✕</Btn>
    </span>
  );
  return <Btn size={size} variant={variant} onClick={() => setConfirming(true)}>{children}</Btn>;
}

// ── Keyboard shortcut toast ───────────────────────────────────────────────
function ShortcutToast({ label }) {
  return (
    <div style={{ position:'fixed', bottom:24, right:24, zIndex:9999, background:C.text, color:C.surface, fontSize:12, padding:'6px 14px', borderRadius:C.radiusLg, fontWeight:500, pointerEvents:'none', opacity:0.92 }}>
      {label}
    </div>
  );
}

// ── Sidebar navigation ────────────────────────────────────────────────────
// 'network' groups are scoped to the active network; 'global' groups apply platform-wide.
const NAV_GROUPS = [
  {
    label: 'Overview',
    scope: 'network',
    items: [
      { id:'dashboard',   label:'Dashboard',      icon:'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6' },
      { id:'growth',      label:'Growth',         icon:'M13 7h8m0 0v8m0-8l-8 8-4-4-6 6' },
    ],
  },
  {
    label: 'Community',
    scope: 'network',
    items: [
      { id:'users',                label:'Users',          icon:'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z' },
      { id:'invitations',          label:'Invitations',    icon:'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z' },
      { id:'cml',                  label:'CML Managers',   icon:'M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z' },
      { id:'discovery_loop',       label:'Likes & Matches',icon:'M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z' },
      { id:'conversations_admin',  label:'Messages',       icon:'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z' },
      { id:'referrals_admin',      label:'Referrals',      icon:'M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1' },
      { id:'moderation',           label:'Moderation',     icon:'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z' },
    ],
  },
  {
    label: 'Engagement',
    scope: 'network',
    items: [
      { id:'events_admin',         label:'Events',         icon:'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' },
      { id:'scout_admin',        label:'Scout',          icon:'M12 2l8.66 5v10L12 22l-8.66-5V7z' },
      { id:'venues_admin',         label:'Venues',         icon:'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6' },
      { id:'notifications_admin',  label:'Notifications',  icon:'M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.437L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9' },
      { id:'partnership_docs',     label:'Partner Docs',   icon:'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
    ],
  },
  {
    label: 'Business',
    scope: 'network',
    items: [
      { id:'revenue',     label:'Revenue',        icon:'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z' },
      { id:'monetization',label:'Plans & Pricing',icon:'M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z' },
      { id:'sponsors',    label:'Sponsors',       icon:'M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z' },
    ],
  },
  {
    label: 'Product',
    scope: 'global',
    items: [
      { id:'categories',  label:'Categories',     icon:'M3 7h18M3 12h18M3 17h18' },
      { id:'hive_groups', label:'Hive groups',    icon:'M17 20h5v-2a3 3 0 00-5.356-1.857M9 20H4v-2a3 3 0 015.356-1.857M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z' },
      { id:'gamification',label:'Gamification',   icon:'M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z' },
      { id:'quiz_dist',   label:'Quiz distribution',icon:'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' },
      { id:'funnel',      label:'Completion funnel',icon:'M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z' },
      { id:'usage',       label:'Usage & Boosts',  icon:'M13 10V3L4 14h7v7l9-11h-7z' },
      { id:'axes',        label:'Compat axes',    icon:'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' },
      { id:'ab_tests',    label:'A/B Tests',      icon:'M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4' },
      { id:'features',    label:'Control center', icon:'M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4' },
      { id:'appconfig',   label:'App config',     icon:'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z' },
    ],
  },
  {
    label: 'Platform',
    scope: 'global',
    items: [
      { id:'networks',    label:'Networks',       icon:'M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064' },
      { id:'countries',   label:'Countries',      icon:'M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064' },
      { id:'translations',label:'Translations',   icon:'M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129' },
      { id:'content',     label:'Site text',      icon:'M4 6h16M4 12h16M4 18h7' },
    ],
  },
];

// ── Network filter (sidebar top) — two cascading selects ─────────────────
const COUNTRY_FLAGS  = { CA:'🇨🇦', FR:'🇫🇷', BE:'🇧🇪', CH:'🇨🇭', US:'🇺🇸', GB:'🇬🇧', DE:'🇩🇪', ES:'🇪🇸', IT:'🇮🇹', PT:'🇵🇹' };
const COUNTRY_LABELS = { CA:'Canada', FR:'France', BE:'Belgique', CH:'Suisse', US:'United States', GB:'United Kingdom', DE:'Deutschland', ES:'España', IT:'Italia', PT:'Portugal' };

function netLabel(n) {
  if (!n) return '';
  const flag = COUNTRY_FLAGS[n.country] || '🌐';
  return `${flag} ${n.label || n.id}`;
}
// Derive readable city name from a network_id slug (e.g. "ca_montreal" → "Montreal")
const netCity = (id) => id ? id.split('_').slice(1).map(w => w[0].toUpperCase() + w.slice(1)).join(' ') : '—';

function NetworkFilter({ networks, selectedCountry, selectedNetwork, onCountryChange, onNetworkChange }) {
  // Unique country codes present in networks list
  const countries = [...new Set(networks.map(n => n.country).filter(Boolean))].sort();
  // Cities available for the selected country
  const cities = selectedCountry ? networks.filter(n => n.country === selectedCountry) : [];

  const selStyle = (hasValue) => ({
    width:'100%', padding:'7px 10px', borderRadius:C.radius,
    border:`0.5px solid ${hasValue ? C.accent : C.borderMd}`,
    background: hasValue ? 'rgba(201,168,76,0.07)' : C.surface,
    fontSize:12, color: hasValue ? C.text : C.muted,
    outline:'none', cursor:'pointer', appearance:'none',
    backgroundImage:`url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='%238C8680' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E")`,
    backgroundRepeat:'no-repeat', backgroundPosition:'right 8px center', paddingRight:24,
  });

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
      {/* Country */}
      <select
        value={selectedCountry || ''}
        onChange={e => { onCountryChange(e.target.value || null); onNetworkChange(null); }}
        style={selStyle(!!selectedCountry)}>
        <option value="">Tous les pays</option>
        {countries.map(c => (
          <option key={c} value={c}>
            {COUNTRY_FLAGS[c] || ''} {COUNTRY_LABELS[c] || c}
          </option>
        ))}
      </select>
      {/* City — only visible once a country is selected */}
      {selectedCountry && (
        <select
          value={selectedNetwork || ''}
          onChange={e => onNetworkChange(e.target.value || null)}
          style={selStyle(!!selectedNetwork)}>
          <option value="">Toutes les villes</option>
          {cities.map(n => (
            <option key={n.id} value={n.id}>
              {n.label || n.id}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}

function NavIcon({ path }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d={path}/>
    </svg>
  );
}

// ── PIN gate ──────────────────────────────────────────────────────────────
function PinGate({ onUnlock }) {
  const [pin, setPin] = useState('');
  const [err, setErr] = useState(false);
  const submit = () => {
    if (!ADMIN_PIN) return;
    if (pin === ADMIN_PIN) { onUnlock(); }
    else { setErr(true); setPin(''); setTimeout(() => setErr(false), 1500); }
  };
  return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:C.bg }}>
      <div style={{ background:C.surface, border:`0.5px solid ${C.border}`, borderRadius:C.radiusLg, padding:32, width:320, textAlign:'center' }}>
        <div style={{ width:44, height:44, borderRadius:'50%', background:'rgba(201,168,76,0.15)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 16px', fontSize:20 }}>🐝</div>
        <h2 style={{ fontSize:18, fontWeight:500, color:C.text, margin:'0 0 4px' }}>Admin access</h2>
        <p style={{ fontSize:13, color:C.sub, margin:'0 0 20px' }}>Enter your PIN to continue</p>
        {!ADMIN_PIN && <p style={{ fontSize:12, color:C.red, margin:'0 0 12px' }}>REACT_APP_ADMIN_PIN not configured</p>}
        <input type="password" value={pin} onChange={e => setPin(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && submit()}
          placeholder="••••" maxLength={8} autoFocus disabled={!ADMIN_PIN}
          style={{ width:'100%', padding:'10px 12px', textAlign:'center', fontSize:22, letterSpacing:8, border:`1px solid ${err ? C.red : C.border}`, borderRadius:C.radius, background:C.surface, color:C.text, outline:'none', boxSizing:'border-box', marginBottom:12 }}/>
        {err && <p style={{ fontSize:12, color:C.red, margin:'0 0 8px' }}>Incorrect PIN</p>}
        <Btn variant="primary" onClick={submit} size="lg">{' '}Unlock admin{' '}</Btn>
      </div>
    </div>
  );
}

// ── Dashboard section ─────────────────────────────────────────────────────
function DashboardTab({ users, events }) {
  const total    = users.length;
  const verified = users.filter(u => u.verified).length;
  const premium  = users.filter(u => u.plan !== 'free').length;
  const honey    = users.filter(u => u.plan === 'honey').length;
  const royal    = users.filter(u => u.plan === 'royal' || u.plan === 'founder').length;
  const recent7  = users.filter(u => u.created_at && (Date.now() - new Date(u.created_at)) < 7*86400000).length;
  const pubEvents= (events||[]).filter(e => e.status === 'published').length;

  // Build 7-day daily signup sparkline data
  const signupSpark = Array.from({ length: 7 }, (_, i) => {
    const day = new Date(); day.setDate(day.getDate() - (6 - i)); day.setHours(0,0,0,0);
    const next = new Date(day); next.setDate(next.getDate() + 1);
    return users.filter(u => { const d = u.created_at ? new Date(u.created_at) : null; return d && d >= day && d < next; }).length;
  });
  const premiumSpark = Array.from({ length: 7 }, (_, i) => {
    const day = new Date(); day.setDate(day.getDate() - (6 - i)); day.setHours(0,0,0,0);
    const next = new Date(day); next.setDate(next.getDate() + 1);
    return users.filter(u => u.plan !== 'free' && u.created_at && new Date(u.created_at) >= day && new Date(u.created_at) < next).length;
  });

  return (
    <>
      <PageHeader title="Dashboard" sub="Overview — live data from Supabase" />
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:12 }}>
        <KpiCard label="Total members"   value={fmt(total)}    sub={`+${recent7} this week`} spark={signupSpark} />
        <KpiCard label="Verified"        value={fmt(verified)} sub={pct(verified,total)+' of total'} color={C.green} />
        <KpiCard label="Paying members"  value={fmt(premium)}  sub={pct(premium,total)+' conversion'} color={C.accent} spark={premiumSpark} />
        <KpiCard label="Active events"   value={fmt(pubEvents)} sub="published & upcoming" color={C.blue} />
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12, marginBottom:24 }}>
        <KpiCard label="Honey plan"      value={fmt(honey)}    sub={pct(honey,total)} />
        <KpiCard label="Royal plan"      value={fmt(royal)}    sub={pct(royal,total)} color={C.accent} />
        <KpiCard label="Unverified"      value={fmt(total-verified)} sub="need email confirm" />
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap:16 }}>
        <div style={{ background:C.surface, border:`0.5px solid ${C.border}`, borderRadius:C.radiusLg, padding:16 }}>
          <p style={{ fontSize:13, fontWeight:500, color:C.text, margin:'0 0 12px' }}>Recent signups</p>
          {users.slice(0,8).map(u => (
            <div key={u.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'8px 0', borderBottom:`0.5px solid ${C.border}` }}>
              <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                <div style={{ width:32, height:32, borderRadius:'50%', background:'rgba(201,168,76,0.15)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, fontWeight:500, color:C.accent, flexShrink:0 }}>
                  {(u.name||'?')[0].toUpperCase()}
                </div>
                <div>
                  <p style={{ fontSize:13, fontWeight:500, color:C.text, margin:0 }}>{u.name}</p>
                  <p style={{ fontSize:11, color:C.muted, margin:0 }}>{u.city||'—'}</p>
                </div>
              </div>
              <div style={{ textAlign:'right' }}>
                <Badge color={(u.plan === 'royal' || u.plan === 'founder') ? 'gold' : u.plan === 'honey' ? 'amber' : 'default'}>{u.plan}</Badge>
                <p style={{ fontSize:11, color:C.muted, margin:'3px 0 0' }}>{ago(u.created_at)}</p>
              </div>
            </div>
          ))}
          {users.length === 0 && <p style={{ fontSize:13, color:C.muted, textAlign:'center', padding:24 }}>No users yet</p>}
        </div>
        <div style={{ background:C.surface, border:`0.5px solid ${C.border}`, borderRadius:C.radiusLg, padding:16 }}>
          <p style={{ fontSize:13, fontWeight:500, color:C.text, margin:'0 0 12px' }}>Plan distribution</p>
          {[['free', 'Free', total-premium], ['honey', 'Honey', honey], ['royal', 'Royal', royal]].map(([id, label, count]) => (
            <div key={id} style={{ marginBottom:12 }}>
              <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
                <span style={{ fontSize:12, color:C.sub }}>{label}</span>
                <span style={{ fontSize:12, fontWeight:500, color:C.text }}>{fmt(count)}</span>
              </div>
              <div style={{ height:6, background:C.bg, borderRadius:99, overflow:'hidden' }}>
                <div style={{ height:'100%', borderRadius:99, width:pct(count,total||1), background: id==='royal'?C.accent: id==='honey'?'#B8922A':'var(--color-border-primary)' }}/>
              </div>
            </div>
          ))}
          <div style={{ marginTop:16, paddingTop:16, borderTop:`0.5px solid ${C.border}` }}>
            <p style={{ fontSize:12, color:C.sub, margin:'0 0 8px', fontWeight:500 }}>Upcoming events</p>
            {(events||[]).filter(e=>e.status==='published').slice(0,3).map(e => (
              <div key={e.id} style={{ padding:'6px 0', borderBottom:`0.5px solid ${C.border}` }}>
                <p style={{ fontSize:12, fontWeight:500, color:C.text, margin:0 }}>{e.title}</p>
                <p style={{ fontSize:11, color:C.muted, margin:'2px 0 0' }}>{e.date}</p>
              </div>
            ))}
            {(events||[]).filter(e=>e.status==='published').length === 0 && (
              <p style={{ fontSize:12, color:C.muted }}>No upcoming events</p>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

// ── Growth section ────────────────────────────────────────────────────────
// Priority KPIs for Founding phase — volume over revenue.
function GrowthTab({ networkId, setTab }) {
  const [users,       setUsers]      = useState([]);
  const [ratings,     setRatings]    = useState([]);
  const [invitations, setInvitations]= useState([]);
  const [matches,     setMatches]    = useState(0);
  const [messages7d,  setMessages7d] = useState(0);
  const [loading,     setLoading]    = useState(true);

  useEffect(() => {
    const since30d = new Date(Date.now() - 30 * 86400000).toISOString();
    const since7d  = new Date(Date.now() - 7  * 86400000).toISOString();
    Promise.all([
      supabase?.from('user_core')
        .select('id, name, created_at, signup_source, invited_by, quiz_answers, invites_remaining, network_id, founding_commit_at')
        .order('created_at', { ascending: false }),
      supabase?.from('friend_ratings').select('target_id, created_at'),
      supabase?.from('invitations').select('id, used_count, created_at, created_by'),
      supabase?.from('matches').select('id', { count: 'exact', head: true }),
      supabase?.from('messages').select('id', { count: 'exact', head: true }).gte('created_at', since7d),
    ]).then(([u, r, i, m, msg]) => {
      setUsers(u?.data || []);
      setRatings(r?.data || []);
      setInvitations(i?.data || []);
      setMatches(m?.count || 0);
      setMessages7d(msg?.count || 0);
      setLoading(false);
    });
  }, [networkId]);

  if (loading) return <><PageHeader title="Growth" sub="Loading live metrics…"/><SkeletonTable cols={4} rows={6}/></>;

  const exportSignups = () => {
    exportCSV('beelove_signups', users, [
      { key:'id',                 label:'id' },
      { key:'name',               label:'name' },
      { key:'created_at',         label:'signup_at' },
      { key:'signup_source',      label:'source' },
      { key:'invited_by',         label:'referrer_id' },
      { key:'network_id',         label:'network' },
      { key:'founding_commit_at', label:'founding_commit_at' },
      { label:'ratings_received', map: r => ratingsByTarget[r.id] || 0 },
      { label:'quiz_done',        map: r => Object.keys(r.quiz_answers || {}).length >= 8 ? 'yes' : 'no' },
    ]);
  };

  const now = Date.now();
  const last1d  = users.filter(u => now - new Date(u.created_at) < 1 * 86400000).length;
  const last7d  = users.filter(u => now - new Date(u.created_at) < 7 * 86400000).length;
  const last30d = users.filter(u => now - new Date(u.created_at) < 30 * 86400000).length;
  const total   = users.length;

  // Acquisition by source
  const bySource = users.reduce((acc, u) => {
    const s = u.signup_source || 'direct';
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});
  const sFriend = bySource['friend_rating'] || 0;
  const sInvite = bySource['invite_code']   || 0;
  const sDirect = bySource['direct']        || 0;

  // Friend-vouching health
  const ratingsByTarget = ratings.reduce((acc, r) => { acc[r.target_id] = (acc[r.target_id] || 0) + 1; return acc; }, {});
  const usersWithAnyRating  = Object.keys(ratingsByTarget).length;
  const usersAt4Plus        = Object.values(ratingsByTarget).filter(c => c >= 4).length;
  const avgRatingsPerUser   = total ? (ratings.length / total) : 0;

  // Activation funnel
  const fSignedUp   = total;
  const fCommitted  = users.filter(u => u.founding_commit_at).length;
  const fQuizDone   = users.filter(u => Object.keys(u.quiz_answers || {}).length >= 8).length;
  const fInviteSent = invitations.filter(i => i.used_count > 0).length; // proxy: at least one code used
  const fFirstRate  = usersWithAnyRating;
  const fReveal     = usersAt4Plus;

  // Viral metrics
  // K-factor (rough): invites sent per user × conversion rate from invite
  const totalInvitesUsed = invitations.reduce((sum, i) => sum + (i.used_count || 0), 0);
  const invitesSentAvg   = total ? (totalInvitesUsed / total) : 0;
  const friendRatingConv = invitations.length ? ((sInvite + sFriend) / Math.max(invitations.length, 1)) : 0;
  const kFactor          = invitesSentAvg * friendRatingConv;

  const signupSpark = Array.from({ length: 14 }, (_, i) => {
    const day = new Date(); day.setDate(day.getDate() - (13 - i)); day.setHours(0,0,0,0);
    const next = new Date(day); next.setDate(next.getDate() + 1);
    return users.filter(u => { const d = new Date(u.created_at); return d >= day && d < next; }).length;
  });

  const FunnelRow = ({ label, count, base }) => {
    const p = base ? (count / base) * 100 : 0;
    return (
      <div style={{ marginBottom:10 }}>
        <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4, fontSize:12 }}>
          <span style={{ color:C.sub }}>{label}</span>
          <span style={{ color:C.text, fontWeight:500 }}>{fmt(count)} · {p.toFixed(0)}%</span>
        </div>
        <div style={{ height:6, background:C.bg, borderRadius:99, overflow:'hidden' }}>
          <div style={{ height:'100%', width:`${Math.min(100, p)}%`, background:C.accent, borderRadius:99 }}/>
        </div>
      </div>
    );
  };

  const SourceBar = ({ label, count, color }) => {
    const p = total ? (count / total) * 100 : 0;
    return (
      <div style={{ marginBottom:10 }}>
        <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4, fontSize:12 }}>
          <span style={{ color:C.sub }}>{label}</span>
          <span style={{ color:C.text, fontWeight:500 }}>{fmt(count)} · {p.toFixed(0)}%</span>
        </div>
        <div style={{ height:6, background:C.bg, borderRadius:99, overflow:'hidden' }}>
          <div style={{ height:'100%', width:`${Math.min(100, p)}%`, background:color, borderRadius:99 }}/>
        </div>
      </div>
    );
  };

  return (
    <>
      <PageHeader title="Growth"
        sub="Volume-first KPIs · Founding phase"
        action={<Btn size="sm" onClick={exportSignups}>⭳ Export signups CSV</Btn>}/>

      {/* Hero row */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:12 }}>
        <KpiCard label="Total members"      value={fmt(total)}    sub={`+${last7d} past 7d`} spark={signupSpark} />
        <KpiCard label="Signups last 24h"   value={fmt(last1d)}   sub={`${last30d} past 30d`} color={C.green} />
        <KpiCard label="K-factor (viral)"   value={kFactor.toFixed(2)} sub={kFactor >= 1 ? 'Self-sustaining ✓' : 'Needs boost'} color={kFactor >= 1 ? C.green : C.accent} />
        <KpiCard label="Users with ratings" value={pct(usersWithAnyRating, total)} sub={`${fmt(usersWithAnyRating)} of ${fmt(total)}`} color={C.blue} />
      </div>

      {/* Funnel + Source breakdown */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:16 }}>
        <div style={{ background:C.surface, border:`0.5px solid ${C.border}`, borderRadius:C.radiusLg, padding:16 }}>
          <p style={{ fontSize:13, fontWeight:500, color:C.text, margin:'0 0 12px' }}>Activation funnel</p>
          <FunnelRow label="Signed up"                   count={fSignedUp}   base={fSignedUp}/>
          <FunnelRow label="Signed Founding pact"        count={fCommitted}  base={fSignedUp}/>
          <FunnelRow label="Quiz completed (≥8 axes)"    count={fQuizDone}   base={fSignedUp}/>
          <FunnelRow label="Sent ≥1 invite (used)"       count={fInviteSent} base={fSignedUp}/>
          <FunnelRow label="Received ≥1 friend rating"   count={fFirstRate}  base={fSignedUp}/>
          <FunnelRow label="Reached reveal (4+ ratings)" count={fReveal}     base={fSignedUp}/>
        </div>

        <div style={{ background:C.surface, border:`0.5px solid ${C.border}`, borderRadius:C.radiusLg, padding:16 }}>
          <p style={{ fontSize:13, fontWeight:500, color:C.text, margin:'0 0 12px' }}>Acquisition by source</p>
          <SourceBar label="Friend rating CTA"     count={sFriend} color={C.green}/>
          <SourceBar label="Invite code (direct)"  count={sInvite} color={C.accent}/>
          <SourceBar label="Direct / organic"      count={sDirect} color={C.blue}/>
          <p style={{ fontSize:11, color:C.muted, margin:'12px 0 0', lineHeight:1.5 }}>
            Double down on the highest-converting channel. Friend-rating signups are the viral loop — every boost there compounds.
          </p>
        </div>
      </div>

      {/* Engagement + vouching */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:12 }}>
        <KpiCard label="Total matches"       value={fmt(matches)}       sub="all-time" />
        <KpiCard label="Messages (7d)"       value={fmt(messages7d)}    sub="past 7 days" />
        <KpiCard label="Avg ratings / user"  value={avgRatingsPerUser.toFixed(1)} sub="health of vouching loop" />
        <KpiCard label="Reveal threshold"    value={pct(usersAt4Plus, total)} sub={`${usersAt4Plus} hit 4+ ratings`} color={C.green}/>
      </div>

      {/* Stuck cohorts — one-click nudge */}
      <div style={{ background:C.surface, border:`0.5px solid ${C.border}`, borderRadius:C.radiusLg, padding:16, marginBottom:16 }}>
        <p style={{ fontSize:13, fontWeight:500, color:C.text, margin:'0 0 3px' }}>Stuck cohorts</p>
        <p style={{ fontSize:11, color:C.muted, margin:'0 0 14px' }}>
          Users blocked at a funnel stage. Click "Nudge" to pre-fill a segmented notification.
        </p>
        {(() => {
          const cohorts = [
            {
              id: 'no_pact',
              label: 'Signed up but haven\'t signed the Founding pact',
              count: users.filter(u => !u.founding_commit_at).length,
              copy: { title: "Ton engagement Founding Member t'attend", body: "Tu as rejoint BeeLove — il te reste à finaliser ton pacte Founding Member (30 secondes). Tes amis attendent ton lien." },
            },
            {
              id: 'no_quiz',
              label: 'Signed up but quiz not completed',
              count: users.filter(u => Object.keys(u.quiz_answers || {}).length < 8).length,
              copy: { title: 'Termine ton quiz — 2 minutes', body: "Ton profil de compatibilité a besoin de tes réponses aux 10 axes. C'est ce qui te rend visible aux bonnes personnes." },
            },
            {
              id: 'no_ratings',
              label: 'No friend ratings received yet',
              count: users.filter(u => !(ratingsByTarget[u.id] > 0)).length,
              copy: { title: 'Ton profil attend tes amis', body: "Envoie ton lien à 3-5 amis. Dès le premier avis, ton radar de compatibilité s'active et tu apparais en premier pour les personnes compatibles." },
            },
            {
              id: 'below_reveal',
              label: 'Between 1 and 3 ratings — almost at reveal',
              count: users.filter(u => (ratingsByTarget[u.id] || 0) > 0 && (ratingsByTarget[u.id] || 0) < 4).length,
              copy: { title: 'Plus que quelques avis pour débloquer ton portrait', body: "Tu as déjà des avis d'amis — il te manque juste quelques réponses pour déclencher la révélation complète. Partage ton lien une dernière fois." },
            },
            {
              id: 'inactive_7d',
              label: 'Inactive for 7 days+',
              count: users.filter(u => !u.last_seen_at || (Date.now() - new Date(u.last_seen_at)) > 7*86400000).length,
              copy: { title: 'On pense à toi 🐝', body: 'Des nouveaux profils compatibles ont rejoint la Ruche cette semaine. Un petit tour pour voir ?' },
            },
          ];
          return cohorts.map(c => (
            <div key={c.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 0', borderBottom:`0.5px solid ${C.border}` }}>
              <div style={{ flex:1, minWidth:0 }}>
                <p style={{ fontSize:13, color:C.text, margin:'0 0 2px' }}>{c.label}</p>
                <p style={{ fontSize:11, color:C.muted, margin:0 }}>{fmt(c.count)} user{c.count === 1 ? '' : 's'}</p>
              </div>
              <Btn size="sm" disabled={!c.count} onClick={() => {
                sessionStorage.setItem('beelove_admin_nudge_prefill', JSON.stringify({
                  segment: c.id,
                  title: c.copy.title,
                  body: c.copy.body,
                  type: 'announcement',
                }));
                setTab?.('notifications_admin');
              }}>
                Nudge →
              </Btn>
            </div>
          ));
        })()}
      </div>

      {/* Top referrers leaderboard */}
      <div style={{ background:C.surface, border:`0.5px solid ${C.border}`, borderRadius:C.radiusLg, padding:16 }}>
        <p style={{ fontSize:13, fontWeight:500, color:C.text, margin:'0 0 12px' }}>Top referrers (users who brought others)</p>
        {(() => {
          const referCounts = users.filter(u => u.invited_by).reduce((acc, u) => {
            acc[u.invited_by] = (acc[u.invited_by] || 0) + 1;
            return acc;
          }, {});
          const ranked = Object.entries(referCounts)
            .map(([id, count]) => ({ id, count, name: users.find(u => u.id === id)?.name || '—' }))
            .sort((a, b) => b.count - a.count)
            .slice(0, 8);
          if (!ranked.length) return <p style={{ fontSize:12, color:C.muted, margin:0 }}>No referrals yet — the viral loop hasn't started.</p>;
          return ranked.map(r => (
            <div key={r.id} style={{ display:'flex', justifyContent:'space-between', padding:'8px 0', borderBottom:`0.5px solid ${C.border}` }}>
              <span style={{ fontSize:13, color:C.text }}>{r.name}</span>
              <span style={{ fontSize:13, fontWeight:500, color:C.accent }}>{r.count} brought</span>
            </div>
          ));
        })()}
      </div>
    </>
  );
}

// ── Revenue section ───────────────────────────────────────────────────────
function RevenueTab({ networkId, networkIdsJson }) {
  const [revenue, setRevenue] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const networkIds = networkIdsJson ? JSON.parse(networkIdsJson) : null;
    setLoading(true);
    Promise.all([
      db.cml_revenue_full()?.select('*').order('created_at', { ascending: false }).limit(50),
      db.payments()?.select('*').order('created_at', { ascending: false }).limit(100),
    ]).then(async ([r, p]) => {
      if (r?.data) setRevenue(r.data);
      if (p?.data) {
        const ids = [...new Set(p.data.map(x => x.user_id).filter(Boolean))];
        const { data: udata } = ids.length ? await supabase.from('users').select('id,name,email,network_id').in('id', ids) : { data: [] };
        const um = Object.fromEntries((udata || []).map(u => [u.id, u]));
        const enriched = p.data.map(x => ({ ...x, user: um[x.user_id]||null }));
        setPayments(networkIds ? enriched.filter(x => networkIds.includes(x.user?.network_id)) : enriched);
      }
      setLoading(false);
    });
  }, [networkId, networkIdsJson]);

  const totalGross   = payments.filter(p=>p.status==='succeeded').reduce((s,p) => s+Number(p.amount),0);
  const totalRefunds = payments.filter(p=>p.status==='refunded').reduce((s,p) => s+Number(p.amount),0);
  const net          = totalGross - totalRefunds;
  const eventPayments= payments.filter(p=>p.type==='event' && p.status==='succeeded');
  const subPayments  = payments.filter(p=>p.type!=='event' && p.status==='succeeded');

  return (
    <>
      <PageHeader title="Revenue" sub="Payments processed via Stripe — live from Supabase" />
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:24 }}>
        <KpiCard label="Gross revenue"   value={fmtC(totalGross)} sub="all time" color={C.green}/>
        <KpiCard label="Net revenue"     value={fmtC(net)}        sub="after refunds"/>
        <KpiCard label="Refunds"         value={fmtC(totalRefunds)} sub={payments.filter(p=>p.status==='refunded').length+' transactions'} color={C.red}/>
        <KpiCard label="Event revenue"   value={fmtC(eventPayments.reduce((s,p)=>s+Number(p.amount),0))} sub={eventPayments.length+' tickets sold'}/>
      </div>
      {loading ? (
        <p style={{ color:C.muted, textAlign:'center', padding:32 }}>Loading…</p>
      ) : (
        <DataTable
          columns={[
            { key:'created_at', label:'Date',    render: v => <span style={{ color:C.sub, fontSize:12 }}>{v ? new Date(v).toLocaleDateString() : '—'}</span> },
            { key:'user',       label:'User',    render: (v,r) => v ? <div><p style={{margin:0,fontSize:13}}>{v.name}</p><p style={{margin:0,fontSize:11,color:C.muted}}>{v.email}</p></div> : <span style={{color:C.muted}}>—</span> },
            { key:'amount',     label:'Amount',  render: (v,r) => <span style={{ fontWeight:500 }}>{fmtC(v, r.currency||'CAD')}</span> },
            { key:'currency',   label:'Currency',render: v => <Badge>{v||'CAD'}</Badge> },
            { key:'type',       label:'Type',    render: v => <Badge color={v==='event'?'blue':'gold'}>{v}</Badge> },
            { key:'status',     label:'Status',  render: v => <Badge color={v==='succeeded'?'green':v==='refunded'?'amber':'red'}>{v}</Badge> },
            ...(!networkId ? [{ key:'_city', label:'City', sortable:false, render:(_,r) => r.user?.network_id ? <span style={{ fontSize:11, color:C.muted }}>{netCity(r.user.network_id)}</span> : '—' }] : []),
          ]}
          rows={payments}
          emptyMsg="No payments yet"
        />
      )}
    </>
  );
}

// ── Users section ─────────────────────────────────────────────────────────
function UsersTab({ users, setUsers, jumpToUser, networkId }) {
  const [search, setSearch]       = useState('');
  const [filter, setFilter]       = useState('all');
  const [selected, setSelected]   = useState(null);
  const [saving, setSaving]       = useState(false);
  const [checkedIds, setCheckedIds] = useState(new Set());
  const [bulkPlan, setBulkPlan]   = useState('honey');
  const [bulkMsg, setBulkMsg]     = useState('');

  useEffect(() => {
    if (!jumpToUser) return;
    const found = users.find(u => u.id === jumpToUser);
    if (found) setSelected(found);
  }, [jumpToUser]);

  const filtered = users.filter(u => {
    const q = search.toLowerCase();
    const matchQ = !q || (u.name||'').toLowerCase().includes(q) || (u.email||'').toLowerCase().includes(q) || (u.city||'').toLowerCase().includes(q);
    const matchF = filter === 'all' || u.plan === filter || (filter === 'unverified' && !u.verified) || (filter === 'deleted' && u.deleted_at);
    return matchQ && matchF;
  });

  const saveUser = async (form) => {
    setSaving(true);
    await dbUpdate.userCore(form.id, { name: form.name, role: form.role, plan: form.plan, verified: form.verified });
    setUsers(prev => prev.map(u => u.id === form.id ? { ...u, ...form } : u));
    setSaving(false);
    setSelected(null);
  };

  const softDelete = async (uid) => {
    await dbUpdate.userCore(uid, { deleted_at: new Date().toISOString(), email: `deleted_${uid}@beelove.invalid` });
    setUsers(prev => prev.filter(u => u.id !== uid));
    setSelected(null);
  };

  const applyBulkPlan = async () => {
    if (!checkedIds.size) return;
    await Promise.all([...checkedIds].map(id => dbUpdate.userCore(id, { plan: bulkPlan })));
    setUsers(prev => prev.map(u => checkedIds.has(u.id) ? { ...u, plan: bulkPlan } : u));
    setBulkMsg(`✓ Plan updated to ${bulkPlan} for ${checkedIds.size} users`);
    setCheckedIds(new Set());
  };
  const applyBulkDelete = async () => {
    if (!checkedIds.size) return;
    const ts = new Date().toISOString();
    await Promise.all([...checkedIds].map(id => dbUpdate.userCore(id, { deleted_at: ts, email: `deleted_${id}@beelove.invalid` })));
    setUsers(prev => prev.filter(u => !checkedIds.has(u.id)));
    setBulkMsg(`✓ ${checkedIds.size} accounts deleted`);
    setCheckedIds(new Set());
  };

  const exportUsers = () => {
    exportCSV('beelove_users', filtered, [
      { key:'id',             label:'id' },
      { key:'name',           label:'name' },
      { key:'email',          label:'email' },
      { key:'city',           label:'city' },
      { key:'network_id',     label:'network' },
      { key:'plan',           label:'plan' },
      { key:'role',           label:'role' },
      { key:'verified',       label:'verified' },
      { key:'signup_source',  label:'signup_source' },
      { key:'invited_by',     label:'invited_by' },
      { key:'founding_commit_at', label:'founding_commit_at' },
      { key:'invites_remaining',  label:'invites_remaining' },
      { key:'created_at',     label:'created_at' },
      { key:'last_seen_at',   label:'last_seen_at' },
    ]);
  };

  return (
    <>
      <PageHeader title="Users"
        sub={`${filtered.length} of ${users.length} members`}
        action={<Btn size="sm" onClick={exportUsers}>⭳ Export CSV</Btn>}/>
      <div style={{ display:'flex', gap:10, marginBottom:16, flexWrap:'wrap' }}>
        <SearchBar value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name, email, city…" />
        <Select value={filter} onChange={e => setFilter(e.target.value)} options={[
          { value:'all', label:'All plans' }, { value:'free', label:'Free' },
          { value:'honey', label:'Honey' }, { value:'royal', label:'Royal' },
          { value:'unverified', label:'Unverified' }, { value:'deleted', label:'Deleted' },
        ]}/>
      </div>

      {/* Bulk action bar */}
      {checkedIds.size > 0 && (
        <div style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 14px', marginBottom:10, background:C.blueBg, border:`0.5px solid ${C.blue}`, borderRadius:C.radius, flexWrap:'wrap' }}>
          <span style={{ fontSize:13, fontWeight:500, color:C.blue, flexShrink:0 }}>{checkedIds.size} selected</span>
          <span style={{ color:C.muted, fontSize:13 }}>→ Change plan to</span>
          <select value={bulkPlan} onChange={e => setBulkPlan(e.target.value)}
            style={{ fontSize:12, padding:'3px 8px', border:`0.5px solid ${C.borderMd}`, borderRadius:C.radius, background:C.surface, color:C.text }}>
            {['free','honey','royal'].map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          <Btn size="sm" variant="primary" onClick={applyBulkPlan}>Apply</Btn>
          <ConfirmBtn size="sm" variant="danger" onConfirm={applyBulkDelete} confirmLabel={`Delete ${checkedIds.size} accounts?`}>Delete all</ConfirmBtn>
          <button onClick={() => setCheckedIds(new Set())} style={{ background:'none', border:'none', color:C.muted, cursor:'pointer', fontSize:18, lineHeight:1, marginLeft:'auto' }}>×</button>
        </div>
      )}
      {bulkMsg && (
        <p style={{ fontSize:12, color:C.green, marginBottom:8 }}>{bulkMsg}</p>
      )}

      <DataTable
        selectable selectedIds={checkedIds} onSelectionChange={setCheckedIds}
        columns={[
          { key:'name',       label:'Name',    render:(v,r) => <div style={{display:'flex',alignItems:'center',gap:8}}><div style={{width:28,height:28,borderRadius:'50%',background:'rgba(201,168,76,0.15)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,fontWeight:600,color:C.accent,flexShrink:0}}>{(v||'?')[0].toUpperCase()}</div><div><p style={{margin:0,fontSize:13,fontWeight:500}}>{v}</p><p style={{margin:0,fontSize:11,color:C.muted}}>{r.email}</p></div></div> },
          { key:'city',       label:'City',    render: v => v||'—' },
          { key:'plan',       label:'Plan',    render: v => <Badge color={(v==='royal'||v==='founder')?'gold':v==='honey'?'amber':'default'}>{v}</Badge> },
          { key:'role',       label:'Role',    render: v => v !== 'member' ? <Badge color="blue">{v}</Badge> : <span style={{color:C.muted}}>member</span> },
          { key:'verified',   label:'Verified',render: v => <Badge color={v?'green':'default'}>{v?'yes':'no'}</Badge> },
          { key:'created_at', label:'Joined',  render: v => v ? ago(v) : '—' },
          ...(!networkId ? [{ key:'network_id', label:'City', render: v => v ? <span style={{ fontSize:11, color:C.muted }}>{netCity(v)}</span> : '—' }] : []),
          { key:'_icons', label:'', sortable:false, render:(_,r) => (
            <div style={{ display:'flex', gap:4 }} onClick={e => e.stopPropagation()}>
              <IconBtn variant="edit" title="Edit" onClick={() => setSelected(r)}/>
              <IconBtn title="Delete" danger onClick={() => window.confirm('Delete this account?') && softDelete(r.id)}>✕</IconBtn>
            </div>
          )},
        ]}
        rows={filtered}
        emptyMsg="No users found"
      />
      {selected && (
        <Modal title={`Edit — ${selected.name}`} onClose={() => setSelected(null)}>
          <UserEditForm user={selected} onSave={saveUser} onDelete={() => softDelete(selected.id)} saving={saving} />
        </Modal>
      )}
    </>
  );
}

function UserEditForm({ user, onSave, onDelete, saving }) {
  const [form, setForm]       = useState({ ...user });
  const [activity, setActivity] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Fetch activity counters in parallel — best-effort, non-blocking
  useEffect(() => {
    if (!user?.id) return;
    const since7d  = new Date(Date.now() - 7  * 86400000).toISOString();
    const since30d = new Date(Date.now() - 30 * 86400000).toISOString();
    const today = new Date().toISOString().slice(0, 10);
    Promise.all([
      supabase?.from('friend_ratings').select('id', { count: 'exact', head: true }).eq('target_id', user.id),
      supabase?.from('matches').select('id', { count: 'exact', head: true }).or(`user_a.eq.${user.id},user_b.eq.${user.id}`),
      supabase?.from('messages').select('id', { count: 'exact', head: true }).eq('sender_id', user.id),
      supabase?.from('messages').select('id', { count: 'exact', head: true }).eq('sender_id', user.id).gte('created_at', since7d),
      supabase?.from('invitations').select('used_count').eq('created_by', user.id),
      supabase?.from('user_core').select('id', { count: 'exact', head: true }).eq('invited_by', user.id),
      supabase?.from('user_core').select('id', { count: 'exact', head: true }).eq('invited_by', user.id).gte('created_at', since30d),
      supabase?.from('user_daily_usage').select('date,likes_count,super_likes_count,passes_count').eq('user_id', user.id).order('date', { ascending:false }).limit(7),
      supabase?.from('likes').select('created_at,to_user:user_core!to_user(name)').eq('from_user', user.id).order('created_at', { ascending:false }).limit(5),
      supabase?.from('matches').select('created_at,user_a,user_b,ua:user_core!user_a(name),ub:user_core!user_b(name)').or(`user_a.eq.${user.id},user_b.eq.${user.id}`).order('created_at', { ascending:false }).limit(5),
    ]).then(([r, m, msg, msg7, inv, ref, ref30, usage, recentLikes, recentMatches]) => {
      const totalInvitesSent = (inv?.data || []).reduce((sum, i) => sum + (i.used_count || 0), 0);
      setActivity({
        ratings: r?.count || 0,
        matches: m?.count || 0,
        messages: msg?.count || 0,
        messages7: msg7?.count || 0,
        invitesUsed: totalInvitesSent,
        refers: ref?.count || 0,
        refers30: ref30?.count || 0,
      });
      setTimeline({
        usage: usage?.data || [],
        recentLikes: recentLikes?.data || [],
        recentMatches: recentMatches?.data || [],
      });
    });
  }, [user?.id]);

  const Stat = ({ label, value, sub, color = C.text }) => (
    <div style={{ padding:'10px 12px', background:C.bg, borderRadius:C.radius, fontSize:12, textAlign:'center' }}>
      <p style={{ color:C.muted, margin:'0 0 4px', fontSize:10, letterSpacing:'0.05em', textTransform:'uppercase' }}>{label}</p>
      <p style={{ color, margin:0, fontSize:18, fontWeight:600 }}>{value}</p>
      {sub && <p style={{ color:C.muted, margin:'2px 0 0', fontSize:10 }}>{sub}</p>}
    </div>
  );

  return (
    <div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:16 }}>
        <FormField label="Name"><Input value={form.name||''} onChange={e => set('name', e.target.value)}/></FormField>
        <FormField label="Email"><Input value={form.email||''} onChange={e => set('email', e.target.value)}/></FormField>
        <FormField label="Plan">
          <Select value={form.plan||'free'} onChange={e => set('plan', e.target.value)} options={[{value:'free',label:'Free'},{value:'honey',label:'Honey'},{value:'royal',label:'Royal'},{value:'founder',label:'Founder'}]}/>
        </FormField>
        <FormField label="Role">
          <Select value={form.role||'member'} onChange={e => set('role', e.target.value)} options={[{value:'member',label:'Member'},{value:'scout',label:'Scout'},{value:'builder',label:'Builder'},{value:'connector',label:'Connector'},{value:'queen',label:'Queen'},{value:'cml',label:'CML'},{value:'admin',label:'Admin'}]}/>
        </FormField>
      </div>
      <FormField label="Verified">
        <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontSize:13 }}>
          <input type="checkbox" checked={!!form.verified} onChange={e => set('verified', e.target.checked)} />
          Email verified
        </label>
      </FormField>

      {/* Activity panel — live counters from DB */}
      <div style={{ marginTop:16, padding:'14px 14px 4px', background:C.bg, borderRadius:C.radius, border:`0.5px solid ${C.border}` }}>
        <p style={{ fontSize:11, letterSpacing:'0.08em', textTransform:'uppercase', color:C.muted, margin:'0 0 10px' }}>Activity</p>
        {activity == null ? (
          <p style={{ fontSize:12, color:C.muted, padding:'6px 0 12px', textAlign:'center' }}>Loading…</p>
        ) : (
          <>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:8, marginBottom:10 }}>
              <Stat label="Ratings"        value={fmt(activity.ratings)}    sub={activity.ratings >= 4 ? '✓ reveal' : `${4 - activity.ratings} to reveal`} color={activity.ratings >= 4 ? C.green : C.text}/>
              <Stat label="Matches"        value={fmt(activity.matches)}    sub="all-time"/>
              <Stat label="Messages"       value={fmt(activity.messages)}   sub={`+${activity.messages7} past 7d`}/>
              <Stat label="Brought"        value={fmt(activity.refers)}     sub={`+${activity.refers30} past 30d`} color={activity.refers > 0 ? C.accent : C.text}/>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:8 }}>
              <div style={{ padding:'8px 12px', background:C.surface, borderRadius:C.radius, fontSize:12, display:'flex', justifyContent:'space-between' }}>
                <span style={{ color:C.muted }}>Source</span>
                <Badge color={form.signup_source === 'friend_rating' ? 'green' : form.signup_source === 'invite_code' ? 'gold' : 'default'}>{form.signup_source || 'direct'}</Badge>
              </div>
              <div style={{ padding:'8px 12px', background:C.surface, borderRadius:C.radius, fontSize:12, display:'flex', justifyContent:'space-between' }}>
                <span style={{ color:C.muted }}>Founding pact</span>
                <span style={{ color: form.founding_commit_at ? C.green : C.muted }}>
                  {form.founding_commit_at ? '✓ Signed' : '— Not signed'}
                </span>
              </div>
              <div style={{ padding:'8px 12px', background:C.surface, borderRadius:C.radius, fontSize:12, display:'flex', justifyContent:'space-between' }}>
                <span style={{ color:C.muted }}>Last seen</span>
                <span style={{ color:C.text }}>{form.last_seen_at ? ago(form.last_seen_at) : '—'}</span>
              </div>
              <div style={{ padding:'8px 12px', background:C.surface, borderRadius:C.radius, fontSize:12, display:'flex', justifyContent:'space-between' }}>
                <span style={{ color:C.muted }}>Invites used</span>
                <span style={{ color:C.text }}>{activity.invitesUsed} of {fmt(form.invites_remaining ?? 25)} left</span>
              </div>
            </div>
          </>
        )}
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginTop:8 }}>
        <div style={{ padding:'10px 12px', background:C.bg, borderRadius:C.radius, fontSize:12 }}>
          <p style={{ color:C.muted, margin:'0 0 2px' }}>City</p><p style={{ color:C.text, margin:0 }}>{form.city||'—'}</p>
        </div>
        <div style={{ padding:'10px 12px', background:C.bg, borderRadius:C.radius, fontSize:12 }}>
          <p style={{ color:C.muted, margin:'0 0 2px' }}>Joined</p><p style={{ color:C.text, margin:0 }}>{form.created_at ? new Date(form.created_at).toLocaleDateString() : '—'}</p>
        </div>
      </div>
      {/* Timeline */}
      {timeline && (
        <div style={{ marginTop:16, padding:'14px', background:C.bg, borderRadius:C.radius, border:`0.5px solid ${C.border}` }}>
          <p style={{ fontSize:11, letterSpacing:'0.08em', textTransform:'uppercase', color:C.muted, margin:'0 0 10px' }}>Timeline — 7 days</p>

          {/* Daily usage sparkline */}
          {timeline.usage.length > 0 && (
            <div style={{ marginBottom:10 }}>
              <p style={{ fontSize:11, color:C.sub, margin:'0 0 6px' }}>Daily likes · passes · super-likes</p>
              <div style={{ display:'flex', gap:4 }}>
                {[...timeline.usage].reverse().map((d, i) => (
                  <div key={i} style={{ flex:1, fontSize:10, color:C.muted, textAlign:'center' }}>
                    <div style={{ display:'flex', flexDirection:'column', gap:2, alignItems:'center', marginBottom:4 }}>
                      <div style={{ background:C.accent, borderRadius:2, width:'100%', height:Math.max(2, (d.likes_count||0)*3) }}/>
                      <div style={{ background:C.red, borderRadius:2, width:'100%', height:Math.max(2, (d.passes_count||0)*1.5) }}/>
                    </div>
                    <span>{d.date?.slice(5)}</span>
                  </div>
                ))}
              </div>
              <div style={{ display:'flex', gap:8, marginTop:4 }}>
                <span style={{ fontSize:10, color:C.muted }}>
                  Today: <strong style={{ color:C.accent }}>{timeline.usage[0]?.likes_count || 0} likes</strong> · {timeline.usage[0]?.passes_count || 0} passes
                </span>
              </div>
            </div>
          )}

          {/* Recent likes sent */}
          {timeline.recentLikes.length > 0 && (
            <div style={{ marginBottom:8 }}>
              <p style={{ fontSize:11, color:C.sub, margin:'0 0 4px' }}>Last likes sent</p>
              {timeline.recentLikes.map((l, i) => (
                <div key={i} style={{ display:'flex', justifyContent:'space-between', fontSize:11, padding:'2px 0', borderBottom:`0.5px solid ${C.border}` }}>
                  <span style={{ color:C.text }}>♥ {l.to_user?.name || '—'}</span>
                  <span style={{ color:C.muted }}>{ago(l.created_at)}</span>
                </div>
              ))}
            </div>
          )}

          {/* Recent matches */}
          {timeline.recentMatches.length > 0 && (
            <div>
              <p style={{ fontSize:11, color:C.sub, margin:'0 0 4px' }}>Recent matches</p>
              {timeline.recentMatches.map((m, i) => {
                const other = m.user_a === user.id ? m.ub?.name : m.ua?.name;
                return (
                  <div key={i} style={{ display:'flex', justifyContent:'space-between', fontSize:11, padding:'2px 0', borderBottom:`0.5px solid ${C.border}` }}>
                    <span style={{ color:C.green }}>✓ {other || '—'}</span>
                    <span style={{ color:C.muted }}>{ago(m.created_at)}</span>
                  </div>
                );
              })}
            </div>
          )}

          {timeline.usage.length === 0 && timeline.recentLikes.length === 0 && timeline.recentMatches.length === 0 && (
            <p style={{ fontSize:12, color:C.muted, margin:0 }}>No recent activity.</p>
          )}
        </div>
      )}

      <div style={{ display:'flex', justifyContent:'space-between', marginTop:20, paddingTop:16, borderTop:`0.5px solid ${C.border}` }}>
        <ConfirmBtn onConfirm={onDelete} size="md" confirmLabel="Confirmer suppression">Delete account</ConfirmBtn>
        <Btn variant="primary" onClick={() => onSave(form)} disabled={saving}>{saving?'Saving…':'Save changes'}</Btn>
      </div>
    </div>
  );
}

// ── CML Managers section ──────────────────────────────────────────────────
function CMLTab({ cmlList, setCmlList, networkId }) {
  const { setPage } = useApp();
  const [loading, setLoading]   = useState(false);
  const [selected, setSelected] = useState(null);

  const viewAsCml = (userId) => {
    sessionStorage.setItem('cml_preview_id', userId);
    setPage('cml');
  };

  const promote = async (userId, stage) => {
    await db.cml_managers()?.update({ stage }).eq('user_id', userId);
    setCmlList(prev => prev.map(c => c.user_id === userId ? { ...c, stage } : c));
  };

  const toggleActive = async (userId, current) => {
    await db.cml_managers()?.update({ active: !current }).eq('user_id', userId);
    setCmlList(prev => prev.map(c => c.user_id === userId ? { ...c, active: !current } : c));
    if (selected?.user_id === userId) setSelected(prev => ({ ...prev, active: !current }));
  };

  const trigger = async (cmlId) => {
    // Trigger payout via Edge Function
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;
    await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/stripe/connect/payout`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ cmlId, amount: 0, currency: 'cad' }),
    });
  };

  return (
    <>
      <PageHeader title="CML Managers" sub="Community Manager Leaders — view, promote, trigger payouts" />
      <DataTable
        columns={[
          { key:'user', label:'Name', render:(v,r) => v ? <div><p style={{margin:0,fontSize:13,fontWeight:500}}>{v.name}</p><p style={{margin:0,fontSize:11,color:C.muted}}>{v.email}</p></div> : r.user_id?.slice(0,8) },
          { key:'stage', label:'Stage', render: v => <Badge color={v==='director'?'gold':v==='coordinator'?'blue':'default'}>{v||'ambassador'}</Badge> },
          { key:'active', label:'Status', render: v => <Badge color={v===false?'red':'green'}>{v===false?'Inactive':'Active'}</Badge> },
          ...(!networkId ? [{ key:'network_id', label:'City', render: v => v ? <span style={{ fontSize:11, color:C.muted }}>{netCity(v)}</span> : '—' }] : []),
          { key:'joined_at', label:'Since', render: v => v ? new Date(v).toLocaleDateString() : '—' },
          { key:'_icons', label:'', sortable:false, render:(_,r) => (
            <div style={{ display:'flex', gap:4 }}>
              <IconBtn variant="edit" title="Manage" onClick={() => setSelected(r)}/>
              <IconBtn variant="view" title="View as CML" onClick={() => viewAsCml(r.user_id)}/>
              <IconBtn
                variant={r.active===false ? 'confirm' : 'warn'}
                title={r.active===false ? 'Activate account' : 'Deactivate account'}
                onClick={() => toggleActive(r.user_id, r.active !== false)}
              />
            </div>
          )},
        ]}
        rows={cmlList}
        emptyMsg="No CML managers yet"
      />
      {selected && (
        <Modal title={`CML — ${selected.user?.name||'Manager'}`} onClose={() => setSelected(null)}>
          <FormField label="Stage">
            <Select value={selected.stage} onChange={e => promote(selected.user_id, e.target.value)} options={[
              {value:'ambassador',label:'Ambassador'},{value:'coordinator',label:'Coordinator'},{value:'director',label:'Director'}
            ]}/>
          </FormField>
          <FormField label="Account status">
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              <Badge color={selected.active===false?'red':'green'}>{selected.active===false?'Inactive':'Active'}</Badge>
              <Btn size="sm" variant={selected.active===false?'primary':'danger'} onClick={() => toggleActive(selected.user_id, selected.active !== false)}>
                {selected.active===false ? 'Activate' : 'Deactivate'}
              </Btn>
            </div>
          </FormField>
          <div style={{ padding:'12px', background:C.bg, borderRadius:C.radius, marginTop:8, fontSize:12 }}>
            <p style={{ color:C.muted, margin:'0 0 4px' }}>Stripe Connect</p>
            <p style={{ color:C.text, margin:0 }}>{selected.stripe_connect_id || 'Not connected — CML must complete onboarding'}</p>
          </div>
          <div style={{ marginTop:16, display:'flex', justifyContent:'flex-end' }}>
            <Btn variant="primary" onClick={() => setSelected(null)}>Close</Btn>
          </div>
        </Modal>
      )}
    </>
  );
}

// ── Events section ────────────────────────────────────────────────────────
function EventsTab({ events, setEvents, currentUser, networkId }) {
  const [search,   setSearch]   = useState('');
  const [filter,   setFilter]   = useState('all');
  const [editing,  setEditing]  = useState(null);
  const [saving,   setSaving]   = useState(false);
  const [selected, setSelected] = useState(new Set());

  const filtered = events.filter(e => {
    const q = search.toLowerCase();
    const matchQ = !q || (e.title||'').toLowerCase().includes(q) || (e.location||'').toLowerCase().includes(q);
    const matchF = filter === 'all' || e.status === filter;
    return matchQ && matchF;
  });

  const saveEvent = async (form) => {
    setSaving(true);
    const { pricing, ...eventFields } = form;
    const isNew = !form.id || typeof form.id === 'number';
    let eventId = form.id;
    if (isNew) {
      const { data } = await db.events()?.insert({ ...eventFields, cml_id: currentUser?.id, created_by: currentUser?.id }).select('id').single();
      eventId = data?.id;
    } else {
      await db.events()?.update(eventFields).eq('id', form.id);
    }
    if (eventId && pricing?.length) {
      await db.event_pricing()?.upsert(pricing.map(p => ({ event_id: eventId, plan: p.plan, price: p.price })), { onConflict: 'event_id,plan' });
    }
    const { data } = await db.events_full()?.select('*').order('date', { ascending: false });
    if (data) setEvents(data);
    setSaving(false);
    setEditing(null);
  };

  const deleteEvent = async (id) => {
    if (!window.confirm('Delete this event?')) return;
    await db.events()?.delete().eq('id', id);
    setEvents(prev => prev.filter(e => e.id !== id));
    setSelected(prev => { const n = new Set(prev); n.delete(id); return n; });
  };

  const bulkDelete = async () => {
    if (!selected.size || !window.confirm(`Delete ${selected.size} events?`)) return;
    await Promise.all([...selected].map(id => db.events()?.delete().eq('id', id)));
    setEvents(prev => prev.filter(e => !selected.has(e.id)));
    setSelected(new Set());
  };

  return (
    <>
      <PageHeader
        title="Events"
        sub={`${filtered.length} events`}
        action={<Btn variant="primary" onClick={() => setEditing({ status:'draft', network_id: networkId || 'ca_montreal', type:'apero', capacity:30, pricing:[{plan:'free',price:0},{plan:'honey',price:0},{plan:'royal',price:0}] })}>+ New event</Btn>}
      />
      <div style={{ display:'flex', gap:10, marginBottom:12 }}>
        <SearchBar value={search} onChange={e => setSearch(e.target.value)} placeholder="Search events…" />
        <Select value={filter} onChange={e => setFilter(e.target.value)} options={[
          {value:'all',label:'All statuses'},{value:'published',label:'Published'},{value:'draft',label:'Draft'},{value:'past',label:'Past'},
        ]}/>
      </div>
      <BatchBar count={selected.size} onClear={() => setSelected(new Set())}>
        <Btn size="sm" variant="danger" onClick={bulkDelete}>Delete selected</Btn>
      </BatchBar>
      <DataTable
        selectable selectedIds={selected} onSelectionChange={setSelected}
        columns={[
          { key:'title',   label:'Event',   render:(v,r) => <div><p style={{margin:0,fontSize:13,fontWeight:500}}>{v}</p><p style={{margin:0,fontSize:11,color:C.muted}}>{r.location}</p></div> },
          { key:'date',    label:'Date',    render: v => v||'—' },
          { key:'status',  label:'Status',  render: v => <Badge color={v==='published'?'green':v==='past'?'default':'amber'}>{v}</Badge> },
          { key:'capacity',label:'Capacity',render: v => fmt(v) },
          { key:'price',   label:'Price',   render:(v,r) => v!=null ? `$${v}` : '—' },
          ...(!networkId ? [{ key:'network_id', label:'City', render: v => v ? <span style={{ fontSize:11, color:C.muted }}>{netCity(v)}</span> : '—' }] : []),
          { key:'_icons', label:'', sortable:false, render:(v,r) => (
            <div style={{ display:'flex', gap:4 }}>
              <IconBtn variant="edit" title="Edit" onClick={() => setEditing(r)}/>
              <IconBtn title="Delete" danger onClick={() => deleteEvent(r.id)}>✕</IconBtn>
            </div>
          )},
        ]}
        rows={filtered}
        emptyMsg="No events — create one"
      />
      {editing && (
        <Modal title={editing.id ? 'Edit event' : 'New event'} onClose={() => setEditing(null)} width={620}>
          <EventForm event={editing} onSave={saveEvent} saving={saving} />
        </Modal>
      )}
    </>
  );
}

function EventForm({ event, onSave, saving }) {
  const { currentUser, lang } = useApp();
  const [form, setForm] = useState({
    ...event,
    is_free: event?.is_free ?? (event?.price === 0 && !event?.id),
    pricing: event.pricing || [
      { plan:'free',  price: event.price       || 0 },
      { plan:'honey', price: event.price_honey  || 0 },
      { plan:'royal', price: event.price_royal ?? event.price_gold ?? 0 },
    ],
  });
  const [venues,      setVenues]      = useState([]);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    supabase?.from('venues').select('id,name,area,photos').eq('is_active', true).order('name')
      .then(({ data }) => { if (data) setVenues(data); });
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setPrice = (plan, price) => setForm(f => ({ ...f, pricing: f.pricing.map(p => p.plan===plan ? {...p,price} : p) }));

  const handleVenueSelect = (venueId) => {
    set('venue_id', venueId || null);
    if (!venueId) return;
    const v = venues.find(v => v.id === venueId);
    if (v?.photos?.length && !form.image_url) set('image_url', v.photos[0]);
  };

  const linkedVenue = venues.find(v => v.id === form.venue_id);

  const previewEvent = {
    ...form,
    price:    form.is_free ? 0 : (form.pricing?.find(p => p.plan==='free')?.price || 0),
    image:    form.image_url || '',
    images:   form.image_url ? [form.image_url] : [],
    registered: 0,
    status:   'published',
  };

  return (
    <div>
      {showPreview && (
        <EventModal
          event={previewEvent}
          currentUser={currentUser}
          onClose={() => setShowPreview(false)}
          registered={false}
          attended={false}
          lang={lang || 'fr'}
        />
      )}

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
        <FormField label="Title"><Input value={form.title||''} onChange={e=>set('title',e.target.value)} placeholder="BeeLove Speed Dating — Plateau"/></FormField>
        <FormField label="Type">
          <Select value={form.type||'apero'} onChange={e=>set('type',e.target.value)} options={[{value:'speed_dating',label:'Speed Dating'},{value:'apero',label:'Apéro'},{value:'outdoor',label:'Outdoor'},{value:'themed',label:'Themed'},{value:'workshop',label:'Workshop'}]}/>
        </FormField>
        <FormField label="Date"><Input type="date" value={form.date||''} onChange={e=>set('date',e.target.value)}/></FormField>
        <FormField label="Status">
          <Select value={form.status||'draft'} onChange={e=>set('status',e.target.value)} options={[{value:'draft',label:'Draft'},{value:'published',label:'Published'},{value:'past',label:'Past'}]}/>
        </FormField>
        <FormField label="Début"><Input type="time" value={form.time||''} onChange={e=>set('time',e.target.value)}/></FormField>
        <FormField label="Fin"><Input type="time" value={form.end_time||''} onChange={e=>set('end_time',e.target.value)}/></FormField>
        <FormField label="Venue (nom affiché)"><Input value={form.location||''} onChange={e=>set('location',e.target.value)} placeholder="Café Resonance"/></FormField>
        <FormField label="Capacity"><Input type="number" value={form.capacity||30} onChange={e=>set('capacity',parseInt(e.target.value)||30)}/></FormField>
      </div>

      <FormField label="Address"><Input value={form.address||''} onChange={e=>set('address',e.target.value)} placeholder="5175 Ave du Parc, Montréal"/></FormField>

      {/* Venue link — auto-populates photo */}
      {venues.length > 0 && (
        <FormField label="Lier un venue (optionnel — remplit la photo)">
          <Select
            value={form.venue_id||''}
            onChange={e => handleVenueSelect(e.target.value)}
            options={[{value:'',label:'— aucun —'}, ...venues.map(v => ({value:v.id, label:`${v.name} · ${v.area}`}))]}
          />
        </FormField>
      )}

      {/* Photo */}
      <FormField label="Photo (URL)">
        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
          <Input value={form.image_url||''} onChange={e=>set('image_url',e.target.value)} placeholder="https://…"/>
          {linkedVenue?.photos?.length > 0 && (
            <Btn onClick={() => set('image_url', linkedVenue.photos[0])}>Photo du venue</Btn>
          )}
        </div>
        {form.image_url && (
          <img src={form.image_url} alt="" onError={e=>e.target.style.display='none'}
            style={{ marginTop:8, width:'100%', height:100, objectFit:'cover', borderRadius:C.radius, display:'block' }}/>
        )}
      </FormField>

      <FormField label="Description (optionnel)">
        <textarea value={form.description||''} onChange={e=>set('description',e.target.value)} rows={3}
          style={{ width:'100%', padding:'8px 10px', border:`0.5px solid ${C.borderMd}`, borderRadius:C.radius, fontSize:13, color:C.text, background:C.surface, resize:'vertical', outline:'none', boxSizing:'border-box' }}/>
      </FormField>

      {/* Free toggle */}
      <div style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 0', marginBottom:4 }}>
        <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontSize:13, color:C.text, userSelect:'none' }}>
          <input type="checkbox" checked={!!form.is_free} onChange={e => {
            const free = e.target.checked;
            setForm(f => ({ ...f, is_free: free, pricing: free ? f.pricing.map(p => ({...p, price:0})) : f.pricing }));
          }} style={{ width:15, height:15, accentColor:C.accent, cursor:'pointer' }}/>
          Événement gratuit
        </label>
        {form.is_free && <span style={{ fontSize:11, color:C.green, background:C.greenBg, padding:'2px 8px', borderRadius:99 }}>Gratuit · prix mis à 0</span>}
      </div>

      {/* Pricing by plan — hidden when free */}
      {!form.is_free && (
        <>
          <p style={{ fontSize:12, fontWeight:500, color:C.sub, marginBottom:8 }}>Pricing by plan</p>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10, marginBottom:16 }}>
            {(form.pricing||[]).map(p => (
              <FormField key={p.plan} label={`${p.plan.charAt(0).toUpperCase()+p.plan.slice(1)} ($)`}>
                <Input type="number" value={p.price} onChange={e => setPrice(p.plan, parseFloat(e.target.value)||0)}/>
              </FormField>
            ))}
          </div>
        </>
      )}

      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', paddingTop:16, borderTop:`0.5px solid ${C.border}` }}>
        <Btn variant="ghost" onClick={() => setShowPreview(true)}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z"/><circle cx="12" cy="12" r="3"/></svg>
          Aperçu utilisateur
        </Btn>
        <Btn variant="primary" onClick={() => onSave(form)} disabled={saving}>{saving?'Saving…':'Save event'}</Btn>
      </div>
    </div>
  );
}

// ── Scouts section ───────────────────────────────────────────────────────
function ScoutTab({ networkId }) {
  const [scouts,   setScouts]   = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [selected, setSelected] = useState(null);   // scout row
  const [detail,   setDetail]   = useState(null);   // { scout, members }
  const [detailLoading, setDetailLoading] = useState(false);
  const [waitlist, setWaitlist] = useState([]);
  const [wlTab,    setWlTab]    = useState('waitlist'); // 'waitlist' | 'all'

  const reload = () => {
    let cq = db.scouts()?.select('*,members:scout_members(user_id,confirmed,voted,cancelled_at)').order('created_at', { ascending:false }).limit(100);
    let wq = db.scout_waitlist()?.select('*,user:user_core!user_id(id,name,email,age,gender,score,plan)').order('joined_at', { ascending:false }).limit(200);
    if (networkId) { cq = cq?.eq('network_id', networkId); wq = wq?.eq('network_id', networkId); }
    Promise.all([cq, wq]).then(([c, w]) => {
      if (c?.data) setScouts(c.data);
      if (w?.data) setWaitlist(w.data);
      setLoading(false);
    });
  };

  const [invitations, setInvitations] = useState([]);

  useEffect(() => { reload(); }, [networkId]);

  const openDetail = async (scout) => {
    setSelected(scout);
    setDetailLoading(true);
    setInvitations([]);
    const [membersRaw, { data: invs }] = await Promise.all([
      supabase?.from('scout_members')
        .select('user_id,confirmed,voted,vote,cancelled_at,cancel_reason')
        .eq('scout_id', scout.id),
      supabase?.from('scout_invitations')
        .select('id,status,preferred_dates,created_at,responded_at,invitee:users!invitee_id(id,name)')
        .eq('scout_id', scout.id)
        .order('created_at', { ascending: true }),
    ]);
    const rawMembers = membersRaw?.data || [];
    const memberUserIds = rawMembers.map(m => m.user_id).filter(Boolean);
    let userMap = {};
    if (memberUserIds.length) {
      const { data: udata } = await supabase?.from('users').select('id,name,age,gender,plan,score').in('id', memberUserIds);
      userMap = Object.fromEntries((udata || []).map(u => [u.id, u]));
    }
    const members = rawMembers.map(m => ({ ...m, user: userMap[m.user_id] || null }));
    setDetail({ scout, members });
    setInvitations(invs || []);
    setDetailLoading(false);
  };

  const forceStatus = async (scoutId, status) => {
    await supabase?.from('scouts').update({ status }).eq('id', scoutId);
    setScouts(prev => prev.map(c => c.id === scoutId ? { ...c, status } : c));
    setDetail(prev => prev ? { ...prev, scout: { ...prev.scout, status } } : prev);
  };

  const removeMember = async (scoutId, userId) => {
    await supabase?.from('scout_members').delete().eq('scout_id', scoutId).eq('user_id', userId);
    setDetail(prev => prev ? { ...prev, members: prev.members.filter(m => m.user?.id !== userId) } : prev);
    setScouts(prev => prev.map(c => c.id === scoutId
      ? { ...c, members: (c.members||[]).filter(m => m.user_id !== userId) }
      : c));
  };

  const removeFromWaitlist = async (id) => {
    await supabase?.from('scout_waitlist').delete().eq('id', id);
    setWaitlist(prev => prev.filter(w => w.id !== id));
  };

  const composeScouts = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;
    const res = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/scouts/compose`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ networkId: networkId || 'ca_montreal', scoutType:'dinner' }),
    });
    const data = await res.json();
    alert(`Composed ${data.composed} scout(s)`);
    reload();
  };

  const statusColor = (s) => s==='confirmed'?'green':s==='proposed'||s==='waiting'?'blue':s==='past'?'default':s==='cancelled'?'red':'amber';

  return (
    <>
      <PageHeader
        title="Scouts"
        sub={`${scouts.length} scouts · ${waitlist.length} on waitlist`}
        action={<Btn variant="primary" onClick={composeScouts}>⚡ Compose scouts</Btn>}
      />

      <div style={{ display:'grid', gridTemplateColumns:'1fr 360px', gap:16 }}>

        {/* ── Circle list ── */}
        <div>
          {loading ? <SkeletonTable cols={5} rows={6}/> : (
            <DataTable
              columns={[
                { key:'type',   label:'Type',    render: (v,r) => (
                  <div style={{ display:'flex', gap:5, alignItems:'center', flexWrap:'wrap' }}>
                    <Badge>{v||'—'}</Badge>
                    {r.is_private && v !== 'date' && <Badge color="blue">PRIVÉ</Badge>}
                    {v === 'date' && <Badge color="gold">💫 DATE</Badge>}
                    {v === 'mixer' && <Badge color="gold">✨ MIXER</Badge>}
                    {r.is_private && r.status === 'proposed' && <Badge color="amber">CONCIERGERIE</Badge>}
                  </div>
                )},
                { key:'status', label:'Status',  render: v => <Badge color={statusColor(v)}>{v}</Badge> },
                { key:'event_date', label:'Date', render:(v,r) => v ? `${v}${r.event_time?' '+r.event_time.slice(0,5):''}` : (r.proposed_dates?.length ? '⏳ '+r.proposed_dates.length+' créneaux' : '—') },
                { key:'venue_name', label:'Venue', render: (v,r) => v || r.location_area || '—' },
                { key:'members', label:'Members', render:(v,r) => {
                  const total = (v||[]).length;
                  const confirmed = (v||[]).filter(m=>m.confirmed&&!m.cancelled_at).length;
                  const min = r.min_confirmations || 4;
                  return (
                    <span style={{ color: confirmed >= min ? C.green : confirmed > 0 ? C.warn : C.muted }}>
                      {confirmed}/{total} ✓ (min {min})
                    </span>
                  );
                }},
                { key:'avg_compat', label:'Compat', render: v => v != null ? <Badge color="gold">{Math.round(v*100)}%</Badge> : '—' },
                { key:'confirm_deadline', label:'Deadline', render: v => v ? ago(v) : '—' },
                ...(!networkId ? [{ key:'network_id', label:'City', render: v => v ? <span style={{ fontSize:11, color:C.muted }}>{netCity(v)}</span> : '—' }] : []),
                { key:'_icons', label:'', sortable:false, render:(_,r) => (
                  <IconBtn variant="view" title="Détails" onClick={() => openDetail(r)}/>
                )},
              ]}
              rows={scouts}
              onRowClick={openDetail}
              emptyMsg="No scouts yet — click Compose"
            />
          )}
        </div>

        {/* ── Waitlist panel ── */}
        <div style={{ background:C.surface, border:`0.5px solid ${C.border}`, borderRadius:C.radiusLg, display:'flex', flexDirection:'column', maxHeight:600, overflow:'hidden' }}>
          <div style={{ padding:'12px 14px', borderBottom:`0.5px solid ${C.border}` }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <p style={{ fontSize:13, fontWeight:600, color:C.text, margin:0 }}>Waitlist</p>
              <Badge color="blue">{waitlist.length}</Badge>
            </div>
            <div style={{ display:'flex', gap:6, marginTop:8 }}>
              {[['waitlist','En attente'],['all','Tous']].map(([id,label])=>(
                <button key={id} onClick={()=>setWlTab(id)}
                  style={{ fontSize:11, padding:'3px 10px', borderRadius:C.radius, border:`0.5px solid ${wlTab===id?C.accent:C.border}`, background:wlTab===id?'rgba(201,168,76,0.1)':'transparent', color:wlTab===id?C.accent:C.sub, cursor:'pointer' }}>
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div style={{ overflowY:'auto', flex:1, padding:'0 0 8px' }}>
            {waitlist.length === 0
              ? <p style={{ fontSize:12, color:C.muted, padding:'16px 14px' }}>Waitlist is empty</p>
              : waitlist.map(w => (
              <div key={w.id} style={{ padding:'10px 14px', borderBottom:`0.5px solid ${C.border}` }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:4 }}>
                  <div>
                    <span style={{ fontSize:12, fontWeight:600, color:C.text }}>{w.user?.name||'—'}</span>
                    <span style={{ fontSize:11, color:C.muted, marginLeft:6 }}>{w.user?.age||'?'} · {w.user?.gender||'?'}</span>
                  </div>
                  <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                    <Badge color={w.user?.plan==='royal'?'gold':w.user?.plan==='honey'?'amber':'default'}>{w.user?.plan||'free'}</Badge>
                    <button onClick={()=>removeFromWaitlist(w.id)} title="Retirer de la liste"
                      style={{ background:'none', border:`0.5px solid ${C.border}`, borderRadius:3, padding:'2px 5px', cursor:'pointer', color:C.muted, fontSize:11, lineHeight:1 }}>✕</button>
                  </div>
                </div>
                <div style={{ display:'flex', flexWrap:'wrap', gap:4, marginBottom:3 }}>
                  {w.scout_type && <Badge>{w.scout_type}</Badge>}
                  {w.lang_pref && w.lang_pref !== 'both' && <Badge color="blue">{w.lang_pref}</Badge>}
                  {w.group_size_pref && w.group_size_pref !== 'any' && <Badge>{w.group_size_pref}</Badge>}
                </div>
                {(w.days_available?.length > 0 || w.time_slots?.length > 0) && (
                  <p style={{ fontSize:10, color:C.muted, margin:'2px 0 0' }}>
                    {(w.days_available||[]).join(', ')}{w.time_slots?.length>0 ? ' · '+(w.time_slots||[]).join(', ') : ''}
                  </p>
                )}
                {w.area_notes && <p style={{ fontSize:10, color:C.muted, margin:'2px 0 0', fontStyle:'italic' }}>"{w.area_notes}"</p>}
                <p style={{ fontSize:10, color:C.muted, margin:'4px 0 0' }}>{ago(w.joined_at)}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Circle detail modal ── */}
      {selected && (
        <Modal title={`Scout — ${selected.type} · ${selected.event_date||'date TBD'}`} onClose={()=>{ setSelected(null); setDetail(null); }} width={620}>
          {detailLoading || !detail ? (
            <p style={{ color:C.muted, textAlign:'center', padding:'24px 0' }}>Chargement…</p>
          ) : (
            <div>
              {/* Info grid */}
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10, marginBottom:18 }}>
                {[
                  ['Statut',     <Badge color={statusColor(detail.scout.status)}>{detail.scout.status}</Badge>],
                  ['Date',       detail.scout.event_date ? `${detail.scout.event_date}${detail.scout.event_time?' '+detail.scout.event_time.slice(0,5):''}` : '—'],
                  ['Venue',      detail.scout.venue_name || '—'],
                  ['Adresse',    detail.scout.venue_address || detail.scout.venue_area || '—'],
                  ['Compat moy', detail.scout.avg_compat != null ? `${Math.round(detail.scout.avg_compat*100)}%` : '—'],
                  ['Deadline',   detail.scout.confirm_deadline ? ago(detail.scout.confirm_deadline) : '—'],
                  ['Min confirm',detail.scout.min_confirmations || 4],
                  ['Créé',       ago(detail.scout.created_at)],
                  ['Intérêts communs', (detail.scout.shared_interests||[]).slice(0,3).join(', ')||'—'],
                ].map(([label, val]) => (
                  <div key={label} style={{ background:C.bg, borderRadius:C.radius, padding:'8px 10px' }}>
                    <p style={{ fontSize:10, color:C.muted, margin:'0 0 3px', textTransform:'uppercase', letterSpacing:'0.06em' }}>{label}</p>
                    <div style={{ fontSize:12, fontWeight:500, color:C.text }}>{val}</div>
                  </div>
                ))}
              </div>

              {/* Status actions */}
              <div style={{ display:'flex', gap:6, marginBottom:18, flexWrap:'wrap' }}>
                <span style={{ fontSize:12, color:C.sub, alignSelf:'center' }}>Forcer statut :</span>
                {['waiting','proposed','confirmed','past','cancelled'].map(s => (
                  <button key={s} onClick={()=>forceStatus(detail.scout.id, s)}
                    disabled={detail.scout.status === s}
                    style={{ fontSize:11, padding:'4px 10px', borderRadius:C.radius, border:`0.5px solid ${C.borderMd}`, background:detail.scout.status===s?C.bg:'transparent', color:detail.scout.status===s?C.muted:C.sub, cursor:detail.scout.status===s?'default':'pointer', opacity:detail.scout.status===s?0.5:1 }}>
                    {s}
                  </button>
                ))}
              </div>

              {/* Invitations (private circles) */}
              {invitations.length > 0 && (
                <div style={{ marginBottom:18 }}>
                  <p style={{ fontSize:12, fontWeight:600, color:C.text, margin:'0 0 8px' }}>
                    Invitations ({invitations.length})
                  </p>
                  <div style={{ border:`0.5px solid ${C.border}`, borderRadius:C.radius, overflow:'hidden' }}>
                    {invitations.map((inv, i) => {
                      const statusColor2 = inv.status==='accepted'?C.green:inv.status==='declined'?C.red:C.warn;
                      const preferredDates = inv.preferred_dates || [];
                      return (
                        <div key={inv.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 12px', borderBottom:i<invitations.length-1?`0.5px solid ${C.border}`:'none' }}>
                          <div style={{ flex:1, minWidth:0 }}>
                            <span style={{ fontSize:12, fontWeight:500, color:C.text }}>{inv.invitee?.name||'—'}</span>
                            {preferredDates.length > 0 && (
                              <p style={{ fontSize:10, color:C.muted, margin:'2px 0 0' }}>
                                Dispo : {preferredDates.map(d=>`${d.date} ${d.time||''}`).join(', ')}
                              </p>
                            )}
                          </div>
                          <Badge color={inv.status==='accepted'?'green':inv.status==='declined'?'red':'amber'}>{inv.status}</Badge>
                          {inv.responded_at && <span style={{ fontSize:10, color:C.muted }}>{ago(inv.responded_at)}</span>}
                        </div>
                      );
                    })}
                  </div>
                  {/* Most popular date across acceptances */}
                  {(() => {
                    const counts = {};
                    invitations.filter(i=>i.status==='accepted').forEach(inv => {
                      (inv.preferred_dates||[]).forEach(d => {
                        const k=`${d.date}|${d.time||''}`;
                        counts[k]=(counts[k]||0)+1;
                      });
                    });
                    const top = Object.entries(counts).sort((a,b)=>b[1]-a[1])[0];
                    if (!top) return null;
                    const [key, cnt] = top;
                    const [date, time] = key.split('|');
                    return (
                      <p style={{ fontSize:11, color:C.accent, marginTop:6 }}>
                        🗓 Créneau favori : <strong>{date} {time}</strong> ({cnt} personnes)
                      </p>
                    );
                  })()}
                </div>
              )}

              {/* Members */}
              <p style={{ fontSize:12, fontWeight:600, color:C.text, margin:'0 0 8px' }}>
                Membres ({detail.members.length}) — {detail.members.filter(m=>m.confirmed&&!m.cancelled_at).length} confirmé(s)
              </p>
              <div style={{ border:`0.5px solid ${C.border}`, borderRadius:C.radius, overflow:'hidden' }}>
                {detail.members.length === 0
                  ? <p style={{padding:12,color:C.muted,fontSize:12,margin:0}}>Aucun membre</p>
                  : detail.members.map((m, i) => {
                    const u = m.user || {};
                    const cancelled = !!m.cancelled_at;
                    return (
                      <div key={u.id||i} style={{ display:'flex', alignItems:'center', gap:10, padding:'9px 12px', borderBottom:i<detail.members.length-1?`0.5px solid ${C.border}`:'none', background:cancelled?C.redBg:'transparent' }}>
                        {/* Avatar */}
                        <div style={{ width:28, height:28, borderRadius:'50%', background:'rgba(201,168,76,0.15)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:600, color:C.accent, flexShrink:0 }}>
                          {(u.name||'?')[0].toUpperCase()}
                        </div>
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                            <span style={{ fontSize:12, fontWeight:500, color:cancelled?C.muted:C.text }}>{u.name||'—'}</span>
                            <span style={{ fontSize:11, color:C.muted }}>{u.age||'?'} · {u.gender||'?'}</span>
                            <Badge color={u.plan==='royal'?'gold':u.plan==='honey'?'amber':'default'}>{u.plan||'free'}</Badge>
                          </div>
                          <div style={{ display:'flex', gap:8, marginTop:2 }}>
                            <span style={{ fontSize:10, color:m.confirmed&&!cancelled?C.green:C.muted }}>
                              {cancelled ? '✕ Annulé' : m.confirmed ? '✓ Confirmé' : '○ En attente'}
                            </span>
                            {m.voted && (
                              <span style={{ fontSize:10, color:m.vote?C.green:C.red }}>
                                Vote : {m.vote ? '👍' : '👎'}
                              </span>
                            )}
                            {u.score != null && <span style={{ fontSize:10, color:C.muted }}>score {u.score}</span>}
                          </div>
                          {m.cancel_reason && <p style={{ fontSize:10, color:C.red, margin:'2px 0 0', fontStyle:'italic' }}>"{m.cancel_reason}"</p>}
                        </div>
                        <button onClick={()=>removeMember(detail.scout.id, u.id)} title="Retirer du Scout"
                          style={{ background:'none', border:`0.5px solid ${C.border}`, borderRadius:3, padding:'3px 7px', cursor:'pointer', color:C.red, fontSize:11, flexShrink:0 }}>
                          Retirer
                        </button>
                      </div>
                    );
                  })
                }
              </div>

              {/* ── Concierge panel (private scouts needing a booking) ── */}
              {detail.scout.is_private && detail.scout.status === 'proposed' && (
                <ConciergePanel
                  scout={detail.scout}
                  invitations={invitations}
                  networkId={detail.scout.network_id}
                  onBooked={(bookingId) => {
                    setDetail(prev => prev ? { ...prev, scout: { ...prev.scout, _bookingId: bookingId } } : prev);
                  }}
                />
              )}

              {/* ── Feature Q — Scout sponsors panel ── */}
              <ScoutSponsorsPanel scoutId={detail.scout.id} networkId={detail.scout.network_id}/>
            </div>
          )}
        </Modal>
      )}
    </>
  );
}

// ── ScoutSponsorsPanel — Feature Q (feature_circle_sponsors) ────────────────
function ScoutSponsorsPanel({ scoutId, networkId }) {
  const { features } = useApp();
  const [linked,   setLinked]   = useState([]);
  const [allOpts,  setAllOpts]  = useState([]);
  const [picked,   setPicked]   = useState('');
  const [contrib,  setContrib]  = useState('');
  const [perk,     setPerk]     = useState('');
  const [loading,  setLoading]  = useState(true);

  const refresh = async () => {
    const { loadScoutSponsors } = await import('../utils/featurePackA');
    const rows = await loadScoutSponsors(scoutId);
    setLinked(rows);
    setLoading(false);
  };

  useEffect(() => {
    if (!features?.feature_circle_sponsors || !scoutId) return;
    refresh();
    let q = supabase?.from('sponsors').select('id,name,category,logo_url').order('name');
    if (networkId) q = q?.eq('network_id', networkId);
    q?.then(({ data }) => setAllOpts(data || []));
  }, [features?.feature_circle_sponsors, scoutId, networkId]);

  if (!features?.feature_circle_sponsors) return null;

  const handleAttach = async () => {
    if (!picked) return;
    const { attachSponsorToScout } = await import('../utils/featurePackA');
    await attachSponsorToScout({
      scoutId, sponsorId: picked,
      contribution: parseFloat(contrib) || 0,
      perkDescription: perk || null,
    });
    setPicked(''); setContrib(''); setPerk('');
    refresh();
  };

  const handleDetach = async (sponsorId) => {
    const { detachSponsorFromScout } = await import('../utils/featurePackA');
    await detachSponsorFromScout({ scoutId, sponsorId });
    refresh();
  };

  return (
    <div style={{ marginTop: 16, padding: 14, background: C.surface, border: `0.5px solid ${C.border}`, borderRadius: C.radius }}>
      <p style={{ margin: '0 0 10px', fontSize: 12, fontWeight: 500, color: C.text }}>Sponsors backing this scout</p>
      {loading && <p style={{ fontSize: 11, color: C.muted }}>Loading…</p>}
      {!loading && linked.length === 0 && (
        <p style={{ fontSize: 11, color: C.muted }}>No sponsor linked yet.</p>
      )}
      {linked.map((row, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: i < linked.length - 1 ? `0.5px solid ${C.border}` : 'none' }}>
          {row.sponsor?.logo_url
            ? <img src={row.sponsor.logo_url} alt={row.sponsor.name} style={{ width: 28, height: 28, borderRadius: 4, objectFit: 'cover' }}/>
            : <div style={{ width: 28, height: 28, borderRadius: 4, background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: C.muted }}>{(row.sponsor?.name || '?')[0]}</div>}
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ margin: 0, fontSize: 12, fontWeight: 500, color: C.text }}>{row.sponsor?.name || '—'}</p>
            <p style={{ margin: 0, fontSize: 10, color: C.muted }}>
              {row.sponsor?.category || '—'} · ${row.contribution || 0}
              {row.perk_description ? ` · ${row.perk_description}` : ''}
            </p>
          </div>
          <button onClick={() => handleDetach(row.sponsor?.id)} style={{ background: 'none', border: `0.5px solid ${C.border}`, borderRadius: 3, padding: '3px 7px', cursor: 'pointer', color: C.red, fontSize: 11 }}>
            Detach
          </button>
        </div>
      ))}
      <div style={{ display: 'flex', gap: 6, marginTop: 12, flexWrap: 'wrap' }}>
        <select value={picked} onChange={e => setPicked(e.target.value)}
          style={{ flex: 2, minWidth: 140, padding: 6, fontSize: 12, border: `0.5px solid ${C.border}`, borderRadius: 4, background: C.surface, color: C.text }}>
          <option value="">— pick a sponsor —</option>
          {allOpts.filter(o => !linked.some(l => l.sponsor?.id === o.id)).map(o => (
            <option key={o.id} value={o.id}>{o.name} {o.category ? `(${o.category})` : ''}</option>
          ))}
        </select>
        <input type="number" placeholder="$ contribution" value={contrib} onChange={e => setContrib(e.target.value)}
          style={{ width: 110, padding: 6, fontSize: 12, border: `0.5px solid ${C.border}`, borderRadius: 4, background: C.surface, color: C.text }}/>
        <input type="text" placeholder="perk (e.g. free drink)" value={perk} onChange={e => setPerk(e.target.value)}
          style={{ flex: 1, minWidth: 140, padding: 6, fontSize: 12, border: `0.5px solid ${C.border}`, borderRadius: 4, background: C.surface, color: C.text }}/>
        <button onClick={handleAttach} disabled={!picked} style={{ padding: '6px 12px', background: picked ? C.accent : C.surface, border: 'none', borderRadius: 4, cursor: picked ? 'pointer' : 'not-allowed', color: picked ? '#0A0A0A' : C.muted, fontSize: 12, fontWeight: 600 }}>
          Attach
        </button>
      </div>
    </div>
  );
}

// ── Concierge panel — venue suggestion + booking trigger ─────────────────
function ConciergePanel({ scout, invitations, networkId, onBooked }) {
  const { currentUser } = useApp();
  const [venues,        setVenues]        = useState([]);
  const [existingBooking,setExistingBooking] = useState(null);
  const [selectedVenue, setSelectedVenue] = useState(null);
  const [selectedDate,  setSelectedDate]  = useState('');
  const [selectedTime,  setSelectedTime]  = useState('');
  const [partySize,     setPartySize]     = useState(
    (invitations?.filter(i => i.status === 'accepted').length || 2) + 1
  );
  const [booking,       setBooking]       = useState(false);
  const [result,        setResult]        = useState(null);
  const [draft,         setDraft]         = useState(null);

  useEffect(() => {
    // Load matching venues — date scouts accept dinner+date tagged venues
    const activityFilter = scout.type === 'date' ? ['dinner'] : [scout.type];
    supabase?.from('venues')
      .select('*,providers:venue_providers(provider,external_id,is_active)')
      .eq('network_id', networkId || 'ca_montreal')
      .eq('is_active', true)
      .overlaps('activity_types', activityFilter)
      .order('rating', { ascending: false })
      .then(({ data }) => { if (data) setVenues(data); });

    // Check if a booking already exists
    supabase?.from('scout_bookings')
      .select('*,venue:venues(name,area)')
      .eq('scout_id', scout.id)
      .maybeSingle()
      .then(({ data }) => { if (data) setExistingBooking(data); });

    // Pre-fill date from top invitation slot
    const counts = {};
    invitations?.filter(i=>i.status==='accepted').forEach(inv => {
      (inv.preferred_dates||[]).forEach(d => {
        const k=`${d.date}|${d.time||''}`;
        counts[k]=(counts[k]||0)+1;
      });
    });
    const top = Object.entries(counts).sort((a,b)=>b[1]-a[1])[0];
    if (top) {
      const [date, time] = top[0].split('|');
      setSelectedDate(date);
      setSelectedTime(time);
    }
  }, [scout.id, networkId, scout.type]);

  const triggerBook = async (provider = 'manual') => {
    if (!selectedVenue) { alert('Sélectionne un lieu d\'abord.'); return; }
    setBooking(true);
    setResult(null);
    setDraft(null);
    try {
      const booking = await upsertScoutBooking({
        supabase,
        scoutId:    scout.id,
        venueId:     selectedVenue.id,
        date:        selectedDate,
        time:        selectedTime,
        partySize,
        provider,
        adminUserId: currentUser?.id,
      });
      const res = await triggerBooking({ supabase, scoutBookingId: booking.id, action: 'book' });
      setResult(res);
      if (res.result?.draft) setDraft(res.result.draft);
      if (res.success) onBooked?.(booking.id);
    } catch (e) {
      setResult({ success: false, error: e.message });
    }
    setBooking(false);
  };

  // Propose dates from proposed_dates + invitee preferences
  const proposedDates = scout.proposed_dates || [];
  const counts = {};
  invitations?.filter(i=>i.status==='accepted').forEach(inv => {
    (inv.preferred_dates||[]).forEach(d => { const k=`${d.date}|${d.time||''}`; counts[k]=(counts[k]||0)+1; });
  });

  return (
    <div style={{ marginTop:20, padding:14, background:'rgba(91,155,213,0.06)', border:`0.5px solid rgba(91,155,213,0.2)`, borderRadius:C.radiusLg }}>
      <p style={{ fontSize:13, fontWeight:700, color:'#5B9BD5', margin:'0 0 12px' }}>🎩 Conciergerie</p>

      {existingBooking && (
        <div style={{ padding:'8px 10px', background: existingBooking.status==='confirmed'?'rgba(78,201,148,0.1)':'rgba(201,168,76,0.08)', border:`0.5px solid ${existingBooking.status==='confirmed'?C.green:C.accent}`, borderRadius:C.radius, marginBottom:12 }}>
          <p style={{ fontSize:12, fontWeight:500, color: existingBooking.status==='confirmed'?C.green:C.accent, margin:0 }}>
            {existingBooking.status==='confirmed'
              ? `✓ Réservé — ${existingBooking.venue?.name||'?'} · ${existingBooking.booked_date||'date TBD'} · ref: ${existingBooking.booking_ref||'—'}`
              : `⏳ Réservation en cours — ${existingBooking.venue?.name||'?'} · statut: ${existingBooking.status}`
            }
          </p>
        </div>
      )}

      {/* Venue selector */}
      {venues.length === 0 ? (
        <p style={{ fontSize:12, color:C.muted }}>Aucun lieu partenaire pour "{scout.type}" dans ce réseau. <a href="#venues_admin" style={{ color:C.accent }}>Ajouter des lieux →</a></p>
      ) : (
        <>
          <p style={{ fontSize:11, color:C.muted, margin:'0 0 6px', textTransform:'uppercase', letterSpacing:'0.07em', fontWeight:600 }}>Lieu</p>
          <div style={{ display:'flex', flexDirection:'column', gap:5, marginBottom:12 }}>
            {venues.map(v => (
              <button key={v.id} onClick={() => setSelectedVenue(v)}
                style={{ textAlign:'left', padding:'8px 10px', borderRadius:C.radius, border:`0.5px solid ${selectedVenue?.id===v.id?C.accent:C.border}`, background:selectedVenue?.id===v.id?'rgba(201,168,76,0.08)':'transparent', cursor:'pointer', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <div>
                  <span style={{ fontSize:12, fontWeight:500, color:C.text }}>{v.name}</span>
                  <span style={{ fontSize:11, color:C.muted, marginLeft:6 }}>{v.area} · {v.venue_type} · {v.capacity_min}–{v.capacity_max} p.</span>
                </div>
                {v.rating && <Badge color="gold">{v.rating} ★</Badge>}
              </button>
            ))}
          </div>
        </>
      )}

      {/* Date / time / party size */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:8, marginBottom:12 }}>
        <div>
          <p style={{ fontSize:11, color:C.muted, margin:'0 0 4px', textTransform:'uppercase', letterSpacing:'0.07em', fontWeight:600 }}>Date</p>
          <input type="date" value={selectedDate} onChange={e=>setSelectedDate(e.target.value)}
            style={{ width:'100%', padding:'5px 7px', background:C.bg, border:`0.5px solid ${C.border}`, borderRadius:C.radius, color:C.text, fontSize:12, colorScheme:'dark', boxSizing:'border-box' }}/>
          {/* Date suggestions from invitees */}
          {Object.entries(counts).sort((a,b)=>b[1]-a[1]).slice(0,2).map(([k,n])=>{
            const [d,t]=k.split('|');
            return (
              <button key={k} onClick={()=>{ setSelectedDate(d); setSelectedTime(t); }}
                style={{ fontSize:10, color:C.accent, background:'transparent', border:'none', cursor:'pointer', padding:'2px 0', display:'block' }}>
                ✓ {d} {t} ({n}×)
              </button>
            );
          })}
        </div>
        <div>
          <p style={{ fontSize:11, color:C.muted, margin:'0 0 4px', textTransform:'uppercase', letterSpacing:'0.07em', fontWeight:600 }}>Heure</p>
          <input type="text" value={selectedTime} onChange={e=>setSelectedTime(e.target.value)}
            placeholder="19:30" style={{ width:'100%', padding:'5px 7px', background:C.bg, border:`0.5px solid ${C.border}`, borderRadius:C.radius, color:C.text, fontSize:12, boxSizing:'border-box' }}/>
        </div>
        <div>
          <p style={{ fontSize:11, color:C.muted, margin:'0 0 4px', textTransform:'uppercase', letterSpacing:'0.07em', fontWeight:600 }}>Personnes</p>
          <input type="number" min={2} max={20} value={partySize} onChange={e=>setPartySize(Number(e.target.value))}
            style={{ width:'100%', padding:'5px 7px', background:C.bg, border:`0.5px solid ${C.border}`, borderRadius:C.radius, color:C.text, fontSize:12, boxSizing:'border-box' }}/>
        </div>
      </div>

      {/* Booking actions */}
      <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
        {Object.values(BOOKING_PROVIDERS).filter(p=>p.active).map(p => (
          <Btn key={p.id} variant={p.id==='manual'?'secondary':'primary'} size="sm"
            onClick={() => triggerBook(p.id)} disabled={booking || !selectedVenue}>
            {booking ? '…' : `${p.icon} ${p.id==='manual'?'Envoyer email':'Réserver via'} ${p.name}`}
          </Btn>
        ))}
        {/* Show inactive providers as greyed out with "coming soon" */}
        {Object.values(BOOKING_PROVIDERS).filter(p=>!p.active && p.phase===3).map(p => (
          <Btn key={p.id} size="sm" disabled title={`Phase 3 — implémenter providers/${p.id}.ts`}>
            {p.icon} {p.name} (soon)
          </Btn>
        ))}
      </div>

      {/* Result */}
      {result && !draft && (
        <p style={{ fontSize:12, color: result.success?C.green:C.red, marginTop:8 }}>
          {result.success ? '✓ Réservation envoyée' : `✕ Erreur: ${result.error}`}
        </p>
      )}

      {/* Manual email draft — show for copy/paste */}
      {draft && (
        <div style={{ marginTop:10, padding:10, background:C.bg, border:`0.5px solid ${C.border}`, borderRadius:C.radius }}>
          <p style={{ fontSize:11, fontWeight:600, color:C.accent, margin:'0 0 6px' }}>
            {draft.to === 'NO_EMAIL_ON_FILE' ? '⚠ Aucun email enregistré — copie manuellement :' : `✓ Email envoyé à ${draft.to}`}
          </p>
          <p style={{ fontSize:11, color:C.muted, margin:'0 0 3px' }}><strong>Objet :</strong> {draft.subject}</p>
          <pre style={{ fontSize:10, color:C.text, whiteSpace:'pre-wrap', margin:0, fontFamily:'monospace', maxHeight:150, overflowY:'auto' }}>{draft.body}</pre>
        </div>
      )}
    </div>
  );
}

// Detect booking platform + external_id from a pasted URL
function parsePlatformUrl(url) {
  if (!url?.trim()) return null;
  try {
    const u = url.trim();
    const tf = u.match(/thefork\.[a-z]+\/restaurant\/[^/?#]+-r(\d+)/i);
    if (tf) return { provider: 'thefork', external_id: tf[1] };
    const resy = u.match(/resy\.com\/cities\/[^/]+\/([^/?#]+)/i);
    if (resy) return { provider: 'resy', external_id: resy[1] };
    const otNum = u.match(/opentable\.com\/restaurant\/profile\/(\d+)/i);
    if (otNum) return { provider: 'opentable', external_id: otNum[1] };
    const otSlug = u.match(/opentable\.com\/r\/([^/?#]+)/i);
    if (otSlug) return { provider: 'opentable', external_id: otSlug[1] };
  } catch {}
  return null;
}

const PROVIDER_META = {
  thefork:   { label:'TheFork',   color:'#00B37A', icon:'🍴' },
  resy:      { label:'Resy',      color:'#E63A3A', icon:'📅' },
  opentable: { label:'OpenTable', color:'#DA3743', icon:'🍽️' },
};

// ── VenuesTab — curated partner venue database ────────────────────────────
function VenuesTab({ networkId, networkIdsJson }) {
  const [venues,       setVenues]       = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [editing,      setEditing]      = useState(null);
  const [saving,       setSaving]       = useState(false);
  const [form,         setForm]         = useState({});
  const [platformUrl,  setPlatformUrl]  = useState('');
  const [search,       setSearch]       = useState('');
  const [filterAct,    setFilterAct]    = useState('');
  const [selected,     setSelected]     = useState(new Set());

  const VENUE_TYPES    = ['restaurant','bar','rooftop','outdoor','private_room','surprise'];
  const ACTIVITY_TYPES = ['dinner','apero','coffee','outdoor','culture','board_games','sport','workshop','date','mixer'];

  const load = () => {
    let q = supabase?.from('venues').select('*, providers:venue_providers(provider,external_id,is_active)').order('name');
    if (networkId) q = q?.eq('network_id', networkId);
    else if (networkIdsJson) { const ids = JSON.parse(networkIdsJson); q = q?.in('network_id', ids); }
    q?.then(({ data }) => { if (data) setVenues(data); setLoading(false); });
  };

  useEffect(() => { load(); }, [networkId, networkIdsJson]);

  const openNew = () => {
    setForm({ network_id: networkId || 'ca_montreal', venue_type:'restaurant', activity_types:[], is_active:true, capacity_min:2, capacity_max:12 });
    setPlatformUrl('');
    setEditing('new');
  };

  const openEdit = (v) => {
    setForm({ ...v });
    const activeProvider = (v.providers || []).find(p => p.is_active);
    setPlatformUrl('');
    if (activeProvider) {
      setForm(prev => ({ ...prev, _provider: activeProvider.provider, _external_id: activeProvider.external_id }));
    }
    setEditing(v);
  };

  const save = async () => {
    setSaving(true);
    const parsed = parsePlatformUrl(platformUrl);
    const payload = { ...form, updated_at: new Date().toISOString() };
    delete payload.providers; delete payload._provider; delete payload._external_id;

    let venueId = editing === 'new' ? null : editing.id;
    if (editing === 'new') {
      const { data } = await supabase?.from('venues').insert(payload).select('id').single();
      venueId = data?.id;
    } else {
      await supabase?.from('venues').update(payload).eq('id', venueId);
    }

    if (venueId && parsed) {
      await supabase?.from('venue_providers').upsert(
        { venue_id: venueId, provider: parsed.provider, external_id: parsed.external_id, is_active: true },
        { onConflict: 'venue_id,provider' }
      );
    }

    setSaving(false);
    setEditing(null);
    load();
  };

  const toggleActive = async (v) => {
    await supabase?.from('venues').update({ is_active: !v.is_active }).eq('id', v.id);
    setVenues(prev => prev.map(x => x.id===v.id ? { ...x, is_active: !x.is_active } : x));
  };

  const del = async (v) => {
    if (!window.confirm(`Supprimer "${v.name}" ?`)) return;
    await supabase?.from('venues').delete().eq('id', v.id);
    setVenues(prev => prev.filter(x => x.id !== v.id));
    setSelected(prev => { const n = new Set(prev); n.delete(v.id); return n; });
  };

  const bulkDelete = async () => {
    if (!selected.size || !window.confirm(`Supprimer ${selected.size} lieu(x) ?`)) return;
    await Promise.all([...selected].map(id => supabase?.from('venues').delete().eq('id', id)));
    setVenues(prev => prev.filter(x => !selected.has(x.id)));
    setSelected(new Set());
  };

  const f = (key, val) => setForm(prev => ({ ...prev, [key]: val }));
  const toggleActivity = (type) => setForm(prev => {
    const arr = prev.activity_types || [];
    return { ...prev, activity_types: arr.includes(type) ? arr.filter(t=>t!==type) : [...arr, type] };
  });

  const filtered = venues.filter(v => {
    const q = search.toLowerCase();
    const matchSearch = !q || [v.name, v.area, v.city].some(s => s?.toLowerCase().includes(q));
    const matchAct = !filterAct || (v.activity_types || []).includes(filterAct);
    return matchSearch && matchAct;
  });

  const parsedUrl = parsePlatformUrl(platformUrl);

  return (
    <>
      <PageHeader
        title="Venues partenaires"
        sub={`${venues.length} lieux · Phase 2 & 3`}
        action={<Btn variant="primary" onClick={openNew}>+ Ajouter un lieu</Btn>}
      />

      {/* Search + filter bar */}
      <div style={{ display:'flex', gap:8, marginBottom:10, flexWrap:'wrap' }}>
        <input
          placeholder="Rechercher par nom, zone, ville…"
          value={search} onChange={e => setSearch(e.target.value)}
          style={{ flex:'1 1 200px', padding:'6px 10px', background:C.surface, border:`0.5px solid ${C.border}`, borderRadius:C.radius, color:C.text, fontSize:12 }}
        />
        <select value={filterAct} onChange={e => setFilterAct(e.target.value)}
          style={{ padding:'6px 10px', background:C.surface, border:`0.5px solid ${C.border}`, borderRadius:C.radius, color:filterAct?C.text:C.muted, fontSize:12, cursor:'pointer' }}>
          <option value="">Toutes les activités</option>
          {ACTIVITY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        {(search || filterAct) && (
          <Btn size="sm" onClick={()=>{ setSearch(''); setFilterAct(''); }}>✕ Effacer</Btn>
        )}
      </div>
      <BatchBar count={selected.size} onClear={() => setSelected(new Set())}>
        <Btn size="sm" variant="danger" onClick={bulkDelete}>Supprimer sélection</Btn>
      </BatchBar>

      {loading ? <SkeletonTable cols={6} rows={6}/> : (
        <DataTable
          selectable selectedIds={selected} onSelectionChange={setSelected}
          columns={[
            { key:'name', label:'Nom', render:(v,r) => (
              <div>
                <p style={{ margin:0, fontSize:13, fontWeight:500, color:C.text }}>{v}</p>
                <p style={{ margin:0, fontSize:11, color:C.muted }}>{r.area} · {r.city}</p>
              </div>
            )},
            { key:'venue_type',    label:'Type',       render: v => <Badge>{v}</Badge> },
            ...(!networkId ? [{ key:'network_id', label:'City', render: v => v ? <span style={{ fontSize:11, color:C.muted }}>{netCity(v)}</span> : '—' }] : []),
            { key:'activity_types',label:'Activités',  render: v => (
              <div style={{ display:'flex', gap:3, flexWrap:'wrap' }}>
                {(v||[]).map(t => <Badge key={t}>{t}</Badge>)}
              </div>
            )},
            { key:'providers', label:'Plateforme', sortable:false, render: v => {
              const active = (v||[]).find(p => p.is_active);
              if (!active) return <span style={{ fontSize:11, color:C.muted }}>Manuel</span>;
              const meta = PROVIDER_META[active.provider] || {};
              return (
                <span style={{ fontSize:11, fontWeight:500, color:meta.color }}>
                  {meta.icon} {meta.label}
                </span>
              );
            }},
            { key:'capacity_min', label:'Capacité', render:(v,r) => `${v}–${r.capacity_max} p.` },
            { key:'rating',       label:'Note',     render: v => v ? <Badge color="gold">{v} ★</Badge> : '—' },
            { key:'is_active',    label:'Statut',   render:(v,r) => (
              <div style={{ display:'flex', gap:6, alignItems:'center' }}>
                <Badge color={v?'green':'default'}>{v?'Actif':'Inactif'}</Badge>
                {r.contact_email && <span style={{ fontSize:10, color:C.muted }}>✉</span>}
              </div>
            )},
            { key:'_icons', label:'', sortable:false, render:(_,r) => (
              <div style={{ display:'flex', gap:4 }}>
                <IconBtn variant="edit" title="Modifier" onClick={() => openEdit(r)}/>
                <IconBtn title={r.is_active?'Désactiver':'Activer'} onClick={() => toggleActive(r)} color={r.is_active?C.warn:C.green}>
                  {r.is_active ? '⏸' : '▶'}
                </IconBtn>
                <IconBtn title="Supprimer" danger onClick={() => del(r)}>✕</IconBtn>
              </div>
            )},
          ]}
          rows={filtered}
          emptyMsg={search||filterAct ? 'Aucun lieu correspond à cette recherche.' : 'Aucun lieu — cliquez + Ajouter'}
        />
      )}

      {/* Edit / New modal */}
      {editing !== null && (
        <Modal title={editing==='new' ? 'Nouveau lieu partenaire' : `Modifier — ${editing.name}`} onClose={()=>setEditing(null)} width={600}>

          {/* Platform URL import */}
          <div style={{ marginBottom:14, padding:10, background:'rgba(91,155,213,0.06)', border:`0.5px solid rgba(91,155,213,0.2)`, borderRadius:C.radius }}>
            <p style={{ fontSize:11, color:'#5B9BD5', margin:'0 0 5px', textTransform:'uppercase', letterSpacing:'0.06em', fontWeight:600 }}>
              🔗 Lien plateforme (TheFork / Resy / OpenTable)
            </p>
            <input
              type="url"
              placeholder="Coller l'URL du lieu sur TheFork, Resy ou OpenTable…"
              value={platformUrl}
              onChange={e => setPlatformUrl(e.target.value)}
              style={{ width:'100%', padding:'5px 8px', background:C.bg, border:`0.5px solid ${parsedUrl ? C.green : C.border}`, borderRadius:C.radius, color:C.text, fontSize:12, boxSizing:'border-box' }}
            />
            {parsedUrl && (
              <p style={{ fontSize:11, color:C.green, margin:'4px 0 0' }}>
                ✓ {PROVIDER_META[parsedUrl.provider]?.label} détecté · ID: {parsedUrl.external_id}
              </p>
            )}
            {platformUrl && !parsedUrl && (
              <p style={{ fontSize:11, color:C.muted, margin:'4px 0 0' }}>
                URL non reconnue — URL TheFork, Resy ou OpenTable uniquement.
              </p>
            )}
            {form._provider && !platformUrl && (
              <p style={{ fontSize:11, color:C.accent, margin:'4px 0 0' }}>
                Plateforme existante : {PROVIDER_META[form._provider]?.label} · ID: {form._external_id}
              </p>
            )}
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:12 }}>
            {[
              ['name',          'Nom',              'text'],
              ['area',          'Quartier/Zone',    'text'],
              ['city',          'Ville',            'text'],
              ['address',       'Adresse',          'text'],
              ['contact_name',  'Contact',          'text'],
              ['contact_email', 'Email',            'email'],
              ['contact_phone', 'Téléphone',        'text'],
              ['booking_url',   'Lien réservation', 'url'],
              ['capacity_min',  'Capacité min',     'number'],
              ['capacity_max',  'Capacité max',     'number'],
              ['rating',        'Note (1–5)',        'number'],
            ].map(([key, label, type]) => (
              <div key={key}>
                <p style={{ fontSize:11, color:C.muted, margin:'0 0 3px', textTransform:'uppercase', letterSpacing:'0.06em', fontWeight:600 }}>{label}</p>
                <input type={type} value={form[key]||''} onChange={e=>f(key, type==='number'?Number(e.target.value):e.target.value)}
                  style={{ width:'100%', padding:'5px 8px', background:C.bg, border:`0.5px solid ${C.border}`, borderRadius:C.radius, color:C.text, fontSize:12, boxSizing:'border-box' }}/>
              </div>
            ))}
          </div>

          {/* Venue type */}
          <div style={{ marginBottom:10 }}>
            <p style={{ fontSize:11, color:C.muted, margin:'0 0 5px', textTransform:'uppercase', letterSpacing:'0.06em', fontWeight:600 }}>Type de lieu</p>
            <div style={{ display:'flex', flexWrap:'wrap', gap:5 }}>
              {VENUE_TYPES.map(t => (
                <button key={t} onClick={()=>f('venue_type', t)}
                  style={{ padding:'3px 10px', borderRadius:C.radius, border:`0.5px solid ${form.venue_type===t?C.accent:C.border}`, background:form.venue_type===t?'rgba(201,168,76,0.1)':'transparent', color:form.venue_type===t?C.accent:C.sub, cursor:'pointer', fontSize:11 }}>
                  {t}
                </button>
              ))}
            </div>
          </div>

          {/* Activity types */}
          <div style={{ marginBottom:10 }}>
            <p style={{ fontSize:11, color:C.muted, margin:'0 0 5px', textTransform:'uppercase', letterSpacing:'0.06em', fontWeight:600 }}>Activités compatibles</p>
            <div style={{ display:'flex', flexWrap:'wrap', gap:5 }}>
              {ACTIVITY_TYPES.map(t => (
                <button key={t} onClick={()=>toggleActivity(t)}
                  style={{ padding:'3px 10px', borderRadius:C.radius, border:`0.5px solid ${(form.activity_types||[]).includes(t)?C.accent:C.border}`, background:(form.activity_types||[]).includes(t)?'rgba(201,168,76,0.1)':'transparent', color:(form.activity_types||[]).includes(t)?C.accent:C.sub, cursor:'pointer', fontSize:11 }}>
                  {t}
                </button>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div style={{ marginBottom:14 }}>
            <p style={{ fontSize:11, color:C.muted, margin:'0 0 5px', textTransform:'uppercase', letterSpacing:'0.06em', fontWeight:600 }}>Notes internes</p>
            <textarea value={form.notes||''} onChange={e=>f('notes', e.target.value)} rows={2}
              style={{ width:'100%', padding:'6px 8px', background:C.bg, border:`0.5px solid ${C.border}`, borderRadius:C.radius, color:C.text, fontSize:12, resize:'none', fontFamily:'Outfit', boxSizing:'border-box' }}/>
          </div>

          {/* Network */}
          <div style={{ marginBottom:14 }}>
            <p style={{ fontSize:11, color:C.muted, margin:'0 0 5px', textTransform:'uppercase', letterSpacing:'0.06em', fontWeight:600 }}>Réseau</p>
            <input type="text" value={form.network_id||''} onChange={e=>f('network_id',e.target.value)}
              style={{ width:'100%', padding:'5px 8px', background:C.bg, border:`0.5px solid ${C.border}`, borderRadius:C.radius, color:C.text, fontSize:12, boxSizing:'border-box' }}/>
          </div>

          <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
            <Btn onClick={()=>setEditing(null)}>Annuler</Btn>
            <Btn variant="primary" onClick={save} disabled={saving}>{saving?'Sauvegarde…':'Sauvegarder'}</Btn>
          </div>
        </Modal>
      )}
    </>
  );
}

// ── Prevent — proactive risk scoring ─────────────────────────────────────
const RISK_REASONS_SERIOUS = new Set(['harassment','threats','fake','underage','explicit','impersonation','violence','stalking']);

// Patterns are intentionally not anchored with \b on accented ends (JS \b only covers [A-Za-z0-9_])
// Severe: unambiguous slurs / hate speech / explicit threats
// Moderate: aggressive insults, vulgar attacks
const OFFENSIVE_TERMS = {
  severe: [
    // French racial / ethnic slurs
    /\bn[eèé]gres?\b/i, /\bn[eèé]grillon\b/i,
    /\bbougnoules?\b/i,
    /\byoupins?\b/i, /\byoupines?\b/i,
    /\bchinetoque?s?\b/i,
    /\bbamboulas?\b/i,
    /\bbicots?\b/i,
    /\bratons?\b/i,
    /\bmelons?\s+(d[eu]\s+)?merde\b/i,   // racial usage
    // French homophobic / transphobic
    /p[eé]d[eé]s?/i,
    /\btapettes?\b/i,
    /\bfiottes?\b/i,
    /\btravel[o0]s?\b/i,
    /gouines?/i,
    // English racial slurs
    /\bn[i1!]gg[ae3]r?s?\b/i,
    /\bn[i1!]gg[ae3]s?\b/i,
    /\bsp[i1]c[k]?s?\b/i,
    /\bch[i1]nks?\b/i,
    /\bg[o0]{2}ks?\b/i,
    /\bkikes?\b/i,
    /\bwetbacks?\b/i,
    /\bragheads?\b/i,
    /\btowelheads?\b/i,
    /\bfagg?[o0]ts?\b/i,
    /\bc[o0]{2}ns?\b/i,
    // Threats
    /\bkill\s+your(self)?\b/i, /\bkys\b/i,
    /\btue[- ]toi\b/i,
    /je\s+vais\s+te\s+violer/i,
    /i.{0,5}ll\s+rape\s+you/i,
    /je\s+vais\s+te\s+(tuer|niquer\s+ta\s+m[eè]re)/i,
  ],
  moderate: [
    // French
    /\bsalopes?\b/i,
    /\bputes?\b/i,
    /\bconnasses?\b/i,
    /va[\s-]+te[\s-]+faire[\s-]+foutre/i,
    /fils\s+de\s+pute/i,
    /encul[eé][es]?/i,
    /\bfais\s+chier\b/i,
    /\bt[ae]s?\s+gueule\b/i,
    // English
    /\bwhores?\b/i,
    /\bsluts?\b/i,
    /\bcunts?\b/i,
    /\bfuck\s+you\b/i,
    /go\s+fuck\s+yourself/i,
    /\bfuck\s+off\b/i,
  ],
};

// Returns 'severe' | 'moderate' | null for a single message text
function scanMessage(text) {
  if (!text) return null;
  if (OFFENSIVE_TERMS.severe.some(p =>  p.test(text))) return 'severe';
  if (OFFENSIVE_TERMS.moderate.some(p => p.test(text))) return 'moderate';
  return null;
}

function computeRiskScore(userId, reports, blocks, offensiveMsgs = {}) {
  const against         = reports.filter(r => r.target_id === userId);
  const uniqueReporters = new Set(against.map(r => r.reporter_id)).size;
  const blocksReceived  = blocks.filter(b => b.blocked_id === userId).length;
  const hasSerious      = against.some(r => RISK_REASONS_SERIOUS.has(r.reason?.toLowerCase()));
  const pendingCount    = against.filter(r => r.status === 'pending').length;
  const msgs            = offensiveMsgs[userId] || { severe: 0, moderate: 0 };
  return Math.min(100,
    uniqueReporters * 15 +
    blocksReceived  * 3  +
    (hasSerious ? 30 : 0) +
    pendingCount    * 5  +
    Math.min(50, msgs.severe   * 25) +
    Math.min(20, msgs.moderate * 10)
  );
}

// score thresholds → recommended admin tier
const RISK_BANDS = [
  { id:'ban',     label:'Ban',     color:'#1A1714', bg:'#E8E6E3', threshold:80 },
  { id:'suspend', label:'Suspend', color:C.red,     bg:C.redBg,   threshold:60 },
  { id:'warn',    label:'Warn',    color:'#C0602A', bg:'#FDEBD0',  threshold:35 },
  { id:'watch',   label:'Watch',   color:C.warn,    bg:C.warnBg,   threshold:15 },
];
function tierFromScore(score) {
  return RISK_BANDS.find(b => score >= b.threshold) || null;
}

// Highlight offensive words in a message text, returns React elements
function highlightText(text) {
  if (!text) return text;
  const severeRe   = new RegExp(OFFENSIVE_TERMS.severe.map(p => `(?:${p.source})`).join('|'), 'ig');
  const moderateRe = new RegExp(OFFENSIVE_TERMS.moderate.map(p => `(?:${p.source})`).join('|'), 'ig');
  const matches = [];
  let m;
  while ((m = severeRe.exec(text))   !== null) matches.push({ start: m.index, end: m.index + m[0].length, word: m[0], level: 'severe' });
  while ((m = moderateRe.exec(text)) !== null) matches.push({ start: m.index, end: m.index + m[0].length, word: m[0], level: 'moderate' });
  if (!matches.length) return text;
  matches.sort((a, b) => a.start - b.start);
  const deduped = matches.filter((x, i) => i === 0 || x.start >= matches[i - 1].end);
  const parts = [];
  let last = 0;
  deduped.forEach((hit, i) => {
    if (hit.start > last) parts.push(text.slice(last, hit.start));
    parts.push(
      <mark key={i} style={{ background: hit.level === 'severe' ? C.redBg : C.warnBg, color: hit.level === 'severe' ? C.red : C.warn, fontWeight: 600, borderRadius: 2, padding: '0 2px' }}>
        {hit.word}
      </mark>
    );
    last = hit.end;
  });
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

function PreventTab({ reports, blocks, networkIdsJson }) {
  const [users,         setUsers]        = useState([]);
  const [flags,         setFlags]        = useState({});
  const [offensiveMsgs, setOffensiveMsgs]= useState({});
  const [detail,        setDetail]       = useState(null);
  const [editing,       setEditing]      = useState(null);
  const [noteInput,     setNoteInput]    = useState('');
  const [tierInput,     setTierInput]    = useState('watch');
  const [filterTier,    setFilterTier]   = useState('all');
  const [saving,        setSaving]       = useState(false);
  const [loading,       setLoading]      = useState(true);

  useEffect(() => {
    const since = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString();
    const networkIds = networkIdsJson ? JSON.parse(networkIdsJson) : null;
    Promise.all([
      supabase?.from('user_core').select('id,name,email,plan,verified,created_at').is('deleted_at', null).limit(1000),
      supabase?.from('user_risk_flags').select('*'),
      // scan last 90 days of messages for offensive language
      supabase?.from('messages').select('sender_id,text').gte('created_at', since).limit(3000),
    ]).then(async ([u, f, m]) => {
      let usersData = u?.data || [];
      if (networkIds?.length && usersData.length) {
        const { data: profiles } = await supabase?.from('user_profiles').select('user_id').in('network_id', networkIds);
        const inNet = new Set((profiles || []).map(p => p.user_id));
        usersData = usersData.filter(u => inNet.has(u.id));
      }
      if (usersData) setUsers(usersData);
      if (f?.data) {
        const map = {};
        f.data.forEach(x => { map[x.user_id] = x; });
        setFlags(map);
      }
      if (m?.data) {
        const agg = {};
        m.data.forEach(({ sender_id, text }) => {
          const level = scanMessage(text);
          if (!level) return;
          if (!agg[sender_id]) agg[sender_id] = { severe: 0, moderate: 0, texts: [] };
          agg[sender_id][level]++;
          agg[sender_id].texts.push({ text, level });
        });
        setOffensiveMsgs(agg);
      }
      setLoading(false);
    });
  }, [networkIdsJson]);

  const riskList = users
    .map(u => {
      const msgs    = offensiveMsgs[u.id] || { severe: 0, moderate: 0 };
      const against = reports.filter(r => r.target_id === u.id);
      const signals = [
        ...new Set(against.map(r => r.reporter_id)).size > 0 ? [`${new Set(against.map(r => r.reporter_id)).size}× signalé`] : [],
        ...blocks.filter(b => b.blocked_id === u.id).length > 0 ? [`${blocks.filter(b => b.blocked_id === u.id).length}× bloqué`] : [],
        ...msgs.severe   > 0 ? [`⚠ ${msgs.severe} msg. haineux`]   : [],
        ...msgs.moderate > 0 ? [`${msgs.moderate} msg. offensant`]  : [],
      ];
      return { ...u, autoScore: computeRiskScore(u.id, reports, blocks, msgs), flag: flags[u.id] || null, signals, msgs, flaggedTexts: offensiveMsgs[u.id]?.texts || [] };
    })
    .filter(u => u.autoScore > 0 || u.flag)
    .sort((a, b) => b.autoScore - a.autoScore)
    .filter(u =>
      filterTier === 'all'  ||
      u.flag?.tier === filterTier ||
      (filterTier === 'auto' && !u.flag && u.autoScore > 0)
    );

  const openEdit = (u) => {
    const ex = flags[u.id];
    setTierInput(ex?.tier || tierFromScore(u.autoScore)?.id || 'watch');
    setNoteInput(ex?.note || '');
    setEditing(u);
  };

  const saveFlag = async () => {
    if (!editing) return;
    setSaving(true);
    const payload = { user_id: editing.id, tier: tierInput, note: noteInput, reviewed_at: new Date().toISOString() };
    await supabase?.from('user_risk_flags').upsert(payload, { onConflict: 'user_id' });
    setFlags(prev => ({ ...prev, [editing.id]: payload }));
    setSaving(false);
    setEditing(null);
  };

  const clearFlag = async (userId) => {
    await supabase?.from('user_risk_flags').delete().eq('user_id', userId);
    setFlags(prev => { const n = { ...prev }; delete n[userId]; return n; });
  };

  const flagCounts = (tier) => Object.values(flags).filter(f => f.tier === tier).length;

  return (
    <>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:20 }}>
        <KpiCard label="Auto-detected" value={riskList.filter(u => !u.flag && u.autoScore > 0).length} sub="score-based alerts" color={C.warn}/>
        <KpiCard label="Watch list"    value={flagCounts('watch')}   sub="being monitored"/>
        <KpiCard label="Suspended"     value={flagCounts('suspend')}  sub="access removed" color={C.red}/>
        <KpiCard label="Banned"        value={flagCounts('ban')}      sub="permanent" color="#1A1714"/>
      </div>

      <div style={{ background:C.blueBg, border:`0.5px solid ${C.blue}80`, borderRadius:C.radius, padding:'9px 13px', marginBottom:16, fontSize:12, color:C.blue, lineHeight:1.7 }}>
        <strong>Auto-score:</strong> +15 par reporter unique · +30 si motif grave (harcèlement, faux profil, menaces…) · +5 par signalement en attente · +3 par blocage
        <span style={{ margin:'0 8px', opacity:0.4 }}>·</span>
        <strong>Langage:</strong> +25 par message haineux (insultes racistes, homophobes, menaces) · +10 par message offensant
        <span style={{ marginLeft:10, opacity:0.7 }}>— Seuils : Watch ≥15 · Warn ≥35 · Suspend ≥60 · Ban ≥80</span>
      </div>

      <div style={{ display:'flex', gap:6, marginBottom:14, flexWrap:'wrap' }}>
        {[['all','All'],['auto','Auto only'],['watch','Watch'],['warn','Warn'],['suspend','Suspend'],['ban','Ban']].map(([id,label]) => (
          <button key={id} onClick={() => setFilterTier(id)}
            style={{ padding:'4px 12px', borderRadius:99, border:`0.5px solid ${filterTier===id?C.accent:C.border}`, background:filterTier===id?'rgba(201,168,76,0.12)':'transparent', color:filterTier===id?C.accent:C.muted, fontSize:11, cursor:'pointer', fontWeight:filterTier===id?600:400 }}>
            {label}
          </button>
        ))}
      </div>

      {loading ? <SkeletonTable cols={6} rows={5}/> : riskList.length === 0 ? (
        <p style={{ color:C.muted, fontSize:13, textAlign:'center', padding:32 }}>No at-risk users detected in this network</p>
      ) : (
        <DataTable
          columns={[
            { key:'name', label:'User', render:(v,r) => (
              <div>
                <p style={{margin:0,fontSize:13,fontWeight:500,color:C.text}}>{v||'—'}</p>
                <p style={{margin:0,fontSize:11,color:C.muted}}>{r.email}</p>
              </div>
            )},
            { key:'autoScore', label:'Score', render: v => {
              const t = tierFromScore(v);
              return (
                <div style={{ minWidth:80 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:5, marginBottom:3 }}>
                    <span style={{ fontSize:13, fontWeight:700, color: t ? t.color : C.muted }}>{v}</span>
                    <span style={{ fontSize:10, color:C.muted }}>/100</span>
                    {t && <span style={{ fontSize:10, background:t.bg, color:t.color, border:`0.5px solid ${t.color}55`, borderRadius:99, padding:'1px 6px' }}>{t.label}</span>}
                  </div>
                  <div style={{ height:3, borderRadius:2, background:C.border, overflow:'hidden' }}>
                    <div style={{ width:`${v}%`, height:'100%', background: t ? t.color : C.border, borderRadius:2 }}/>
                  </div>
                </div>
              );
            }},
            { key:'signals', label:'Signaux', render: (v, r) => (
              <div style={{ display:'flex', flexWrap:'wrap', gap:4, maxWidth:220 }}>
                {(v || []).map((s, i) => {
                  const isHate = s.includes('haineux');
                  const isOff  = s.includes('offensant');
                  return (
                    <span key={i} style={{ fontSize:10, padding:'2px 7px', borderRadius:99,
                      background: isHate ? C.redBg : isOff ? C.warnBg : C.bg,
                      color:      isHate ? C.red   : isOff ? C.warn   : C.sub,
                      border:`0.5px solid ${isHate ? C.red+'55' : isOff ? C.warn+'55' : C.border}`,
                    }}>{s}</span>
                  );
                })}
                {(!v || v.length === 0) && <span style={{color:C.muted,fontSize:11}}>—</span>}
              </div>
            )},
            { key:'flag', label:'Flag admin', render:(v,r) => {
              if (!v) return <span style={{color:C.muted,fontSize:12}}>—</span>;
              const band = RISK_BANDS.find(b => b.id === v.tier);
              return (
                <div>
                  <span style={{ fontSize:12, fontWeight:500, color: band?.color || C.sub }}>{v.tier}</span>
                  {v.note && <p style={{margin:'2px 0 0',fontSize:11,color:C.muted,maxWidth:180,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}} title={v.note}>{v.note}</p>}
                </div>
              );
            }},
            { key:'verified', label:'', render: v => <Badge color={v?'green':'amber'}>{v?'Verified':'Unverified'}</Badge> },
            { key:'plan',     label:'Plan', render: v => <Badge>{v||'free'}</Badge> },
            { key:'_icons',   label:'', sortable:false, render:(_,r) => (
              <div style={{ display:'flex', gap:4 }}>
                <IconBtn variant="view" title="Détails" onClick={() => setDetail(r)}/>
                <IconBtn variant="warn" title="Flag" onClick={() => openEdit(r)}>⚑</IconBtn>
                {r.flag && <IconBtn title="Clear flag" danger onClick={() => clearFlag(r.id)}>✕</IconBtn>}
              </div>
            )},
          ]}
          rows={riskList}
          emptyMsg="No at-risk users"
        />
      )}

      {/* ── Detail panel ── */}
      {detail && (() => {
        const userReports = reports.filter(rp => rp.target_id === detail.id);
        const userBlocks  = blocks.filter(b  => b.blocked_id  === detail.id);
        const band        = tierFromScore(detail.autoScore);
        return (
          <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', alignItems:'flex-start', justifyContent:'flex-end' }}
            onClick={() => setDetail(null)}>
            <div style={{ background:C.surface, width:'min(520px,100vw)', height:'100vh', overflowY:'auto', padding:24, boxShadow:'-8px 0 32px rgba(0,0,0,0.2)' }}
              onClick={e => e.stopPropagation()}>

              {/* Header */}
              <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:18 }}>
                <div>
                  <p style={{ margin:0, fontSize:16, fontWeight:700, color:C.text }}>{detail.name}</p>
                  <p style={{ margin:0, fontSize:12, color:C.muted }}>{detail.email}</p>
                </div>
                <button onClick={() => setDetail(null)} style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:C.muted, lineHeight:1 }}>✕</button>
              </div>

              {/* Score banner */}
              <div style={{ background: band ? band.bg : C.bg, border:`0.5px solid ${band ? band.color+'55' : C.border}`, borderRadius:C.radius, padding:'10px 14px', marginBottom:18, display:'flex', alignItems:'center', gap:12 }}>
                <div style={{ fontSize:28, fontWeight:800, color: band ? band.color : C.muted }}>{detail.autoScore}</div>
                <div>
                  <p style={{ margin:0, fontSize:12, color: band ? band.color : C.muted, fontWeight:600 }}>{band ? band.label : 'Below threshold'}</p>
                  <p style={{ margin:0, fontSize:11, color:C.muted }}>Score de risque automatique / 100</p>
                </div>
                {detail.flag && (
                  <div style={{ marginLeft:'auto', textAlign:'right' }}>
                    <p style={{ margin:0, fontSize:11, color:C.muted }}>Flag admin</p>
                    <p style={{ margin:0, fontSize:13, fontWeight:600, color: RISK_BANDS.find(b=>b.id===detail.flag.tier)?.color || C.sub }}>{detail.flag.tier}</p>
                  </div>
                )}
              </div>

              {/* Signals summary */}
              {detail.signals.length > 0 && (
                <div style={{ marginBottom:18 }}>
                  <p style={{ margin:'0 0 8px', fontSize:12, fontWeight:600, color:C.sub, textTransform:'uppercase', letterSpacing:'0.05em' }}>Signaux détectés</p>
                  <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                    {detail.signals.map((s, i) => {
                      const isHate = s.includes('haineux');
                      const isOff  = s.includes('offensant');
                      return (
                        <span key={i} style={{ fontSize:11, padding:'3px 9px', borderRadius:99, background: isHate ? C.redBg : isOff ? C.warnBg : C.bg, color: isHate ? C.red : isOff ? C.warn : C.sub, border:`0.5px solid ${isHate ? C.red+'55' : isOff ? C.warn+'55' : C.border}` }}>{s}</span>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Reports */}
              {userReports.length > 0 && (
                <div style={{ marginBottom:18 }}>
                  <p style={{ margin:'0 0 8px', fontSize:12, fontWeight:600, color:C.sub, textTransform:'uppercase', letterSpacing:'0.05em' }}>Signalements reçus ({userReports.length})</p>
                  <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                    {userReports.map(rp => {
                      const isSrz = RISK_REASONS_SERIOUS.has(rp.reason?.toLowerCase());
                      return (
                        <div key={rp.id} style={{ background:C.bg, border:`0.5px solid ${isSrz ? C.red+'55' : C.border}`, borderRadius:C.radius, padding:'10px 12px' }}>
                          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
                            <span style={{ fontSize:11, fontWeight:600, color: isSrz ? C.red : C.sub, background: isSrz ? C.redBg : C.bg, padding:'1px 6px', borderRadius:99, border:`0.5px solid ${isSrz ? C.red+'44' : C.border}` }}>{rp.reason}</span>
                            <span style={{ fontSize:11, color:C.muted }}>{ago(rp.created_at)}</span>
                            <span style={{ marginLeft:'auto', fontSize:10, color:C.muted, background:C.border, padding:'1px 5px', borderRadius:99 }}>{rp.status}</span>
                          </div>
                          {rp.details && <p style={{ margin:'2px 0 0', fontSize:12, color:C.text, lineHeight:1.5 }}>{rp.details}</p>}
                          <p style={{ margin:'4px 0 0', fontSize:11, color:C.muted }}>Signalé par : {rp.reporter?.name || 'anonyme'}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Blocks */}
              {userBlocks.length > 0 && (
                <div style={{ marginBottom:18 }}>
                  <p style={{ margin:'0 0 8px', fontSize:12, fontWeight:600, color:C.sub, textTransform:'uppercase', letterSpacing:'0.05em' }}>Bloqué par ({userBlocks.length})</p>
                  <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                    {userBlocks.map((b, i) => (
                      <div key={i} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', background:C.bg, border:`0.5px solid ${C.border}`, borderRadius:C.radius, padding:'7px 12px' }}>
                        <span style={{ fontSize:12, color:C.text }}>{b.blocker?.name || '—'}</span>
                        <span style={{ fontSize:11, color:C.muted }}>{ago(b.created_at)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Flagged messages */}
              {detail.flaggedTexts.length > 0 && (
                <div style={{ marginBottom:18 }}>
                  <p style={{ margin:'0 0 8px', fontSize:12, fontWeight:600, color:C.sub, textTransform:'uppercase', letterSpacing:'0.05em' }}>Messages signalés ({detail.flaggedTexts.length})</p>
                  <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                    {detail.flaggedTexts.map((fm, i) => (
                      <div key={i} style={{ background: fm.level === 'severe' ? C.redBg : C.warnBg, border:`0.5px solid ${fm.level === 'severe' ? C.red+'44' : C.warn+'44'}`, borderRadius:C.radius, padding:'9px 12px', fontSize:13, color:C.text, lineHeight:1.6 }}>
                        <span style={{ fontSize:10, fontWeight:600, color: fm.level === 'severe' ? C.red : C.warn, display:'block', marginBottom:4 }}>{fm.level === 'severe' ? '⚠ Haineux' : 'Offensant'}</span>
                        {highlightText(fm.text)}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Flag action */}
              <div style={{ borderTop:`0.5px solid ${C.border}`, paddingTop:16, display:'flex', gap:8 }}>
                <Btn variant="primary" style={{ flex:1 }} onClick={() => { setDetail(null); openEdit(detail); }}>Modifier le flag</Btn>
                {detail.flag && <Btn variant="danger" onClick={() => { clearFlag(detail.id); setDetail(null); }}>Clear flag</Btn>}
              </div>
            </div>
          </div>
        );
      })()}

      {editing && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:999, display:'flex', alignItems:'center', justifyContent:'center' }}
          onClick={() => setEditing(null)}>
          <div style={{ background:C.surface, borderRadius:C.radiusLg, padding:24, width:380, boxShadow:'0 12px 48px rgba(0,0,0,0.25)' }} onClick={e => e.stopPropagation()}>
            <p style={{ fontSize:15, fontWeight:600, color:C.text, margin:'0 0 2px' }}>Flag: {editing.name}</p>
            <p style={{ fontSize:12, color:C.muted, margin:'0 0 4px' }}>Score auto : <strong>{editing.autoScore}</strong>/100{tierFromScore(editing.autoScore) ? ` · ${tierFromScore(editing.autoScore).label}` : ''}</p>
            {(editing.msgs?.severe > 0 || editing.msgs?.moderate > 0) && (
              <p style={{ fontSize:11, color:C.red, margin:'0 0 12px' }}>
                {editing.msgs.severe   > 0 && `⚠ ${editing.msgs.severe} message${editing.msgs.severe>1?'s':''} haineux détecté${editing.msgs.severe>1?'s':''}`}
                {editing.msgs.severe > 0 && editing.msgs.moderate > 0 && ' · '}
                {editing.msgs.moderate > 0 && `${editing.msgs.moderate} message${editing.msgs.moderate>1?'s':''} offensant${editing.msgs.moderate>1?'s':''}`}
              </p>
            )}
            {(!editing.msgs?.severe && !editing.msgs?.moderate) && <p style={{marginBottom:12}}/>}
            <FormField label="Risk tier">
              <Select value={tierInput} onChange={e => setTierInput(e.target.value)} options={[
                { value:'watch',   label:'👁  Watch — monitor, no action yet' },
                { value:'warn',    label:'⚠️  Warn — send a warning message' },
                { value:'suspend', label:'⏸  Suspend — temporary access removal' },
                { value:'ban',     label:'🚫  Ban — permanent removal from network' },
              ]}/>
            </FormField>
            <FormField label="Admin note">
              <textarea value={noteInput} onChange={e => setNoteInput(e.target.value)} rows={3}
                placeholder="Pattern observed, context, decision rationale…"
                style={{ width:'100%', padding:'8px 10px', border:`0.5px solid ${C.borderMd}`, borderRadius:C.radius, fontSize:13, color:C.text, background:C.surface, resize:'vertical', outline:'none', boxSizing:'border-box', fontFamily:'inherit' }}/>
            </FormField>
            <div style={{ display:'flex', gap:8, justifyContent:'flex-end', marginTop:12 }}>
              <Btn onClick={() => setEditing(null)}>Cancel</Btn>
              <Btn variant="primary" onClick={saveFlag} disabled={saving}>{saving ? 'Saving…' : 'Save flag'}</Btn>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ── Moderation section ────────────────────────────────────────────────────
function ReportDetail({ report, onClose, onResolve }) {
  const [convo,    setConvo]    = useState(null);
  const [msgs,     setMsgs]     = useState([]);
  const [profile,  setProfile]  = useState(null);
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    if (!report) return;
    setLoading(true);
    Promise.all([
      // Find conversation between reporter and target
      supabase?.from('conversations')
        .select('id')
        .or(`and(user_a.eq.${report.reporter_id},user_b.eq.${report.target_id}),and(user_a.eq.${report.target_id},user_b.eq.${report.reporter_id})`)
        .maybeSingle(),
      // Fetch target profile (photos, bio)
      supabase?.from('users').select('name,bio,photos,city,plan,verified').eq('id', report.target_id).maybeSingle(),
    ]).then(async ([convRes, profileRes]) => {
      setProfile(profileRes?.data || null);
      if (convRes?.data?.id) {
        setConvo(convRes.data);
        const { data: mData } = await supabase.from('messages')
          .select('id,text,sender_id,created_at')
          .eq('conversation_id', convRes.data.id)
          .order('created_at', { ascending: false }).limit(20);
        setMsgs((mData || []).reverse());
      }
      setLoading(false);
    });
  }, [report?.id]);

  if (!report) return null;
  const REASON_LABELS = {
    harassment: 'Harcèlement', hate_speech: 'Discours haineux', fake_profile: 'Faux profil',
    inappropriate_photos: 'Photos inappropriées', spam: 'Spam', underage: 'Mineur',
    scam: 'Arnaque', threats: 'Menaces', other: 'Autre',
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', alignItems:'flex-start', justifyContent:'flex-end' }}
      onClick={onClose}>
      <div style={{ background:C.surface, width:'min(480px,100vw)', height:'100vh', overflowY:'auto', padding:24, boxShadow:'-8px 0 32px rgba(0,0,0,0.15)' }}
        onClick={e=>e.stopPropagation()}>

        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:20 }}>
          <div>
            <p style={{ margin:0, fontSize:15, fontWeight:600, color:C.text }}>Signalement</p>
            <Badge color={report.status==='pending'?'amber':report.status==='resolved'?'green':'default'}>{report.status}</Badge>
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:C.muted }}>✕</button>
        </div>

        {/* Reason */}
        <div style={{ background:C.redBg, border:`0.5px solid ${C.red}40`, borderRadius:C.radius, padding:'10px 14px', marginBottom:16 }}>
          <p style={{ fontSize:11, color:C.muted, margin:'0 0 2px', textTransform:'uppercase', letterSpacing:'0.06em' }}>Motif</p>
          <p style={{ fontSize:14, fontWeight:600, color:C.red, margin:0 }}>{REASON_LABELS[report.reason] || report.reason || '—'}</p>
          {report.description && <p style={{ fontSize:12, color:C.sub, margin:'6px 0 0', lineHeight:1.5 }}>{report.description}</p>}
        </div>

        {/* Parties */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:16 }}>
          {[['Signaleur', report.reporter], ['Signalé', report.target]].map(([label, u]) => (
            <div key={label} style={{ background:C.bg, borderRadius:C.radius, padding:'10px 12px' }}>
              <p style={{ fontSize:10, color:C.muted, margin:'0 0 4px', textTransform:'uppercase', letterSpacing:'0.06em' }}>{label}</p>
              <p style={{ fontSize:13, fontWeight:500, color:C.text, margin:0 }}>{u?.name || '—'}</p>
              <p style={{ fontSize:11, color:C.muted, margin:0 }}>{u?.email || ''}</p>
            </div>
          ))}
        </div>

        {loading ? <p style={{ color:C.muted, fontSize:12, textAlign:'center', padding:16 }}>Chargement…</p> : (
          <>
            {/* Target profile */}
            {profile && (
              <div style={{ marginBottom:16 }}>
                <p style={{ fontSize:11, fontWeight:600, color:C.sub, margin:'0 0 8px', textTransform:'uppercase', letterSpacing:'0.06em' }}>Profil signalé</p>
                <div style={{ background:C.bg, borderRadius:C.radius, padding:'10px 12px' }}>
                  <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom:6 }}>
                    <span style={{ fontSize:13, fontWeight:500, color:C.text }}>{profile.name}</span>
                    <span style={{ fontSize:11, color:C.muted }}>{profile.city}</span>
                    {profile.verified && <Badge color="green">Vérifié</Badge>}
                    <Badge>{profile.plan||'free'}</Badge>
                  </div>
                  {profile.bio && <p style={{ fontSize:12, color:C.sub, margin:'0 0 8px', lineHeight:1.5 }}>{profile.bio?.slice(0,200)}{profile.bio?.length>200?'…':''}</p>}
                  {(profile.photos||[]).length > 0 && (
                    <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                      {(profile.photos||[]).slice(0,4).map((ph,i) => (
                        <img key={i} src={ph} alt="" style={{ width:64, height:64, objectFit:'cover', borderRadius:4, border:`0.5px solid ${C.border}` }}/>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Conversation */}
            {convo && (
              <div style={{ marginBottom:16 }}>
                <p style={{ fontSize:11, fontWeight:600, color:C.sub, margin:'0 0 8px', textTransform:'uppercase', letterSpacing:'0.06em' }}>Conversation ({msgs.length} messages récents)</p>
                <div style={{ background:C.bg, borderRadius:C.radius, padding:'8px 12px', maxHeight:240, overflowY:'auto', display:'flex', flexDirection:'column', gap:5 }}>
                  {msgs.length === 0 ? <p style={{color:C.muted,fontSize:12}}>Aucun message</p> : msgs.map(m => (
                    <div key={m.id} style={{ fontSize:12, color:m.sender_id===report.reporter_id?C.blue:C.text }}>
                      <span style={{ fontWeight:600, marginRight:5 }}>{m.sender_id===report.reporter_id?report.reporter?.name:report.target?.name}:</span>
                      {highlightText(m.text)}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {!convo && <p style={{ fontSize:12, color:C.muted, marginBottom:16 }}>Aucune conversation entre ces utilisateurs.</p>}
          </>
        )}

        {/* Actions */}
        {report.status === 'pending' && (
          <div style={{ display:'flex', gap:8, paddingTop:16, borderTop:`0.5px solid ${C.border}` }}>
            <Btn variant="danger" onClick={() => { onResolve(report.id, 'resolved'); onClose(); }}>Résoudre</Btn>
            <Btn onClick={() => { onResolve(report.id, 'dismissed'); onClose(); }}>Classer</Btn>
          </div>
        )}
      </div>
    </div>
  );
}

function ModerationTab({ networkId, networkIdsJson }) {
  const [reports,    setReports]    = useState([]);
  const [blocks,     setBlocks]     = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [tab,        setTab]        = useState('reports');
  const [checkedIds, setCheckedIds] = useState(new Set());
  const [detailReport, setDetailReport] = useState(null);

  useEffect(() => {
    const networkIds = networkIdsJson ? JSON.parse(networkIdsJson) : null;
    setLoading(true);
    Promise.all([
      db.reports()?.select('*').order('created_at', { ascending: false }).limit(100),
      db.blocks()?.select('*').order('created_at', { ascending: false }).limit(100),
    ]).then(async ([r, b]) => {
      const allIds = [...new Set([
        ...(r?.data || []).flatMap(x => [x.reporter_id, x.target_id]),
        ...(b?.data || []).flatMap(x => [x.blocker_id, x.blocked_id]),
      ].filter(Boolean))];
      let um = {};
      if (allIds.length) {
        const { data: udata } = await supabase.from('users').select('id,name,email,network_id').in('id', allIds);
        um = Object.fromEntries((udata || []).map(u => [u.id, u]));
      }
      if (r?.data) {
        const enriched = r.data.map(x => ({ ...x, reporter: um[x.reporter_id]||null, target: um[x.target_id]||null }));
        setReports(networkIds ? enriched.filter(x => networkIds.includes(x.reporter?.network_id) || networkIds.includes(x.target?.network_id)) : enriched);
      }
      if (b?.data) {
        const enriched = b.data.map(x => ({ ...x, blocker: um[x.blocker_id]||null, blocked: um[x.blocked_id]||null }));
        setBlocks(networkIds ? enriched.filter(x => networkIds.includes(x.blocker?.network_id) || networkIds.includes(x.blocked?.network_id)) : enriched);
      }
      setLoading(false);
    });
  }, [networkIdsJson]);

  const resolve = async (id, status) => {
    await db.reports()?.update({ status, resolved_at: new Date().toISOString() }).eq('id', id);
    setReports(prev => prev.map(r => r.id === id ? { ...r, status } : r));
  };

  const bulkResolve = async (status) => {
    if (!checkedIds.size) return;
    const ts = new Date().toISOString();
    await Promise.all([...checkedIds].map(id => db.reports()?.update({ status, resolved_at: ts }).eq('id', id)));
    setReports(prev => prev.map(r => checkedIds.has(r.id) ? { ...r, status } : r));
    setCheckedIds(new Set());
  };

  const removeBlock = async (id) => {
    await supabase?.from('blocks').delete().eq('id', id);
    setBlocks(prev => prev.filter(b => b.id !== id));
    setCheckedIds(prev => { const n = new Set(prev); n.delete(id); return n; });
  };

  const bulkRemoveBlocks = async () => {
    if (!checkedIds.size) return;
    await Promise.all([...checkedIds].map(id => supabase?.from('blocks').delete().eq('id', id)));
    setBlocks(prev => prev.filter(b => !checkedIds.has(b.id)));
    setCheckedIds(new Set());
  };

  const pending = reports.filter(r => r.status === 'pending');

  return (
    <>
      <PageHeader title="Moderation" sub={`${pending.length} pending reports`} />
      <div style={{ display:'flex', gap:6, marginBottom:16 }}>
        {[['reports','Reports'], ['blocks','Blocks'], ['prevent','🛡 Prevent']].map(([id,label]) => (
          <button key={id} onClick={() => { setTab(id); setCheckedIds(new Set()); }}
            style={{ padding:'6px 14px', borderRadius:C.radius, border:`0.5px solid ${tab===id?C.accent:C.border}`, background:tab===id?'rgba(201,168,76,0.1)':'transparent', color:tab===id?C.accent:C.sub, fontSize:13, cursor:'pointer', fontWeight:tab===id?500:400 }}>{label}</button>
        ))}
      </div>

      <BatchBar count={checkedIds.size} onClear={() => setCheckedIds(new Set())}>
        {tab === 'reports' && <>
          <Btn size="sm" variant="danger" onClick={() => bulkResolve('resolved')}>Resolve all</Btn>
          <Btn size="sm"                  onClick={() => bulkResolve('dismissed')}>Dismiss all</Btn>
        </>}
        {tab === 'blocks' && (
          <Btn size="sm" variant="danger" onClick={bulkRemoveBlocks}>Remove blocks</Btn>
        )}
      </BatchBar>

      {tab === 'prevent' ? (
        <PreventTab reports={reports} blocks={blocks} networkIdsJson={networkIdsJson} />
      ) : loading ? <SkeletonTable cols={5} rows={5} /> : tab === 'reports' ? (
        <>
          <DataTable
            selectable selectedIds={checkedIds} onSelectionChange={setCheckedIds}
            columns={[
              { key:'reporter',   label:'Signaleur', render: v => v ? <div><p style={{margin:0,fontSize:13}}>{v.name}</p>{!networkId && v.network_id && <p style={{margin:0,fontSize:10,color:C.muted}}>{netCity(v.network_id)}</p>}</div> : '—' },
              { key:'target',     label:'Signalé',   render: v => v ? <div><p style={{margin:0,fontSize:13}}>{v.name}</p><p style={{margin:0,fontSize:11,color:C.muted}}>{v.email}</p>{!networkId && v.network_id && <p style={{margin:0,fontSize:10,color:C.muted}}>{netCity(v.network_id)}</p>}</div> : '—' },
              { key:'reason',     label:'Motif',     render: v => <span style={{ fontSize:12 }}>{v}</span> },
              { key:'status',     label:'Statut',    render: v => <Badge color={v==='pending'?'amber':v==='resolved'?'green':'default'}>{v}</Badge> },
              { key:'created_at', label:'Date',      render: v => v ? ago(v) : '—' },
              { key:'_icons', label:'', sortable:false, render:(_,r) => (
                <div style={{ display:'flex', gap:4 }}>
                  <IconBtn variant="view" title="Voir détails" onClick={() => setDetailReport(r)}/>
                  {r.status === 'pending' && <>
                    <IconBtn variant="confirm" title="Résoudre" onClick={() => resolve(r.id,'resolved')}/>
                    <IconBtn title="Classer" onClick={() => resolve(r.id,'dismissed')}>—</IconBtn>
                  </>}
                </div>
              )},
            ]}
            rows={reports}
            emptyMsg="No reports"
          />
          {detailReport && (
            <ReportDetail
              report={detailReport}
              onClose={() => setDetailReport(null)}
              onResolve={resolve}
            />
          )}
        </>
      ) : (
        <DataTable
          selectable selectedIds={checkedIds} onSelectionChange={setCheckedIds}
          columns={[
            { key:'blocker', label:'Bloqueur', render: v => v ? <div><p style={{margin:0,fontSize:13}}>{v.name}</p>{!networkId && v.network_id && <p style={{margin:0,fontSize:10,color:C.muted}}>{netCity(v.network_id)}</p>}</div> : '—' },
            { key:'blocked', label:'Bloqué',   render: v => v ? <div><p style={{margin:0,fontSize:13}}>{v.name}</p>{!networkId && v.network_id && <p style={{margin:0,fontSize:10,color:C.muted}}>{netCity(v.network_id)}</p>}</div> : '—' },
            { key:'created_at', label:'Date',     render: v => v ? ago(v) : '—' },
            { key:'_icons', label:'', sortable:false, render:(_,r) => (
              <IconBtn title="Supprimer le blocage" danger onClick={() => removeBlock(r.id)}>✕</IconBtn>
            )},
          ]}
          rows={blocks}
          emptyMsg="No blocks"
        />
      )}
    </>
  );
}

function NotificationsAdminTab({ networkId, networkIdsJson }) {
  const [form, setForm]         = useState({ title:'', body:'', type:'announcement' });
  const [categoryIds, setCategoryIds] = useState(new Set());
  const [sent, setSent]         = useState(false);
  const [sending, setSending]   = useState(false);
  const [history, setHistory]   = useState([]);
  const [users, setUsers]       = useState([]);
  const [interestsByUser, setInterestsByUser] = useState({});
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    db.notifications()?.select('id,title,body,type,created_at').order('created_at', { ascending: false }).limit(20)
      .then(({ data }) => { if (data) setHistory(data); });

    let userQ = supabase?.from('user_profiles').select('user_id');
    if (networkId) userQ = userQ?.eq('network_id', networkId);
    else if (networkIdsJson) { const ids = JSON.parse(networkIdsJson); userQ = userQ?.in('network_id', ids); }
    userQ?.then(({ data }) => { if (data) setUsers(data.map(r => ({ id: r.user_id }))); });

    supabase?.from('user_profiles').select('user_id, interests')
      .then(({ data }) => {
        const map = {};
        (data || []).forEach(p => { map[p.user_id] = p.interests || []; });
        setInterestsByUser(map);
      });
  }, [networkId, networkIdsJson]);

  const targetIds = categoryIds.size === 0 ? [] :
    users.filter(u => (interestsByUser[u.id] || []).some(i => categoryIds.has(i))).map(u => u.id);
  const targetCount = targetIds.length;

  const broadcast = async () => {
    if (!form.title || !form.body || !targetCount) return;
    setSending(true);
    const rows = targetIds.map(id => ({ user_id: id, type: form.type, title: form.title, body: form.body }));
    for (let i = 0; i < rows.length; i += 500) {
      await db.notifications()?.insert(rows.slice(i, i+500));
    }
    setSent(true); setSending(false);
    setTimeout(() => setSent(false), 3000);
  };

  return (
    <>
      <PageHeader title="Notifications" sub="Category-based broadcasting" />
      <div style={{ display:'grid', gridTemplateColumns:'1.2fr 1fr', gap:24 }}>
        <div style={{ background:C.surface, border:`0.5px solid ${C.border}`, borderRadius:C.radiusLg, padding:20 }}>
          <p style={{ fontSize:14, fontWeight:500, color:C.text, margin:'0 0 4px' }}>Send notification</p>
          <p style={{ fontSize:12, color:C.muted, margin:'0 0 16px' }}>Select interest categories — recipients are members who match at least one.</p>

          <div style={{ border:`0.5px solid ${C.border}`, borderRadius:C.radius, padding:'10px 12px', maxHeight:260, overflowY:'auto', background:C.bg, marginBottom:4 }}>
            <CategoryTreePicker selected={categoryIds} onChange={setCategoryIds} lang="fr" compact />
          </div>
          <div style={{ fontSize:11, color: targetCount > 0 ? C.green : C.muted, margin:'4px 0 14px' }}>
            {users.length === 0 ? 'Loading…' : categoryIds.size === 0 ? 'Select at least one category to target' : targetCount === 0 ? 'No users match' : `Will send to ${targetCount} user${targetCount === 1 ? '' : 's'}`}
          </div>

          <FormField label="Title"><Input value={form.title} onChange={e=>set('title',e.target.value)} placeholder="🐝 New feature available"/></FormField>
          <FormField label="Message">
            <textarea value={form.body} onChange={e=>set('body',e.target.value)} rows={4} placeholder="Your message here…"
              style={{ width:'100%', padding:'8px 10px', border:`0.5px solid ${C.borderMd}`, borderRadius:C.radius, fontSize:13, color:C.text, background:C.surface, resize:'vertical', outline:'none', boxSizing:'border-box' }}/>
          </FormField>
          <FormField label="Type">
            <Select value={form.type} onChange={e=>set('type',e.target.value)} options={[
              {value:'announcement',label:'Announcement'},{value:'system',label:'System'},{value:'event_reminder',label:'Event reminder'},{value:'circle_proposal',label:'Scout proposal'},
            ]}/>
          </FormField>
          <Btn variant="primary" onClick={broadcast} disabled={sending||!form.title||!form.body||!targetCount}>
            {sent ? '✓ Sent!' : sending ? 'Sending…' : `Send to ${targetCount} user${targetCount === 1 ? '' : 's'}`}
          </Btn>
        </div>
        <div style={{ background:C.surface, border:`0.5px solid ${C.border}`, borderRadius:C.radiusLg, padding:20 }}>
          <p style={{ fontSize:14, fontWeight:500, color:C.text, margin:'0 0 12px' }}>Recent broadcasts</p>
          {history.map(n => (
            <div key={n.id} style={{ padding:'8px 0', borderBottom:`0.5px solid ${C.border}` }}>
              <p style={{ fontSize:13, fontWeight:500, color:C.text, margin:0 }}>{n.title}</p>
              <p style={{ fontSize:12, color:C.sub, margin:'2px 0' }}>{n.body?.slice(0,60)}{n.body?.length>60?'…':''}</p>
              <p style={{ fontSize:11, color:C.muted, margin:0 }}>{ago(n.created_at)}</p>
            </div>
          ))}
          {history.length === 0 && <p style={{ fontSize:12, color:C.muted }}>No broadcasts yet</p>}
        </div>
      </div>
    </>
  );
}

// ── Sponsors section ──────────────────────────────────────────────────────
function SponsorsTab({ sponsors, setSponsors, networkId }) {
  const [editing,  setEditing]  = useState(null);
  const [saving,   setSaving]   = useState(false);
  const [selected, setSelected] = useState(new Set());

  const save = async (form) => {
    setSaving(true);
    if (form.id && String(form.id).length === 36) {
      await db.sponsors()?.update(form).eq('id', form.id);
      setSponsors(prev => prev.map(s => s.id === form.id ? { ...s, ...form } : s));
    } else {
      const { data } = await db.sponsors()?.insert(form).select('id').single();
      if (data) setSponsors(prev => [...prev, { ...form, id: data.id }]);
    }
    setSaving(false);
    setEditing(null);
  };

  const del = async (id) => {
    if (!window.confirm('Delete this sponsor?')) return;
    await db.sponsors()?.delete().eq('id', id);
    setSponsors(prev => prev.filter(s => s.id !== id));
    setSelected(prev => { const n = new Set(prev); n.delete(id); return n; });
  };

  const bulkDelete = async () => {
    if (!selected.size || !window.confirm(`Delete ${selected.size} sponsors?`)) return;
    await Promise.all([...selected].map(id => db.sponsors()?.delete().eq('id', id)));
    setSponsors(prev => prev.filter(s => !selected.has(s.id)));
    setSelected(new Set());
  };

  return (
    <>
      <PageHeader
        title="Sponsors"
        sub={`${sponsors.length} sponsors`}
        action={<Btn variant="primary" onClick={() => setEditing({ status:'prospect', monthly_value:0, network_id: networkId })}>+ Add sponsor</Btn>}
      />
      <BatchBar count={selected.size} onClear={() => setSelected(new Set())}>
        <Btn size="sm" variant="danger" onClick={bulkDelete}>Delete selected</Btn>
      </BatchBar>
      <DataTable
        selectable selectedIds={selected} onSelectionChange={setSelected}
        columns={[
          { key:'name',             label:'Sponsor',  render:(v,r) => <div><p style={{margin:0,fontSize:13,fontWeight:500}}>{v}</p><p style={{margin:0,fontSize:11,color:C.muted}}>{r.category}</p></div> },
          ...(!networkId ? [{ key:'network_id', label:'City', render: v => v ? <span style={{ fontSize:11, color:C.muted }}>{netCity(v)}</span> : '—' }] : []),
          { key:'partnership_type', label:'Type',     render: v => { const PT_ICONS = {venue_partner:'🏛️',event_sponsor:'📣',member_offer:'🎯',first_date:'💑',circle_sponsor:'⭕',cobranded:'✨'}; const PT_LABELS = {venue_partner:'Venue',event_sponsor:'Événement',member_offer:'Offre membre',first_date:'1er RDV',circle_sponsor:'Cercle',cobranded:'Co-brandé'}; return <span style={{fontSize:12}}>{PT_ICONS[v]||'🤝'} {PT_LABELS[v]||v||'—'}</span>; } },
          { key:'status',           label:'Status',   render: v => <Badge color={v==='active'?'green':v==='signed'?'blue':v==='prospect'?'amber':'default'}>{v}</Badge> },
          { key:'monthly_value',    label:'Monthly',  render: v => v ? fmtC(v) : '—' },
          { key:'contact_email',    label:'Contact',  render: v => v||'—' },
          { key:'_icons', label:'', sortable:false, render:(v,r) => (
            <div style={{ display:'flex', gap:4 }}>
              <IconBtn variant="edit" title="Edit" onClick={() => setEditing(r)}/>
              <IconBtn title="Delete" danger onClick={() => del(r.id)}>✕</IconBtn>
            </div>
          )},
        ]}
        rows={sponsors}
        emptyMsg="No sponsors yet"
      />
      {editing && (
        <Modal title={editing.id ? 'Edit sponsor' : 'New sponsor'} onClose={() => setEditing(null)}>
          <SponsorForm sponsor={editing} onSave={save} saving={saving} />
        </Modal>
      )}
    </>
  );
}

const ADMIN_PT_OPTIONS = [
  { value:'venue_partner',  label:'🏛️ Partenaire Venue' },
  { value:'event_sponsor',  label:"📣 Commanditaire d'événement" },
  { value:'member_offer',   label:'🎯 Offre membre exclusive' },
  { value:'first_date',     label:'💑 Premier rendez-vous recommandé' },
  { value:'circle_sponsor', label:'⭕ Parrainage de Cercle' },
  { value:'cobranded',      label:'✨ Événement co-brandé' },
];

function SponsorForm({ sponsor, onSave, saving }) {
  const [form, setForm] = useState({ ...sponsor });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  return (
    <div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
        <FormField label="Name"><Input value={form.name||''} onChange={e=>set('name',e.target.value)}/></FormField>
        <FormField label="Category"><Input value={form.category||''} onChange={e=>set('category',e.target.value)} placeholder="Venue, Activity…"/></FormField>
        <FormField label="Type de partenariat" style={{ gridColumn:'1/-1' }}>
          <Select value={form.partnership_type||'venue_partner'} onChange={e=>set('partnership_type',e.target.value)} options={ADMIN_PT_OPTIONS}/>
        </FormField>
        {form.partnership_type === 'circle_sponsor' && (
          <FormField label="Nom du BeeCircle">
            <Input value={form.circle_name||''} onChange={e=>set('circle_name',e.target.value)} placeholder="ex: Foodies MTL, Bien-être…"/>
          </FormField>
        )}
        <FormField label="Status">
          <Select value={form.status||'prospect'} onChange={e=>set('status',e.target.value)} options={[{value:'prospect',label:'Prospect'},{value:'contacted',label:'Contacted'},{value:'signed',label:'Signed'},{value:'active',label:'Active'},{value:'paused',label:'Paused'}]}/>
        </FormField>
        <FormField label="Monthly value ($)"><Input type="number" value={form.monthly_value||0} onChange={e=>set('monthly_value',parseFloat(e.target.value)||0)}/></FormField>
        <FormField label="Contact email"><Input type="email" value={form.contact_email||''} onChange={e=>set('contact_email',e.target.value)}/></FormField>
      </div>
      <FormField label="Notes">
        <textarea value={form.notes||''} onChange={e=>set('notes',e.target.value)} rows={2}
          style={{ width:'100%', padding:'8px 10px', border:`0.5px solid ${C.borderMd}`, borderRadius:C.radius, fontSize:13, color:C.text, background:C.surface, resize:'vertical', outline:'none', boxSizing:'border-box' }}/>
      </FormField>
      <div style={{ display:'flex', justifyContent:'flex-end', paddingTop:16, borderTop:`0.5px solid ${C.border}` }}>
        <Btn variant="primary" onClick={() => onSave(form)} disabled={saving}>{saving?'Saving…':'Save sponsor'}</Btn>
      </div>
    </div>
  );
}

// ── Countries section ─────────────────────────────────────────────────────
function CountriesTab() {
  const [countries, setCountries] = useState([]);
  const [editing, setEditing]     = useState(null);
  const [loading, setLoading]     = useState(true);

  useEffect(() => {
    supabase?.from('countries').select('*').order('name')
      .then(({ data }) => { if (data) setCountries(data); setLoading(false); });
  }, []);

  const save = async (form) => {
    if (form.code && countries.find(c => c.code === form.code && !editing?.code)) {
      // new row
      const { data } = await supabase?.from('countries').insert(form).select().single() || {};
      if (data) setCountries(prev => [...prev, data].sort((a,b) => a.name.localeCompare(b.name)));
    } else {
      await supabase?.from('countries').update({ name:form.name, name_fr:form.name_fr, flag:form.flag, active:form.active, currency:form.currency, language_default:form.language_default }).eq('code', form.code);
      setCountries(prev => prev.map(c => c.code === form.code ? { ...c, ...form } : c));
    }
    setEditing(null);
  };

  const toggleActive = async (code, active) => {
    await supabase?.from('countries').update({ active }).eq('code', code);
    setCountries(prev => prev.map(c => c.code === code ? { ...c, active } : c));
  };

  return (
    <>
      <PageHeader title="Countries" sub="Manage active countries and their defaults"
        action={<Btn variant="primary" onClick={() => setEditing({ active:true })}>+ Add country</Btn>}
      />
      {loading ? <p style={{ color:C.muted }}>Loading…</p> : (
        <DataTable
          columns={[
            { key:'flag',     label:'',        render: v => <span style={{ fontSize:18 }}>{v||'🌍'}</span> },
            { key:'code',     label:'Code',    render: v => <code style={{ fontSize:12, background:C.bg, padding:'2px 6px', borderRadius:4 }}>{v}</code> },
            { key:'name',     label:'Name',    render:(v,r) => <span style={{ fontWeight:500, color:C.text }}>{v}{r.name_fr ? ` / ${r.name_fr}` : ''}</span> },
            { key:'currency', label:'Currency',render: v => v||'—' },
            { key:'language_default', label:'Lang', render: v => v||'—' },
            { key:'active',   label:'Active',  render: v => <Badge color={v?'green':'default'}>{v?'Active':'Inactive'}</Badge> },
            { key:'_icons',   label:'', sortable:false, render:(_,r) => (
              <div style={{ display:'flex', gap:4 }}>
                <IconBtn title={r.active?'Désactiver':'Activer'} onClick={() => toggleActive(r.code, !r.active)} color={r.active?C.warn:C.green}>
                  {r.active ? '⏸' : '▶'}
                </IconBtn>
                <IconBtn variant="edit" title="Edit" onClick={() => setEditing(r)}/>
              </div>
            )},
          ]}
          rows={countries}
          onRowClick={setEditing}
          emptyMsg="No countries yet"
        />
      )}
      {editing && (
        <Modal title={editing.code && countries.find(c=>c.code===editing.code) ? `Edit ${editing.name||editing.code}` : 'New country'} onClose={() => setEditing(null)}>
          <CountryForm country={editing} onSave={save}/>
        </Modal>
      )}
    </>
  );
}

function CountryForm({ country, onSave }) {
  const [form, setForm] = useState({ active:true, ...country });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const isNew = !country?.code || !country?.name;
  return (
    <div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:16 }}>
        <FormField label="ISO code (e.g. CA)">
          <Input value={form.code||''} onChange={e=>set('code',e.target.value.toUpperCase())} placeholder="CA" disabled={!isNew} maxLength={2}/>
        </FormField>
        <FormField label="Flag emoji">
          <Input value={form.flag||''} onChange={e=>set('flag',e.target.value)} placeholder="🇨🇦"/>
        </FormField>
        <FormField label="Name (EN)">
          <Input value={form.name||''} onChange={e=>set('name',e.target.value)} placeholder="Canada"/>
        </FormField>
        <FormField label="Name (FR)">
          <Input value={form.name_fr||''} onChange={e=>set('name_fr',e.target.value)} placeholder="Canada"/>
        </FormField>
        <FormField label="Currency">
          <Input value={form.currency||''} onChange={e=>set('currency',e.target.value)} placeholder="CAD"/>
        </FormField>
        <FormField label="Default language">
          <Select value={form.language_default||'en'} onChange={e=>set('language_default',e.target.value)}
            options={[{value:'en',label:'English'},{value:'fr',label:'Français'}]}/>
        </FormField>
        <FormField label="Active" style={{ gridColumn:'1/-1' }}>
          <label style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, marginTop:8 }}>
            <input type="checkbox" checked={!!form.active} onChange={e=>set('active',e.target.checked)}/>
            Visible and selectable by users
          </label>
        </FormField>
      </div>
      <div style={{ display:'flex', justifyContent:'flex-end', paddingTop:16, borderTop:`0.5px solid ${C.border}` }}>
        <Btn variant="primary" onClick={() => onSave(form)}>Save country</Btn>
      </div>
    </div>
  );
}

// ── Networks section ──────────────────────────────────────────────────────
function NetworksTab() {
  const [networks, setNetworks] = useState([]);
  const [editing, setEditing]   = useState(null);

  useEffect(() => {
    db.networks()?.select('*').then(({ data }) => { if (data) setNetworks(data); });
  }, []);

  const save = async (form) => {
    if (form.id) {
      await db.networks()?.update(form).eq('id', form.id);
      setNetworks(prev => prev.map(n => n.id === form.id ? { ...n, ...form } : n));
    } else {
      await db.networks()?.insert(form);
      setNetworks(prev => [...prev, form]);
    }
    setEditing(null);
  };

  return (
    <>
      <PageHeader title="Networks" sub="Geographic communities"
        action={<Btn variant="primary" onClick={() => setEditing({ active: true })}>+ New network</Btn>}
      />
      <DataTable
        columns={[
          { key:'id',      label:'ID',      render: v => <code style={{ fontSize:12, background:C.bg, padding:'2px 6px', borderRadius:4 }}>{v}</code> },
          { key:'label',   label:'Name',    render:(v,r) => <span>{r.label_fr ? `${v} / ${r.label_fr}` : v}</span> },
          { key:'country', label:'Country', render: v => <Badge>{v}</Badge> },
          { key:'timezone',label:'Timezone',render: v => v||'—' },
          { key:'active',  label:'Active',  render: v => <Badge color={v?'green':'default'}>{v?'yes':'no'}</Badge> },
          { key:'_icons',  label:'', sortable:false, render:(_,r) => <IconBtn variant="edit" title="Edit" onClick={() => setEditing(r)}/> },
        ]}
        rows={networks}
        onRowClick={setEditing}
        emptyMsg="No networks"
      />
      {editing && (
        <Modal title={editing.id ? 'Edit network' : 'New network'} onClose={() => setEditing(null)}>
          <NetworkForm network={editing} onSave={save} />
        </Modal>
      )}
    </>
  );
}

const NET_COUNTRIES = [
  {value:'CA',label:'🇨🇦 Canada'},{value:'FR',label:'🇫🇷 France'},{value:'BE',label:'🇧🇪 Belgique'},
  {value:'CH',label:'🇨🇭 Suisse'},{value:'GB',label:'🇬🇧 United Kingdom'},{value:'US',label:'🇺🇸 United States'},
  {value:'DE',label:'🇩🇪 Deutschland'},{value:'ES',label:'🇪🇸 España'},{value:'IT',label:'🇮🇹 Italia'},
  {value:'PT',label:'🇵🇹 Portugal'},{value:'NL',label:'🇳🇱 Netherlands'},{value:'LU',label:'🇱🇺 Luxembourg'},
  {value:'AU',label:'🇦🇺 Australia'},{value:'AE',label:'🇦🇪 UAE'},{value:'SG',label:'🇸🇬 Singapore'},
];
const NET_TIMEZONES = {
  CA:'America/Toronto',FR:'Europe/Paris',BE:'Europe/Brussels',CH:'Europe/Zurich',
  GB:'Europe/London',US:'America/New_York',DE:'Europe/Berlin',ES:'Europe/Madrid',
  IT:'Europe/Rome',PT:'Europe/Lisbon',NL:'Europe/Amsterdam',LU:'Europe/Luxembourg',
  AU:'Australia/Sydney',AE:'Asia/Dubai',SG:'Asia/Singapore',
};
const toSlug = (country, city) =>
  `${country.toLowerCase()}_${city.toLowerCase().trim().replace(/\s+/g,'_').replace(/[^a-z0-9_]/g,'')}`;

function NetworkForm({ network, onSave }) {
  const isNew = !network?.id;
  const [form, setForm] = useState({ country:'CA', active:true, ...network });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleLabelChange = (v) => {
    set('label', v);
    if (isNew && v && form.country) set('id', toSlug(form.country, v));
  };
  const handleCountryChange = (v) => {
    set('country', v);
    set('timezone', NET_TIMEZONES[v] || '');
    if (isNew && form.label) set('id', toSlug(v, form.label));
  };

  return (
    <div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
        <FormField label="Country">
          <Select value={form.country||'CA'} onChange={e=>handleCountryChange(e.target.value)} options={NET_COUNTRIES}/>
        </FormField>
        <FormField label="Label (EN)">
          <Input value={form.label||''} onChange={e=>handleLabelChange(e.target.value)} placeholder="London"/>
        </FormField>
        <FormField label="ID (slug)">
          <Input value={form.id||''} onChange={e=>set('id',e.target.value)} placeholder="gb_london"
            style={{ fontFamily:'monospace', fontSize:12 }}/>
        </FormField>
        <FormField label="Label (FR)"><Input value={form.label_fr||''} onChange={e=>set('label_fr',e.target.value)}/></FormField>
        <FormField label="Timezone"><Input value={form.timezone||''} onChange={e=>set('timezone',e.target.value)} placeholder="Europe/London"/></FormField>
        <FormField label="Active"><label style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, marginTop:8 }}><input type="checkbox" checked={!!form.active} onChange={e=>set('active',e.target.checked)}/> Active</label></FormField>
      </div>
      {isNew && form.id && (
        <p style={{ fontSize:11, color:C.muted, margin:'4px 0 0' }}>Slug auto-généré : <code style={{ color:C.accent }}>{form.id}</code></p>
      )}
      <div style={{ display:'flex', justifyContent:'flex-end', paddingTop:16, borderTop:`0.5px solid ${C.border}`, marginTop:12 }}>
        <Btn variant="primary" onClick={() => onSave(form)}>Save network</Btn>
      </div>
    </div>
  );
}

// ── Monetization section ──────────────────────────────────────────────────
// ── Pricing currency symbol helper ───────────────────────────────────────────
function currSym(currency) {
  if (currency === 'EUR') return '€';
  if (currency === 'CHF') return 'CHF ';
  return '$';
}

function MonetizationTab({ selectedCountry = null, selectedNetwork = null }) {
  const [rules, setRules]               = useState([]);
  const [countries, setCountries]       = useState([]);
  const [networks, setNetworks]         = useState([]);
  const [countryPricing, setCountryPricing] = useState([]);  // [{country_code, plan, price, currency}]
  const [netPricing, setNetPricing]     = useState([]);       // [{network_id, plan, price, currency}]
  const [selCountry, setSelCountry]     = useState('');       // country tab
  const [editRule, setEditRule]         = useState(null);
  const [editPrice, setEditPrice]       = useState(null);     // {level:'country'|'network', key, plan, price, currency}
  const [saving, setSaving]             = useState(false);

  useEffect(() => {
    db.revenue_rules()?.select('*').order('effective_from', { ascending: false })
      .then(({ data }) => { if (data) setRules(data); });
    db.countries()?.select('*').order('name')
      .then(({ data }) => {
        if (data) {
          setCountries(data);
          // Auto-select: sidebar filter if active + active country, else first active country
          const active = data.filter(c => c.active);
          const init = (selectedCountry && active.find(c => c.code === selectedCountry))
            ? selectedCountry
            : (active[0]?.code || '');
          setSelCountry(init);
        }
      });
    db.networks()?.select('id,label,currency,country').order('label')
      .then(({ data }) => { if (data) setNetworks(data); });
    db.country_plan_pricing()?.select('*')
      .then(({ data }) => { if (data) setCountryPricing(data); });
    db.plan_pricing()?.select('*')
      .then(({ data }) => { if (data) setNetPricing(data); });
  }, []);

  // Sync selCountry when sidebar filter changes (only if that country is active)
  useEffect(() => {
    if (selectedCountry && countries.find(c => c.code === selectedCountry && c.active))
      setSelCountry(selectedCountry);
  }, [selectedCountry, countries]);

  const saveRule = async (form) => {
    if (form.id) {
      await db.revenue_rules()?.update(form).eq('id', form.id);
      setRules(prev => prev.map(r => r.id === form.id ? { ...r, ...form } : r));
    } else {
      await db.revenue_rules()?.insert(form);
      setRules(prev => [...prev, form]);
    }
    setEditRule(null);
  };

  const savePrice = async (form) => {
    setSaving(true);
    if (form.level === 'country') {
      await db.country_plan_pricing()?.upsert(
        { country_code: form.key, plan: form.plan, price: form.price, currency: form.currency },
        { onConflict: 'country_code,plan' }
      );
      setCountryPricing(prev => {
        const idx = prev.findIndex(p => p.country_code === form.key && p.plan === form.plan);
        const row = { country_code: form.key, plan: form.plan, price: form.price, currency: form.currency };
        if (idx >= 0) { const n = [...prev]; n[idx] = row; return n; }
        return [...prev, row];
      });
    } else {
      await db.plan_pricing()?.upsert(
        { network_id: form.key, plan: form.plan, price: form.price, currency: form.currency },
        { onConflict: 'network_id,plan' }
      );
      setNetPricing(prev => {
        const idx = prev.findIndex(p => p.network_id === form.key && p.plan === form.plan);
        const row = { network_id: form.key, plan: form.plan, price: form.price, currency: form.currency };
        if (idx >= 0) { const n = [...prev]; n[idx] = row; return n; }
        return [...prev, row];
      });
    }
    setSaving(false);
    setEditPrice(null);
  };

  const toggleCountry = async (code, active) => {
    await db.countries()?.update({ active }).eq('code', code);
    setCountries(prev => prev.map(c => c.code === code ? { ...c, active } : c));
  };

  const planOrder = ['free', 'honey', 'royal'];
  const selC = countries.find(c => c.code === selCountry);
  const countryNets = networks.filter(n => n.country === selCountry &&
    (!selectedNetwork || n.id === selectedNetwork));
  // Only active countries appear in Plans & Pricing; sidebar filter further restricts
  const visibleCountries = countries.filter(c =>
    c.active && (!selectedCountry || c.code === selectedCountry));

  return (
    <>
      <PageHeader title="Plans & Pricing"
        sub={selectedCountry
          ? `Filtered: ${countries.find(c => c.code === selectedCountry)?.name || selectedCountry}`
          : 'Country activation · prices per country and per city'} />

      {/* Country tabs */}
      <div style={{ display:'flex', gap:6, marginBottom:20, flexWrap:'wrap' }}>
        {visibleCountries.map(c => (
          <button key={c.code} onClick={() => setSelCountry(c.code)} style={{
            padding:'5px 14px', borderRadius:C.radius, fontSize:12, fontWeight:500,
            cursor:'pointer', border:`0.5px solid ${c.active ? C.green : C.border}`,
            background: selCountry === c.code ? (c.active ? C.greenBg : C.bg) : C.surface,
            color: selCountry === c.code ? (c.active ? C.green : C.text) : C.sub,
          }}>
            {c.code} · {c.name}
            <span style={{ marginLeft:6, fontSize:10, opacity:0.7 }}>{c.currency}</span>
            {c.active
              ? <span style={{ marginLeft:6, fontSize:9, background:C.green, color:'#fff', borderRadius:3, padding:'1px 4px' }}>LIVE</span>
              : <span style={{ marginLeft:6, fontSize:9, background:C.border, color:C.muted, borderRadius:3, padding:'1px 4px' }}>OFF</span>}
          </button>
        ))}
      </div>

      {selC && (
        <>
          {/* Country header — read-only; activation managed from Countries tab */}
          <div style={{ marginBottom:16, padding:'10px 14px', background:C.greenBg,
            border:`0.5px solid ${C.green}`, borderRadius:C.radiusLg }}>
            <p style={{ margin:0, fontWeight:600, fontSize:13, color:C.green }}>
              {selC.name}
            </p>
            <p style={{ margin:'2px 0 0', fontSize:11, color:C.sub }}>
              Currency: {selC.currency}
              {countryNets.length > 0 && ` · ${countryNets.length} network${countryNets.length > 1 ? 's' : ''}`}
              {selC.launch_date ? ` · Launch: ${selC.launch_date}` : ''}
            </p>
          </div>

          {/* Country-level default prices */}
          <p style={{ fontSize:12, fontWeight:500, color:C.sub, marginBottom:8, textTransform:'uppercase', letterSpacing:'0.08em' }}>
            Default prices — applies to all cities in {selC.name}
          </p>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10, marginBottom:24 }}>
            {planOrder.map(planId => {
              const p = countryPricing.find(x => x.country_code === selCountry && x.plan === planId);
              return (
                <div key={planId} style={{ background:C.surface, border:`0.5px solid ${C.border}`, borderRadius:C.radiusLg, padding:14 }}>
                  <p style={{ fontSize:12, fontWeight:600, color:C.text, margin:'0 0 2px', textTransform:'capitalize' }}>{planId}</p>
                  <p style={{ fontSize:20, fontWeight:500, color:C.accent, margin:'0 0 8px' }}>
                    {p ? (p.price === 0 ? 'Free' : `${currSym(p.currency)}${p.price}/mo`) : '—'}
                  </p>
                  <Btn size="sm" onClick={() => setEditPrice({
                    level: 'country', key: selCountry, plan: planId,
                    price: p?.price ?? 0, currency: p?.currency ?? selC.currency,
                  })}>Edit</Btn>
                </div>
              );
            })}
          </div>

          {/* Per-city overrides */}
          {countryNets.length > 0 && (
            <>
              <p style={{ fontSize:12, fontWeight:500, color:C.sub, marginBottom:8, textTransform:'uppercase', letterSpacing:'0.08em' }}>
                City overrides — leave empty to inherit country defaults
              </p>
              {countryNets.map(net => (
                <div key={net.id} style={{ marginBottom:12, padding:'10px 14px', background:C.surface, border:`0.5px solid ${C.border}`, borderRadius:C.radiusLg }}>
                  <p style={{ fontWeight:600, fontSize:12, color:C.text, margin:'0 0 8px' }}>{net.label}</p>
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:8 }}>
                    {planOrder.map(planId => {
                      const np = netPricing.find(x => x.network_id === net.id && x.plan === planId);
                      const cp = countryPricing.find(x => x.country_code === selCountry && x.plan === planId);
                      return (
                        <div key={planId} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:6,
                          padding:'4px 8px', background:C.bg, borderRadius:C.radius }}>
                          <span style={{ fontSize:11, color:C.sub, textTransform:'capitalize' }}>{planId}</span>
                          <span style={{ fontSize:12, fontWeight:500, color: np ? C.blue : C.muted }}>
                            {np
                              ? (np.price === 0 ? 'Free' : `${currSym(np.currency)}${np.price}`)
                              : <span style={{ fontStyle:'italic', fontSize:10 }}>default ({cp ? (cp.price === 0 ? 'Free' : `${currSym(cp.currency)}${cp.price}`) : '—'})</span>}
                          </span>
                          <Btn size="sm" onClick={() => setEditPrice({
                            level: 'network', key: net.id, plan: planId,
                            price: np?.price ?? cp?.price ?? 0,
                            currency: np?.currency ?? cp?.currency ?? selC.currency,
                          })}>Edit</Btn>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </>
          )}
        </>
      )}

      <div style={{ marginTop:24, display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
        <p style={{ fontSize:14, fontWeight:500, color:C.text, margin:0 }}>Revenue split rules</p>
        <Btn variant="primary" size="sm" onClick={() => setEditRule({ source:'events', cml_pct:0.5, platform_pct:0.3, fund_pct:0.2, effective_from:new Date().toISOString().split('T')[0] })}>+ Add rule</Btn>
      </div>
      <DataTable
        columns={[
          { key:'source',        label:'Source',     render: v => <Badge color="blue">{v}</Badge> },
          { key:'cml_pct',       label:'CML %',      render: v => `${Math.round(v*100)}%` },
          { key:'platform_pct',  label:'Platform %', render: v => `${Math.round(v*100)}%` },
          { key:'fund_pct',      label:'Fund %',     render: v => `${Math.round(v*100)}%` },
          { key:'effective_from',label:'From',       render: v => v||'—' },
          { key:'actions',       label:'',           render:(v,r) => <Btn size="sm" onClick={()=>setEditRule(r)}>Edit</Btn> },
        ]}
        rows={rules}
        emptyMsg="No revenue rules"
      />

      {editRule && (
        <Modal title={editRule.id ? 'Edit rule' : 'New rule'} onClose={() => setEditRule(null)}>
          <RevenueRuleForm rule={editRule} onSave={saveRule} />
        </Modal>
      )}

      {editPrice && (
        <Modal title={`Edit price — ${editPrice.plan} (${editPrice.level === 'country' ? editPrice.key : networks.find(n => n.id === editPrice.key)?.label || editPrice.key})`} onClose={() => setEditPrice(null)}>
          <div>
            <FormField label="Price">
              <Input type="number" step="0.01" min="0"
                value={editPrice.price}
                onChange={e => setEditPrice(p => ({ ...p, price: parseFloat(e.target.value) || 0 }))}/>
            </FormField>
            <FormField label="Currency">
              <Select value={editPrice.currency}
                onChange={e => setEditPrice(p => ({ ...p, currency: e.target.value }))}
                options={[
                  {value:'CAD',label:'CAD — Canadian dollar'},
                  {value:'EUR',label:'EUR — Euro'},
                  {value:'USD',label:'USD — US dollar'},
                  {value:'CHF',label:'CHF — Swiss franc'},
                ]}/>
            </FormField>
            <div style={{ display:'flex', justifyContent:'flex-end', paddingTop:16, borderTop:`0.5px solid ${C.border}` }}>
              <Btn variant="primary" onClick={() => savePrice(editPrice)} disabled={saving}>Save</Btn>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

function RevenueRuleForm({ rule, onSave }) {
  const [form, setForm] = useState({ ...rule });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const total = Math.round(((form.cml_pct||0)+(form.platform_pct||0)+(form.fund_pct||0))*100);
  return (
    <div>
      <FormField label="Source">
        <Select value={form.source} onChange={e=>set('source',e.target.value)} options={[{value:'events',label:'Events'},{value:'sponsoring',label:'Sponsoring'},{value:'subscriptions',label:'Subscriptions'},{value:'alacarte',label:'À-la-carte'}]}/>
      </FormField>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10 }}>
        <FormField label="CML %"><Input type="number" value={Math.round((form.cml_pct||0)*100)} onChange={e=>set('cml_pct',parseFloat(e.target.value)/100||0)}/></FormField>
        <FormField label="Platform %"><Input type="number" value={Math.round((form.platform_pct||0)*100)} onChange={e=>set('platform_pct',parseFloat(e.target.value)/100||0)}/></FormField>
        <FormField label="Fund %"><Input type="number" value={Math.round((form.fund_pct||0)*100)} onChange={e=>set('fund_pct',parseFloat(e.target.value)/100||0)}/></FormField>
      </div>
      <p style={{ fontSize:12, color:total===100?C.green:C.red, marginBottom:12 }}>Total: {total}% {total===100?'✓':'— must equal 100%'}</p>
      <FormField label="Effective from"><Input type="date" value={form.effective_from||''} onChange={e=>set('effective_from',e.target.value)}/></FormField>
      <div style={{ display:'flex', justifyContent:'flex-end', paddingTop:16, borderTop:`0.5px solid ${C.border}` }}>
        <Btn variant="primary" onClick={() => onSave(form)} disabled={total!==100}>Save rule</Btn>
      </div>
    </div>
  );
}

// ── Features flags section ────────────────────────────────────────────────
// ── Toggle component ──────────────────────────────────────────────────────
function Toggle({ on, onChange, disabled = false, color = C.accent }) {
  return (
    <label style={{ position:'relative', display:'inline-block', width:44, height:24, cursor:disabled?'not-allowed':'pointer', flexShrink:0, opacity:disabled?0.5:1 }}>
      <input type="checkbox" checked={!!on} onChange={onChange} disabled={disabled}
        style={{ opacity:0, width:0, height:0, position:'absolute' }}/>
      <div style={{ position:'absolute', inset:0, borderRadius:99, background: on ? color : C.borderMd, transition:'background 0.2s', border:`0.5px solid ${C.borderMd}` }}>
        <div style={{ position:'absolute', top:3, left: on ? 22 : 3, width:18, height:18, borderRadius:'50%', background:'white', transition:'left 0.2s', boxShadow:'0 1px 3px rgba(0,0,0,0.2)' }}/>
      </div>
    </label>
  );
}

// ── Feature row ───────────────────────────────────────────────────────────
function FeatureRow({ feature, onToggle, onFoundersToggle, onDeploy, showFounders }) {
  const isFoundersLab = feature.category === 'founders_lab';
  const teal = '#1D9E75';

  return (
    <div style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 16px',
      borderBottom:`0.5px solid ${C.border}`,
      background: feature.founders_only && feature.enabled ? 'rgba(29,158,117,0.04)' : 'transparent',
    }}>
      {/* Icon + label */}
      <span style={{ fontSize:16, width:22, textAlign:'center', flexShrink:0 }}>{feature.icon || '·'}</span>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap' }}>
          <span style={{ fontSize:13, fontWeight:500, color:C.text }}>
            {feature.label_key ? tr(feature.label_key) : feature.label}
          </span>
          {feature.requires_plan && (
            <Badge color={feature.requires_plan === 'royal' ? 'amber' : 'gold'}>
              {feature.requires_plan}
            </Badge>
          )}
          {feature.founders_only && <Badge color="green">Founders only</Badge>}
          {feature.wave && <span style={{ fontSize:10, color:C.muted }}>Wave {feature.wave}</span>}
        </div>
        {feature.description && (
          <p style={{ fontSize:11, color:C.muted, margin:'2px 0 0', lineHeight:1.5 }}>
            {feature.description}
          </p>
        )}
      </div>

      {/* Controls */}
      <div style={{ display:'flex', alignItems:'center', gap:10, flexShrink:0 }}>
        {/* Auto-save state */}
        {feature._saving && (
          <span title="Saving…" style={{ width:10, height:10, borderRadius:'50%', background:C.accent, opacity:0.5, animation:'pulse 1s ease-in-out infinite' }}/>
        )}
        {feature._err && (
          <span title={feature._err} style={{ fontSize:13, color:C.red, cursor:'help' }}>⚠</span>
        )}

        {/* Founders-only toggle (only shown in founders lab or when showFounders) */}
        {(isFoundersLab || showFounders) && !isFoundersLab && (
          <div style={{ display:'flex', alignItems:'center', gap:5 }}>
            <span style={{ fontSize:10, color:teal }}>Founders</span>
            <Toggle on={feature.founders_only} onChange={() => onFoundersToggle(feature.key)} color={teal}/>
          </div>
        )}

        {/* Deploy button for founders lab */}
        {isFoundersLab && feature.founders_only && feature.enabled && onDeploy && (
          <button onClick={() => onDeploy(feature.key)} style={{ padding:'3px 10px', borderRadius:C.radius, border:`0.5px solid ${C.borderMd}`, background:C.surface, color:C.sub, fontSize:11, cursor:'pointer' }}>
            Deploy to all →
          </button>
        )}

        {/* Main toggle */}
        <Toggle on={feature.enabled} onChange={() => onToggle(feature.key)}
          color={isFoundersLab ? teal : C.accent}/>
      </div>
    </div>
  );
}

// ── Section group ─────────────────────────────────────────────────────────
function FeatureGroup({ title, features, onToggle, onFoundersToggle, onDeploy, showFounders, color }) {
  const [open, setOpen] = useState(true);
  // Hide groups that have no rows in the DB — keeps the Control Center clean
  // when a migration hasn't been applied yet, or when the seed is partial.
  if (!features || features.length === 0) return null;
  const activeCount = features.filter(f => f.enabled).length;

  return (
    <div style={{ background:C.surface, border:`0.5px solid ${C.border}`, borderRadius:C.radiusLg, overflow:'hidden', marginBottom:12 }}>
      <button onClick={() => setOpen(o => !o)} style={{ width:'100%', display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 16px', background:'transparent', border:'none', cursor:'pointer', borderBottom: open ? `0.5px solid ${C.border}` : 'none' }}>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <span style={{ fontSize:13, fontWeight:500, color: color || C.text }}>{title}</span>
          <span style={{ fontSize:11, color:C.muted }}>{activeCount}/{features.length} on</span>
        </div>
        <span style={{ fontSize:12, color:C.muted }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && features.sort((a,b) => (a.sort_order||50)-(b.sort_order||50)).map(f => (
        <FeatureRow key={f.key} feature={f} onToggle={onToggle}
          onFoundersToggle={onFoundersToggle} onDeploy={onDeploy}
          showFounders={showFounders}/>
      ))}
    </div>
  );
}

// ── Default feature rows — mirrors 003_rls_seed.sql ──────────────────────
const DEFAULT_FEATURES = [
  { key:'invite_only',             category:'system',      sub_section:null,         label:'Invite-only mode',            description:'Require invite code to register',              enabled:true,  founders_only:false, sort_order:20 },
  { key:'subscriptions_enabled',   category:'system',      sub_section:'monetization',label:'Subscriptions active',       description:'When OFF: all users get Honey free',           enabled:false, founders_only:false, sort_order:10 },
  { key:'gamification_streaks',    category:'system',      sub_section:'gamification',label:'Streak counter',             description:'Daily streak rewards',                         enabled:false, founders_only:false, sort_order:10 },
  { key:'gamification_milestones', category:'system',      sub_section:'gamification',label:'Match milestones',           description:'Celebrate first match, message, etc.',         enabled:false, founders_only:false, sort_order:20 },
  { key:'gamification_journey',    category:'system',      sub_section:'gamification',label:'Bee Journey progress',       description:'Chrysalis → Queen Bee progression in Profile', enabled:false, founders_only:false, sort_order:30 },
  { key:'gamification_hive_score', category:'system',      sub_section:'gamification',label:'Hive Score',                description:'Community contribution score in BeeHive',      enabled:false, founders_only:false, sort_order:40 },
  { key:'discover',                category:'section',     sub_section:null,          label:'Discover',                  description:'Romantic discovery feed',                      enabled:true,  founders_only:false, sort_order:10 },
  { key:'matches',                 category:'section',     sub_section:null,          label:'Matches',                   description:'Mutual likes and matches',                     enabled:true,  founders_only:false, sort_order:20 },
  { key:'messages',                category:'section',     sub_section:null,          label:'Messages',                  description:'In-app messaging',                             enabled:true,  founders_only:false, sort_order:30 },
  { key:'profile',                 category:'section',     sub_section:null,          label:'Profile',                   description:'User profile',                                 enabled:true,  founders_only:false, sort_order:40 },
  { key:'notifications',           category:'section',     sub_section:null,          label:'Notifications',             description:'In-app notifications',                         enabled:true,  founders_only:false, sort_order:50 },
  { key:'beehive',                 category:'section',     sub_section:null,          label:'BeeHive',                   description:'Community hub + journey',                      enabled:false, founders_only:false, sort_order:60 },
  { key:'events',                  category:'section',     sub_section:null,          label:'Events',                    description:'In-person BeeLove events',                     enabled:false, founders_only:false, sort_order:70 },
  { key:'spotify',                 category:'section',     sub_section:null,          label:'Spotify Connect',           description:'Music taste matching',                         enabled:false, founders_only:false, sort_order:80 },
  { key:'beeconnect',              category:'section',     sub_section:null,          label:'BeeConnect',                description:'Friendly connections mode',                    enabled:false, founders_only:false, sort_order:90 },
  { key:'circles',                 category:'section',     sub_section:null,          label:'Scout',                     description:'Private & group matchmaking events',           enabled:false, founders_only:false, sort_order:100 },
  { key:'friend_rating',           category:'section',     sub_section:null,          label:'Friend Rating',             description:'Be vouched for by friends',                    enabled:false, founders_only:false, sort_order:110 },
  { key:'referral',                category:'section',     sub_section:null,          label:'Referral program',          description:'Invite & earn rewards',                        enabled:false, founders_only:false, sort_order:120 },
  { key:'discover_daily_picks',    category:'discover',    sub_section:null,          label:'Daily Picks mode',          description:'3–5 curated profiles/day',                     enabled:false, founders_only:false, sort_order:10 },
  { key:'discover_explicit_btns',  category:'discover',    sub_section:null,          label:'Explicit Pass/Like labels', description:'Text labels on action buttons',                enabled:true,  founders_only:false, sort_order:20 },
  { key:'discover_quota_visible',  category:'discover',    sub_section:null,          label:'Quota counter visible',     description:'Show "X likes left today"',                    enabled:false, founders_only:false, sort_order:30 },
  { key:'discover_rewind',         category:'discover',    sub_section:null,          label:'Rewind button',             description:'Undo last pass',                               enabled:false, founders_only:false, sort_order:40 },
  { key:'perk_see_liked',          category:'plan_perk',   sub_section:null,          label:'See Who Liked You',         description:'Show likes received list',                     enabled:false, founders_only:false, sort_order:10 },
  { key:'perk_super_likes',        category:'plan_perk',   sub_section:null,          label:'Super Likes',               description:'Show super likes feature',                     enabled:false, founders_only:false, sort_order:20 },
  { key:'perk_boosts',             category:'plan_perk',   sub_section:null,          label:'Profile Boosts',            description:'Show boost feature',                           enabled:false, founders_only:false, sort_order:30 },
  { key:'perk_analytics',          category:'plan_perk',   sub_section:null,          label:'Profile Analytics',         description:'Show profile view stats (Royal)',               enabled:false, founders_only:false, sort_order:40 },
  { key:'perk_incognito',          category:'plan_perk',   sub_section:null,          label:'Incognito mode',            description:'Show incognito toggle',                        enabled:false, founders_only:false, sort_order:50 },
  { key:'perk_read_receipts',      category:'plan_perk',   sub_section:null,          label:'Read Receipts',             description:'Show read receipts in messages',               enabled:false, founders_only:false, sort_order:60 },
  { key:'perk_daily_picks',        category:'plan_perk',   sub_section:null,          label:'Daily Picks',               description:'Curated daily profile picks',                  enabled:false, founders_only:false, sort_order:70 },
  { key:'perk_priority',           category:'plan_perk',   sub_section:null,          label:'Priority Matching',         description:'Royal users appear first',                     enabled:false, founders_only:false, sort_order:80 },
  { key:'landing_stats',           category:'sub_section', sub_section:'landing',     label:'Landing — live stats',      description:'Show real stats on landing',                   enabled:true,  founders_only:false, sort_order:10 },
  { key:'landing_waitlist',        category:'sub_section', sub_section:'landing',     label:'Landing — waitlist',        description:'Show waitlist CTA',                            enabled:true,  founders_only:false, sort_order:20 },
  { key:'landing_photos',          category:'sub_section', sub_section:'landing',     label:'Landing — profile cards',   description:'Animated cards in hero',                       enabled:true,  founders_only:false, sort_order:30 },
  { key:'landing_pricing',         category:'sub_section', sub_section:'landing',     label:'Landing — pricing',         description:'Show pricing section',                         enabled:false, founders_only:false, sort_order:40 },
  { key:'landing_faq',             category:'sub_section', sub_section:'landing',     label:'Landing — FAQ',             description:'Show FAQ accordion',                           enabled:true,  founders_only:false, sort_order:50 },
  { key:'discover_icebreakers',    category:'sub_section', sub_section:'discover',    label:'Discover — icebreakers',    description:'AI-generated icebreakers',                     enabled:true,  founders_only:false, sort_order:10 },
  { key:'discover_journey',        category:'sub_section', sub_section:'discover',    label:'Discover — journey tip',    description:'Journey tip banner',                           enabled:false, founders_only:false, sort_order:20 },
  { key:'beta_daily_picks',        category:'founders_lab',sub_section:null,          label:'Daily Picks mode',          description:'Curated 5 profiles/day vs infinite swipe',     enabled:false, founders_only:true,  sort_order:10 },
  { key:'beta_spotify_match',      category:'founders_lab',sub_section:null,          label:'Spotify compatibility',     description:'Factor music taste into compat score',         enabled:false, founders_only:true,  sort_order:20 },
  { key:'beta_hive_score',         category:'founders_lab',sub_section:null,          label:'Hive Score visible',        description:'Show Hive Score on profiles in BeeHive',       enabled:false, founders_only:true,  sort_order:30 },
  { key:'beta_compat_detail',      category:'founders_lab',sub_section:null,          label:'Detailed compat',           description:'Axis-by-axis compatibility breakdown',         enabled:false, founders_only:true,  sort_order:40 },
];

// ── Main ControlCenterTab ─────────────────────────────────────────────────
// Auto-save on every toggle: each change is upserted immediately. No save button.
// Tabs are organised by user intent, not by raw DB category.

// Real navigation pages (those with a button in the bottom nav).
// Other rows in DB.category='section' are mechanics (friend_rating, referral, spotify).
const NAV_PAGES = new Set([
  'discover', 'matches', 'messages', 'beehive', 'profile',
  'notifications', 'events', 'agenda', 'scout', 'beeconnect',
]);

// Core features that are NOT optional — they cannot be disabled from the UI.
// They stay in the DB but the Control Center never displays a toggle for them.
const CORE_FLAGS = new Set([
  'friend_rating',  // central BeeLove mechanic — friends describe who you are
]);

function ControlCenterTab() {
  const tr = useTr();
  const { lang, setAppConfig } = useApp();
  const [features,   setFeatures]   = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [activeTab,  setActiveTab]  = useState('navigation');
  const [trialDays,  setTrialDays]  = useState(30);
  const [trialSaved, setTrialSaved] = useState(false);
  const [errMsg,     setErrMsg]     = useState(null);

  const loadFeatures = () => {
    supabase?.from('app_features').select('*').order('sort_order').order('key')
      .then(({ data }) => {
        if (data) {
          // Strip core (non-optional) flags from the displayable list.
          // They remain in the DB; the rest of the app reads them as always-on.
          setFeatures(data.filter(f => !CORE_FLAGS.has(f.key)));
        }
        setLoading(false);
        // Make sure the DB row for every core flag is enabled — guard against
        // someone toggling it OFF historically before we hid the UI.
        if (data?.length) {
          const offCore = data.filter(f => CORE_FLAGS.has(f.key) && f.enabled === false);
          for (const f of offCore) {
            supabase?.from('app_features').update({ enabled: true }).eq('key', f.key);
          }
        }
      });
  };

  const saveTrialDays = async (val) => {
    const raw = val !== undefined ? val : trialDays;
    const days = Math.max(1, Math.min(730, parseInt(raw, 10) || 30));
    setTrialDays(days);
    const { error } = await supabase?.from('app_config')
      .upsert({ key: 'invite_trial_days', value: days }, { onConflict: 'key' }) ?? {};
    if (error) {
      setErrMsg('Trial duration save failed: ' + error.message);
      setTimeout(() => setErrMsg(null), 4000);
      return;
    }
    setAppConfig(prev => ({ ...prev, invite_trial_days: days }));
    setTrialSaved(true);
    setTimeout(() => setTrialSaved(false), 2500);
  };

  useEffect(() => {
    supabase?.from('app_config').select('value').eq('key', 'invite_trial_days').maybeSingle()
      .then(({ data }) => { if (data?.value != null) setTrialDays(Number(data.value)); });
  }, []);

  useEffect(() => { loadFeatures(); }, []);

  // Auto-save: optimistic update + immediate upsert + revert on error.
  // Use UPDATE (not upsert) since the row already exists in app_features —
  // upserting partial fields trips NOT-NULL columns like label/category.
  const persistRow = async (key, patch) => {
    const current = features.find(f => f.key === key);
    if (!current) return;
    setFeatures(prev => prev.map(f => f.key === key ? { ...f, ...patch, _saving: true, _err: null } : f));
    const fields = {};
    if ('enabled'       in patch) fields.enabled       = patch.enabled;
    if ('founders_only' in patch) fields.founders_only = patch.founders_only;
    if ('launch_date'   in patch) fields.launch_date   = patch.launch_date;
    const { error } = await supabase?.from('app_features')
      .update(fields).eq('key', key) ?? {};
    if (error) {
      setErrMsg(error.message);
      setTimeout(() => setErrMsg(null), 4000);
      // Revert to previous state
      setFeatures(prev => prev.map(f => f.key === key ? { ...current, _saving: false, _err: error.message } : f));
    } else {
      setFeatures(prev => prev.map(f => f.key === key ? { ...f, _saving: false } : f));
    }
  };

  const toggle         = (key) => {
    const cur = features.find(f => f.key === key);
    persistRow(key, { enabled: !cur?.enabled });
  };
  const toggleFounders = (key) => {
    const cur = features.find(f => f.key === key);
    persistRow(key, { founders_only: !cur?.founders_only });
  };

  const deployToAll = async (key) => {
    if (!window.confirm(`Deploy "${key}" to all users? This removes founders-only restriction.`)) return;
    await supabase?.rpc('deploy_founders_feature', { p_key: key });
    setFeatures(prev => prev.map(f => f.key === key ? { ...f, founders_only: false, enabled: true } : f));
  };

  if (loading) return <p style={{ color:C.muted, padding:24 }}>Loading…</p>;


  // Group by category
  const byCategory = (cat) => features.filter(f => f.category === cat);
  const bySub      = (cat, sub) => features.filter(f => f.category === cat && f.sub_section === sub);
  const teal       = '#1D9E75';

  const subsEnabled = features.find(f => f.key === 'subscriptions_enabled')?.enabled;

  // ── Tab definitions — organised by user intent, not raw DB category ──
  const TABS = [
    { id:'navigation', label:'Navigation' },
    { id:'mechanics',  label:'Mechanics' },
    { id:'engagement', label:'Engagement & Safety' },
    { id:'content',    label:'UI sections' },
    { id:'plans',      label:'Plans & money' },
    { id:'cities',     label:'Cities' },
    { id:'matrix',     label:'Plan × feature' },
    { id:'founders',   label:'🧪 Founders Lab' },
  ];

  // ── Selectors used by the tabs ──
  const navPages       = features.filter(f => f.category === 'section' && NAV_PAGES.has(f.key));
  const sectionMechanics = features.filter(f => f.category === 'section' && !NAV_PAGES.has(f.key));
  const systemRows     = byCategory('system');
  const accessRows     = systemRows.filter(f => f.key === 'invite_only');
  const subsRow        = systemRows.find(f => f.key === 'subscriptions_enabled');
  const gamificationRows = systemRows.filter(f => (f.sub_section === 'gamification') || f.key.startsWith('gamification_'));

  return (
    <>
      <PageHeader
        title="Control center"
        sub={`${features.filter(f=>f.enabled).length}/${features.length} features active · changes apply immediately`}
      />

      {errMsg && (
        <div style={{ background:'rgba(192,69,58,0.12)', border:`0.5px solid ${C.red}`, borderRadius:C.radius, padding:'8px 12px', marginBottom:12, fontSize:12, color:C.red }}>
          ⚠ {errMsg}
        </div>
      )}

      {/* Tab bar */}
      <div style={{ display:'flex', gap:4, marginBottom:20, borderBottom:`0.5px solid ${C.border}`, paddingBottom:0 }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)} style={{
            padding:'8px 14px', border:'none', borderBottom:`2px solid ${activeTab===t.id?C.accent:'transparent'}`,
            background:'transparent', color:activeTab===t.id?C.accent:C.sub,
            fontSize:13, fontWeight:activeTab===t.id?500:400, cursor:'pointer',
            marginBottom:-1,
          }}>{t.label}</button>
        ))}
      </div>

      {/* ── Navigation — only the actual nav-bar pages ── */}
      {activeTab === 'navigation' && (
        <div>
          <p style={{ fontSize:12, color:C.muted, margin:'0 0 14px' }}>
            Pages with a button in the bottom navigation. Toggling OFF removes the page from the nav and the router.
          </p>
          <FeatureGroup title="Nav-bar pages" color={C.accent}
            features={navPages}
            onToggle={toggle} onFoundersToggle={toggleFounders}/>
        </div>
      )}

      {/* ── Mechanics — non-page features that the user experiences ── */}
      {activeTab === 'mechanics' && (
        <div>
          <p style={{ fontSize:12, color:C.muted, margin:'0 0 14px' }}>
            Cross-cutting mechanics (no nav button): public flows, integrations, programs.
          </p>
          <FeatureGroup title="Programs & flows" color={C.accent}
            features={sectionMechanics}
            onToggle={toggle} onFoundersToggle={toggleFounders}/>
          <FeatureGroup title="Access" color={teal}
            features={accessRows}
            onToggle={toggle} onFoundersToggle={toggleFounders}/>
        </div>
      )}

      {/* ── Engagement & Safety — Feature Pack A flags ── */}
      {activeTab === 'engagement' && (() => {
        const packA = features.filter(f =>
          ['engagement', 'safety', 'messages', 'beehive', 'scout', 'cml'].includes(f.category)
        );
        return (
          <div>
            <div style={{ background:'rgba(201,168,76,0.05)', border:`0.5px solid ${C.border}`, borderRadius:C.radiusLg, padding:'12px 16px', marginBottom:16 }}>
              <p style={{ fontSize:13, fontWeight:500, color:C.text, margin:'0 0 4px' }}>Feature Pack A</p>
              <p style={{ fontSize:12, color:C.muted, margin:0, lineHeight:1.6 }}>
                Opt-in features that boost virality (K-factor) and trust.
                Each flag also gates the matching UI text — disabled features become invisible to users.
              </p>
            </div>

            {packA.length === 0 ? (
              <div style={{ background:C.surface, border:`0.5px solid ${C.border}`, borderRadius:C.radiusLg, padding:'18px 20px' }}>
                <p style={{ fontSize:13, fontWeight:500, color:C.text, margin:'0 0 6px' }}>No Pack A flags found in the DB</p>
                <p style={{ fontSize:12, color:C.muted, margin:'0 0 8px', lineHeight:1.6 }}>
                  Apply migration <code style={{ background:C.bg, padding:'1px 5px', borderRadius:3 }}>037_feature_pack_a.sql</code> on your Supabase project, then refresh this page.
                </p>
                <code style={{ display:'block', fontSize:11, color:C.sub, background:C.bg, padding:'8px 10px', borderRadius:C.radius, border:`0.5px solid ${C.border}`, fontFamily:'monospace' }}>
                  supabase db push
                </code>
              </div>
            ) : (
              <>
                <FeatureGroup title="Viral loop" color={C.accent}
                  features={features.filter(f => f.category === 'engagement' && f.sub_section === 'viral')}
                  onToggle={toggle} onFoundersToggle={toggleFounders}/>
                <FeatureGroup title="Reputation & gamification" color={C.accent}
                  features={features.filter(f => f.category === 'engagement' && f.sub_section === 'gamif')}
                  onToggle={toggle} onFoundersToggle={toggleFounders}/>
                <FeatureGroup title="Retention" color={C.accent}
                  features={features.filter(f => f.category === 'engagement' && f.sub_section === 'retain')}
                  onToggle={toggle} onFoundersToggle={toggleFounders}/>
                <FeatureGroup title="Social proof" color={C.accent}
                  features={features.filter(f => f.category === 'engagement' && f.sub_section === 'social')}
                  onToggle={toggle} onFoundersToggle={toggleFounders}/>
                <FeatureGroup title="Safety" color={teal}
                  features={byCategory('safety')}
                  onToggle={toggle} onFoundersToggle={toggleFounders}/>
                <FeatureGroup title="Messages" color={C.blue}
                  features={byCategory('messages')}
                  onToggle={toggle} onFoundersToggle={toggleFounders}/>
                <FeatureGroup title="BeeHive add-ons" color={C.accent}
                  features={byCategory('beehive')}
                  onToggle={toggle} onFoundersToggle={toggleFounders}/>
                <FeatureGroup title="Scout / Circles add-ons" color={C.accent}
                  features={byCategory('scout')}
                  onToggle={toggle} onFoundersToggle={toggleFounders}/>
                <FeatureGroup title="CML community" color={C.accent}
                  features={byCategory('cml')}
                  onToggle={toggle} onFoundersToggle={toggleFounders}/>
              </>
            )}
          </div>
        );
      })()}

      {/* ── UI sections — granular layout / variant flags inside pages ── */}
      {activeTab === 'content' && (
        <div>
          <p style={{ fontSize:12, color:C.muted, margin:'0 0 14px' }}>
            Sub-sections and UI variants within already-enabled pages.
          </p>
          <FeatureGroup title="Landing page sections" color={C.blue}
            features={byCategory('sub_section').filter(f => f.sub_section === 'landing')}
            onToggle={toggle} onFoundersToggle={toggleFounders}/>
          {/* Profile tabs — static info (tabs are core, not toggleable) */}
          <div style={{ marginBottom:16 }}>
            <p style={{ fontSize:11, fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', color:C.blue, margin:'0 0 10px' }}>
              Profile tabs
            </p>
            <div style={{ background:C.card, border:`0.5px solid ${C.border}`, borderRadius:C.radius, overflow:'hidden' }}>
              {[
                { label:'Profile',    desc:'Core — always visible',                      always:true },
                { label:'Hive',       desc:'Core — always visible',                      always:true },
                { label:'Entourage',  desc:'Visible when Friend Rating is enabled (see Mechanics tab)', always:false },
                { label:'Settings',   desc:'Core — always visible',                      always:true },
              ].map((row, i, arr) => (
                <div key={row.label} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'9px 14px', borderBottom: i < arr.length-1 ? `0.5px solid ${C.border}` : 'none' }}>
                  <div>
                    <span style={{ fontSize:13, color:C.text, fontWeight:500 }}>{row.label}</span>
                    <p style={{ fontSize:11, color:C.muted, margin:'2px 0 0' }}>{row.desc}</p>
                  </div>
                  <span style={{ fontSize:10, padding:'2px 8px', borderRadius:99, background: row.always ? 'rgba(78,201,148,0.12)' : 'rgba(201,168,76,0.12)', border:`0.5px solid ${row.always ? 'rgba(78,201,148,0.35)' : 'rgba(201,168,76,0.35)'}`, color: row.always ? '#4EC994' : C.accent, fontWeight:600, letterSpacing:'0.06em', whiteSpace:'nowrap' }}>
                    {row.always ? 'TOUJOURS ACTIF' : 'CONDITIONNEL'}
                  </span>
                </div>
              ))}
            </div>
          </div>
          <FeatureGroup title="Discover UX variants" color={C.blue}
            features={byCategory('discover')}
            onToggle={toggle} onFoundersToggle={toggleFounders}/>
          <FeatureGroup title="Other page sections" color={C.blue}
            features={byCategory('sub_section').filter(f => !['landing','profile','monetization','access','gamification'].includes(f.sub_section))}
            onToggle={toggle} onFoundersToggle={toggleFounders}/>
        </div>
      )}

      {/* ── Plans & money — subscriptions, perks, gamification, trial ── */}
      {activeTab === 'plans' && (
        <div>
          {/* Subscriptions banner */}
          <div style={{ background: subsEnabled ? 'rgba(201,168,76,0.08)' : 'rgba(29,158,117,0.06)', border:`0.5px solid ${subsEnabled?C.accent:teal}`, borderRadius:C.radiusLg, padding:'14px 18px', marginBottom:16, display:'flex', alignItems:'center', justifyContent:'space-between', gap:12 }}>
            <div>
              <p style={{ fontSize:14, fontWeight:500, color:subsEnabled?C.accent:teal, margin:'0 0 3px' }}>
                {subsEnabled ? '💳 Subscriptions active' : '🎁 All features free — subscriptions OFF'}
              </p>
              <p style={{ fontSize:12, color:C.muted, margin:0 }}>
                {subsEnabled
                  ? 'Users see pricing, upgrade UI and plan limits.'
                  : 'All users have Honey-level access for free. Turn ON when ready to monetize.'}
              </p>
            </div>
            <Toggle on={subsEnabled} color={C.accent}
              onChange={() => {
                if (!subsEnabled && !window.confirm('Activating subscriptions grants all current users 30 days Honey free, then downgrade. Run SELECT activate_subscriptions() in Supabase SQL editor to finalize. Continue?')) return;
                toggle('subscriptions_enabled');
              }}/>
          </div>

          {/* Trial duration */}
          <div style={{ background:C.surface, border:`0.5px solid ${C.border}`, borderRadius:C.radiusLg, padding:'16px 18px', marginBottom:12, opacity: subsEnabled ? 1 : 0.85 }}>
            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:3, flexWrap:'wrap' }}>
              <p style={{ fontSize:13, fontWeight:500, color:C.text, margin:0 }}>🎁 Trial duration — invite link</p>
              {!subsEnabled && (
                <span style={{ fontSize:9, fontWeight:600, letterSpacing:'0.08em', textTransform:'uppercase', padding:'2px 7px', borderRadius:99, background:'rgba(140,134,128,0.12)', color:C.muted, border:`1px solid ${C.border}` }}>
                  Parked
                </span>
              )}
            </div>
            <p style={{ fontSize:12, color:C.muted, margin:'0 0 12px', lineHeight:1.5 }}>
              {subsEnabled
                ? 'Number of free days shown on the friend-invite signup CTA (FriendRating + invite emails).'
                : 'Currently has no user-facing effect. While Subscriptions are OFF, all members are Founding Members with permanent free access — no trial copy is shown anywhere. This setting will be reactivated when paid plans launch.'}
            </p>
            <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
              <input
                type="number" min={1} max={730}
                value={trialDays}
                onChange={e => setTrialDays(e.target.value)}
                onBlur={e => { const v = Math.max(1, Math.min(730, parseInt(e.target.value, 10) || 30)); saveTrialDays(v); }}
                style={{ width:72, padding:'6px 10px', borderRadius:C.radius, border:`0.5px solid ${C.borderMd}`, background:C.bg, color:C.text, fontSize:14, textAlign:'center' }}
              />
              <span style={{ fontSize:12, color:C.muted }}>days {trialSaved && <span style={{ color:C.green }}>✓</span>}</span>
              <span style={{ fontSize:11, color:C.muted }}>
                Preview: <em style={{ color:C.sub }}>
                  {trialPhrase(parseInt(trialDays, 10) || 30, lang)}
                </em>
              </span>
            </div>
          </div>

          <FeatureGroup title="Plan perks visible" color={C.accent}
            features={byCategory('plan_perk')}
            onToggle={toggle} onFoundersToggle={toggleFounders}/>

          <FeatureGroup title="Gamification" color={teal}
            features={gamificationRows}
            onToggle={toggle} onFoundersToggle={toggleFounders}/>
        </div>
      )}

      {/* ── Cities — per-network feature flags ── */}
      {activeTab === 'cities' && <CityFeaturesTab/>}

      {/* ── Plan × feature matrix ── */}
      {activeTab === 'matrix' && <PlanFeaturesTab/>}

      {/* ── Founders Lab ── */}
      {activeTab === 'founders' && (
        <div>
          <div style={{ background:`${teal}08`, border:`0.5px solid ${teal}40`, borderRadius:C.radiusLg, padding:'12px 16px', marginBottom:16 }}>
            <p style={{ fontSize:13, fontWeight:500, color:teal, margin:'0 0 4px' }}>🧪 Founders Lab</p>
            <p style={{ fontSize:12, color:C.muted, margin:0, lineHeight:1.6 }}>
              Features visible only to Founding Members (<code>is_founding_member = true</code>). They see a small 🧪 badge.
              When ready to deploy, click "Deploy to all" — removes the founders restriction and enables for everyone.
            </p>
          </div>

          <FounderStatsBar/>

          <FeatureGroup title="Beta features" color={teal}
            features={byCategory('founders_lab')}
            onToggle={toggle} onFoundersToggle={toggleFounders}
            onDeploy={deployToAll}/>

          <FeatureGroup title="Nav pages & sections (founders gate)" color={C.muted}
            features={features.filter(f => ['section','discover','sub_section'].includes(f.category))}
            onToggle={toggle} onFoundersToggle={toggleFounders} showFounders/>

          <FeatureGroup title="Plan perks (founders gate)" color={C.muted}
            features={byCategory('plan_perk')}
            onToggle={toggle} onFoundersToggle={toggleFounders} showFounders/>
        </div>
      )}
    </>
  );
}

// ── City feature flags ────────────────────────────────────────────────────
const CITY_FEATURES = [
  { key: 'events',     label: 'Events'     },
  { key: 'circles',    label: 'Scout'      },
  { key: 'beeconnect', label: 'BeeConnect' },
  { key: 'beehive',    label: 'BeeHive'    },
];

const COUNTRY_FLAG = { CA:'🇨🇦', FR:'🇫🇷', GB:'🇬🇧', BE:'🇧🇪', US:'🇺🇸', CH:'🇨🇭', DE:'🇩🇪' };

function CityFeaturesTab() {
  const [networks, setNetworks] = useState([]);
  const [flags,    setFlags]    = useState({}); // { networkId: { feature: bool } }
  const [errMsg,   setErrMsg]   = useState(null);

  useEffect(() => {
    supabase?.from('networks').select('id, label, country, active').order('active', { ascending: false }).order('label').then(({ data }) => {
      if (data) setNetworks(data);
    });
    supabase?.from('network_features').select('network_id, feature, enabled').then(({ data }) => {
      if (!data) return;
      const map = {};
      data.forEach(({ network_id, feature, enabled }) => {
        if (!map[network_id]) map[network_id] = {};
        map[network_id][feature] = enabled;
      });
      setFlags(map);
    });
  }, []);

  // Auto-save on every toggle.
  const toggle = async (networkId, feature) => {
    const prevVal = flags[networkId]?.[feature] ?? false;
    const nextVal = !prevVal;
    setFlags(prev => ({ ...prev, [networkId]: { ...prev[networkId], [feature]: nextVal } }));
    const { error } = await supabase?.from('network_features').upsert({
      network_id: networkId,
      feature,
      label_key:  `feature.${feature}`,
      desc_key:   `feature.${feature}_desc`,
      enabled:    nextVal,
    }, { onConflict: 'network_id,feature' }) ?? {};
    if (error) {
      // Revert
      setFlags(prev => ({ ...prev, [networkId]: { ...prev[networkId], [feature]: prevVal } }));
      setErrMsg(`${networkId}.${feature}: ${error.message}`);
      setTimeout(() => setErrMsg(null), 4000);
    }
  };

  return (
    <div>
      <p style={{ fontSize:13, color:C.muted, margin:'0 0 16px' }}>
        Features enabled per city. Each toggle is saved immediately.
      </p>

      {errMsg && (
        <div style={{ background:'rgba(192,69,58,0.12)', border:`0.5px solid ${C.red}`, borderRadius:C.radius, padding:'8px 12px', marginBottom:12, fontSize:12, color:C.red }}>
          ⚠ {errMsg}
        </div>
      )}

      <div style={{ background:C.surface, border:`0.5px solid ${C.border}`, borderRadius:C.radiusLg, overflow:'hidden' }}>
        {/* Header row */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr repeat(4, 100px)', borderBottom:`0.5px solid ${C.border}`, padding:'8px 16px', background:C.card }}>
          <span style={{ fontSize:11, color:C.muted, fontWeight:500, textTransform:'uppercase', letterSpacing:'0.06em' }}>Network</span>
          {CITY_FEATURES.map(f => (
            <span key={f.key} style={{ fontSize:11, color:C.muted, fontWeight:500, textTransform:'uppercase', letterSpacing:'0.06em', textAlign:'center' }}>{f.label}</span>
          ))}
        </div>

        {/* Network rows */}
        {networks.map((net, i) => (
          <div key={net.id} style={{ display:'grid', gridTemplateColumns:'1fr repeat(4, 100px)', padding:'12px 16px', borderBottom: i < networks.length-1 ? `0.5px solid ${C.border}` : 'none', alignItems:'center' }}>
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <span style={{ fontSize:18 }}>{COUNTRY_FLAG[net.country] || '🌐'}</span>
              <div>
                <div style={{ fontSize:13, fontWeight:500, color:C.text }}>{net.label}</div>
                <div style={{ fontSize:11, color:C.muted }}>{net.id}</div>
              </div>
            </div>
            {CITY_FEATURES.map(f => {
              const on = flags[net.id]?.[f.key] ?? false;
              return (
                <div key={f.key} style={{ textAlign:'center' }}>
                  <Toggle on={on} onChange={() => toggle(net.id, f.key)} color={C.accent}/>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Founders stats bar ─────────────────────────────────────────────────────
function FounderStatsBar() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    supabase?.from('user_core').select('id', { count:'exact', head:true })
      .eq('is_founding_member', true)
      .then(({ count }) => setStats({ founders: count || 0 }));
  }, []);
  if (!stats) return null;
  return (
    <div style={{ display:'flex', gap:10, marginBottom:14 }}>
      <div style={{ background:C.surface, border:`0.5px solid ${C.border}`, borderRadius:C.radius, padding:'8px 14px' }}>
        <p style={{ fontSize:18, fontWeight:500, color:'#1D9E75', margin:0 }}>{stats.founders}</p>
        <p style={{ fontSize:11, color:C.muted, margin:0 }}>Founding members</p>
      </div>
    </div>
  );
}


function AppConfigTab() {
  const { setDiscoveryPrefs, discoveryPrefs } = useApp();
  const [form, setForm] = useState({ maxRadius: 100, maxAge: 80, minAge: 18, defaultNetwork: 'ca_montreal', appName: 'BeeLove', supportEmail: 'hello@beelove.me' });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  return (
    <>
      <PageHeader title="App config" sub="Platform-wide settings" />
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:24 }}>
        <div style={{ background:C.surface, border:`0.5px solid ${C.border}`, borderRadius:C.radiusLg, padding:20 }}>
          <p style={{ fontSize:14, fontWeight:500, color:C.text, margin:'0 0 16px' }}>General</p>
          <FormField label="App name"><Input value={form.appName} onChange={e=>set('appName',e.target.value)}/></FormField>
          <FormField label="Support email"><Input type="email" value={form.supportEmail} onChange={e=>set('supportEmail',e.target.value)}/></FormField>
          <FormField label="Default network">
            <Select value={form.defaultNetwork} onChange={e=>set('defaultNetwork',e.target.value)} options={[{value:'ca_montreal',label:'Montréal'},{value:'fr_paris',label:'Paris'},{value:'fr_lorient',label:'Lorient'}]}/>
          </FormField>
          <Btn variant="primary" onClick={() => {}}>Save config</Btn>
        </div>
        <div style={{ background:C.surface, border:`0.5px solid ${C.border}`, borderRadius:C.radiusLg, padding:20 }}>
          <p style={{ fontSize:14, fontWeight:500, color:C.text, margin:'0 0 16px' }}>Discovery defaults</p>
          <FormField label={`Max radius: ${discoveryPrefs?.radiusKm||25} km`}>
            <input type="range" min={5} max={200} step={5} value={discoveryPrefs?.radiusKm||25}
              onChange={e => setDiscoveryPrefs(p => ({ ...p, radiusKm: parseInt(e.target.value) }))}
              style={{ width:'100%' }}/>
          </FormField>
          <FormField label={`Age range: ${discoveryPrefs?.minAge||18}–${discoveryPrefs?.maxAge||55}`}>
            <div style={{ display:'flex', gap:10 }}>
              <input type="number" min={18} max={100} value={discoveryPrefs?.minAge||18} onChange={e=>setDiscoveryPrefs(p=>({...p,minAge:parseInt(e.target.value)||18}))} style={{ width:'100%', padding:'8px', border:`0.5px solid ${C.border}`, borderRadius:C.radius, fontSize:13 }}/>
              <input type="number" min={18} max={100} value={discoveryPrefs?.maxAge||55} onChange={e=>setDiscoveryPrefs(p=>({...p,maxAge:parseInt(e.target.value)||55}))} style={{ width:'100%', padding:'8px', border:`0.5px solid ${C.border}`, borderRadius:C.radius, fontSize:13 }}/>
            </div>
          </FormField>
        </div>
      </div>
    </>
  );
}

// ── Compatibility Axes section ────────────────────────────────────────────
// Pole labels mirror FriendRating.js AXIS_POLES / AXIS_POLES_FIT exactly.
// INVERTED_AXES mirrors FriendRating.js — display poles swapped to avoid left=bad bias.
const COMPAT_INVERTED = new Set(['accountability', 'generosity', 'narrative', 'stability']);
const COMPAT_POLES = {
  repair:         ['Évite le sujet',         'Affronte directement'      ],
  conflict:       ['Évite le conflit',        'Très affirmatif·ve'        ],
  accountability: ['Dévie la responsabilité', 'Assume tout'               ],
  generosity:     ['Centré·e sur lui-même',   'Extrêmement généreux·se'   ],
  presence:       ['Souvent distrait·e',      'Totalement présent·e'      ],
  narrative:      ['Encore en recherche',     'Exceptionnellement ancré·e'],
  stability:      ['Très réactif·ve',         'Inébranlable'              ],
  boredom:        ['Aime la routine',         'Adore le changement'       ],
  social:         ['Très introverti·e',       'Totalement extraverti·e'   ],
  stakes:         ['Très détendu·e',          'Très ambitieux·se'         ],
};
const COMPAT_POLES_FIT = {
  repair:         ['Laisse se tasser',        'Confronte sans attendre'   ],
  conflict:       ['Calme, évite la tension', 'Aussi direct·e'            ],
  accountability: ['Pardonne facilement',     'Hauts standards'           ],
  generosity:     ['Très indépendant·e',      'Aussi généreux·se'         ],
  presence:       ["Donne de l'espace",       'Présent·e 100% du temps'   ],
  narrative:      ['Aussi en exploration',    'Aussi accompli·e'          ],
  stability:      ['Spontané·e, énergique',   'Aussi stable et posé·e'    ],
  boredom:        ['Aime les habitudes',      'Aussi aventurier·ère'      ],
  social:         ['Aime rester chez soi',    'Aussi extraverti·e'        ],
  stakes:         ["Valorise l'équilibre",    'Aussi ambitieux·se'        ],
};

// ── A/B Tests Tab ────────────────────────────────────────────────────────
const DEFAULT_EXPERIMENTS = [
  { id:'discover_cta',    name:'Discover CTA label',       variants:['like','connect','spark'],             description:'Label on the primary action button in Discover' },
  { id:'match_header',    name:'Match modal header style', variants:['minimal','with_score','with_compat'], description:'How compat info is shown in the match modal' },
  { id:'quiz_entry',      name:'Quiz entry prompt',        variants:['soft','direct','skip_default'],       description:'How the quiz interstitial is presented to new users' },
  { id:'hive_boost_cta',  name:'Boost CTA copy',          variants:['boost','highlight','pin'],            description:'Copy on the targeted hive boost button' },
  { id:'digest_timing',   name:'Weekly digest placement',  variants:['top','inline','none'],                description:'Where the weekly digest banner appears in Matches' },
];

function ABTestsTab() {
  const { features, supabase } = useApp();
  const savedExps = features?.ab_experiments || {};
  const [experiments, setExperiments] = useState(() =>
    DEFAULT_EXPERIMENTS.map(exp => ({
      ...exp,
      enabled: savedExps[exp.id]?.enabled !== false,
      activeVariants: savedExps[exp.id]?.variants || exp.variants,
    }))
  );
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const toggle = (id) => setExperiments(prev => prev.map(e => e.id === id ? { ...e, enabled: !e.enabled } : e));

  const save = async () => {
    setSaving(true);
    const payload = {};
    experiments.forEach(e => { payload[e.id] = { enabled: e.enabled, variants: e.activeVariants }; });
    try {
      await supabase?.from('networks').update({ features: { ...features, ab_experiments: payload } }).eq('id', features?.network_id || 'global');
    } catch {}
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const getLocalOverride = (id) => { try { return localStorage.getItem(`ab_${id}`) || ''; } catch { return ''; } };
  const setLocalOverride = (id, val) => { try { if (val) localStorage.setItem(`ab_${id}`, val); else localStorage.removeItem(`ab_${id}`); } catch {} };

  return (
    <div>
      <PageHeader title="A/B Tests" sub="Manage experiments — variant assignment is deterministic per user ID"/>
      <div style={{ padding:'0 1.5rem' }}>
        <div style={{ display:'flex', flexDirection:'column', gap:'1rem', marginBottom:'1.5rem' }}>
          {experiments.map(exp => (
            <div key={exp.id} style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:C.r, padding:'1rem 1.2rem' }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'0.4rem' }}>
                <div>
                  <p style={{ fontSize:'0.85rem', fontWeight:600, color:C.text, margin:'0 0 2px' }}>{exp.name}</p>
                  <p style={{ fontSize:'0.65rem', color:C.muted, margin:0 }}>{exp.description}</p>
                </div>
                <button onClick={() => toggle(exp.id)} style={{ width:40, height:22, borderRadius:11, border:'none', cursor:'pointer', background:exp.enabled ? '#C9A84C' : C.border, position:'relative', transition:'background 0.2s', flexShrink:0 }}>
                  <span style={{ position:'absolute', top:2, left:exp.enabled ? 19 : 2, width:18, height:18, borderRadius:9, background:'#fff', transition:'left 0.2s', display:'block' }}/>
                </button>
              </div>
              <div style={{ display:'flex', flexWrap:'wrap', gap:'0.4rem', marginTop:'0.6rem' }}>
                {exp.variants.map(v => (
                  <span key={v} style={{ fontSize:'0.62rem', padding:'2px 8px', borderRadius:99, background:'rgba(201,168,76,0.1)', border:'1px solid rgba(201,168,76,0.25)', color:'#C9A84C', fontFamily:'Outfit' }}>{v}</span>
                ))}
              </div>
              <div style={{ marginTop:'0.6rem', display:'flex', alignItems:'center', gap:'0.5rem' }}>
                <span style={{ fontSize:'0.6rem', color:C.muted }}>QA override:</span>
                <select defaultValue={getLocalOverride(exp.id)} onChange={e => setLocalOverride(exp.id, e.target.value)}
                  style={{ fontSize:'0.65rem', padding:'2px 6px', background:C.bg, border:`1px solid ${C.border}`, color:C.text, borderRadius:4, fontFamily:'Outfit', cursor:'pointer' }}>
                  <option value="">— auto —</option>
                  {exp.variants.map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
            </div>
          ))}
        </div>
        <button onClick={save} disabled={saving} style={{ padding:'0.7rem 1.4rem', background:'linear-gradient(135deg,#C9A84C,#B8922A)', border:'none', color:'#0A0A0A', fontFamily:'Outfit', fontSize:'0.8rem', fontWeight:700, letterSpacing:'0.06em', textTransform:'uppercase', cursor:'pointer', borderRadius:C.r, opacity:saving?0.7:1 }}>
          {saving ? 'Saving…' : saved ? '✓ Saved' : 'Save experiments'}
        </button>
        <div style={{ marginTop:'1.5rem', padding:'0.9rem', background:'rgba(201,168,76,0.05)', border:'1px solid rgba(201,168,76,0.15)', borderRadius:C.r }}>
          <p style={{ fontSize:'0.65rem', color:C.muted, lineHeight:1.6, margin:0 }}>
            <strong style={{ color:C.text }}>Usage in code:</strong> import {'{ useVariant }'} from '../utils/useVariant'<br/>
            const cta = useVariant('discover_cta', ['like','connect','spark'])<br/>
            <span style={{ opacity:0.7 }}>Returns the same variant for every session of a given user.</span>
          </p>
        </div>
      </div>
    </div>
  );
}

function CompatibilityWeightsPanel() {
  const [weights, setWeights] = useState(() => getCompatWeights());
  const [saved, setSaved] = useState(false);

  const handleChange = (ax, val) => {
    setWeights(w => ({ ...w, [ax]: parseFloat(val) }));
    setSaved(false);
  };
  const handleSave = () => { saveCompatWeights(weights); setSaved(true); setTimeout(() => setSaved(false), 2000); };
  const handleReset = () => { setWeights({ ...DEFAULT_COMPAT_WEIGHTS }); saveCompatWeights(DEFAULT_COMPAT_WEIGHTS); setSaved(false); };

  const relational = ['repair','conflict','accountability','generosity','presence'];
  const identity   = ['narrative','stability','boredom','social','stakes'];

  const renderGroup = (axes, groupLabel, groupColor) => (
    <div style={{ marginBottom:20 }}>
      <div style={{ fontSize:11, fontWeight:600, color:groupColor, letterSpacing:'0.08em', textTransform:'uppercase', marginBottom:10 }}>{groupLabel}</div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
        {axes.map(ax => {
          const meta = AXIS_META[ax] || {};
          const w = weights[ax] ?? DEFAULT_COMPAT_WEIGHTS[ax];
          const def = DEFAULT_COMPAT_WEIGHTS[ax];
          const pct = Math.round((w / 3.0) * 100);
          return (
            <div key={ax} style={{ background:C.bg, border:`0.5px solid ${C.border}`, borderRadius:C.radiusMd, padding:'10px 12px' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
                <span style={{ fontSize:12, fontWeight:600, color:C.text }}>{meta.icon} {meta.label || ax}</span>
                <span style={{ fontSize:13, fontWeight:700, color: w > def ? '#C9A84C' : w < def ? '#7AB8C4' : C.sub, minWidth:28, textAlign:'right' }}>{w.toFixed(1)}</span>
              </div>
              <input
                type="range" min="0.5" max="3.0" step="0.1" value={w}
                onChange={e => handleChange(ax, e.target.value)}
                style={{ width:'100%', accentColor:'#C9A84C', cursor:'pointer' }}
              />
              <div style={{ display:'flex', justifyContent:'space-between', marginTop:3 }}>
                <span style={{ fontSize:9, color:C.muted }}>0.5× impact</span>
                <div style={{ height:3, flex:1, margin:'0 6px', background:`linear-gradient(90deg,#C9A84C ${pct}%,${C.border} ${pct}%)`, borderRadius:2, alignSelf:'center' }}/>
                <span style={{ fontSize:9, color:C.muted }}>3×</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <div style={{ background:C.surface, border:`0.5px solid ${C.border}`, borderRadius:C.radiusLg, padding:'16px 20px', marginBottom:28 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
        <div>
          <p style={{ fontSize:14, fontWeight:700, color:C.text, margin:'0 0 2px' }}>⚖️ Pondération des axes</p>
          <p style={{ fontSize:11, color:C.sub, margin:0 }}>Modifie l'impact de chaque axe sur le score global de compatibilité (0.5× → 3×). Sauvegardé dans le navigateur.</p>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          <button onClick={handleReset} style={{ padding:'6px 12px', fontSize:11, background:C.bg, border:`0.5px solid ${C.border}`, borderRadius:C.radiusMd, color:C.sub, cursor:'pointer' }}>Réinitialiser</button>
          <button onClick={handleSave} style={{ padding:'6px 14px', fontSize:11, background: saved ? '#2A6A2A' : '#C9A84C', border:'none', borderRadius:C.radiusMd, color:'#fff', fontWeight:600, cursor:'pointer', transition:'background 0.2s' }}>
            {saved ? '✓ Sauvegardé' : 'Sauvegarder'}
          </button>
        </div>
      </div>
      {renderGroup(relational, 'Axes relationnels', '#7AB8C4')}
      {renderGroup(identity,   'Axes identitaires', '#C9A84C')}
    </div>
  );
}

function CompatibilityAxesTab() {
  const relational = ['repair','conflict','accountability','generosity','presence'];
  const identity   = ['narrative','stability','boredom','social','stakes'];

  const PoleBar = ({ axKey, polesMap, label, accentColor, textColor, bgColor }) => {
    const inv  = COMPAT_INVERTED.has(axKey);
    const raw  = polesMap[axKey] || ['–', '–'];
    const [l, r] = inv ? [raw[1], raw[0]] : raw;
    return (
      <div style={{ background:bgColor, borderRadius:C.radius, padding:'8px 12px' }}>
        <div style={{ fontSize:10, fontWeight:600, color:textColor, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:6 }}>{label}</div>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <span style={{ fontSize:11, color:C.sub, flex:1, textAlign:'left' }}>{l}</span>
          <div style={{ display:'flex', gap:3, flexShrink:0 }}>
            {[1,2,3,4,5,6,7].map(i => (
              <div key={i} style={{ width:5, height:5, borderRadius:'50%', background: i <= 3 ? accentColor+'99' : i >= 5 ? accentColor : accentColor+'44' }}/>
            ))}
          </div>
          <span style={{ fontSize:11, color:C.sub, flex:1, textAlign:'right' }}>{r}</span>
        </div>
      </div>
    );
  };

  const AxisCard = ({ axKey }) => {
    const meta   = AXIS_META[axKey] || {};
    const isRel  = meta.category === 'relational';
    const inv    = COMPAT_INVERTED.has(axKey);
    const wDef   = DEFAULT_COMPAT_WEIGHTS[axKey] ?? 1.0;
    const catColor    = isRel ? '#7AB8C4' : '#C9A84C';
    const catBg       = isRel ? 'rgba(122,184,196,0.12)' : 'rgba(201,168,76,0.12)';
    const catText     = isRel ? '#1B5FAA' : '#92600A';
    return (
      <div style={{ background:C.surface, border:`0.5px solid ${C.border}`, borderRadius:C.radiusLg, overflow:'hidden', marginBottom:12 }}>
        {/* Header */}
        <div style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 14px', background:C.bg, borderBottom:`0.5px solid ${C.border}` }}>
          <span style={{ fontSize:16, lineHeight:1, flexShrink:0 }}>{meta.icon}</span>
          <span style={{ fontSize:13, fontWeight:700, color:C.text, flex:1 }}>{meta.label || axKey}</span>
          <span style={{ fontSize:10, padding:'2px 8px', borderRadius:99, background:catBg, color:catText, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.05em' }}>
            {isRel ? 'Relationnel' : 'Identitaire'}
          </span>
          <span style={{ fontSize:11, color: wDef >= 1.8 ? C.accent : C.sub, background:'rgba(0,0,0,0.04)', padding:'2px 8px', borderRadius:99, fontWeight:600 }}>
            ×{wDef.toFixed(1)}
          </span>
          {inv && (
            <span title="Inverted axis — poles swapped in UI to avoid left=bad bias" style={{ fontSize:10, color:C.muted, background:C.bg, border:`0.5px solid ${C.border}`, padding:'2px 7px', borderRadius:99 }}>
              🔄 inversé
            </span>
          )}
        </div>
        {/* Poles */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, padding:'10px 14px' }}>
          <PoleBar axKey={axKey} polesMap={COMPAT_POLES}     label="Soi (noté par les amis)"   accentColor={catColor} textColor={catText} bgColor={catBg} />
          <PoleBar axKey={axKey} polesMap={COMPAT_POLES_FIT} label="Partenaire idéal (souhaité)" accentColor='#9CA880'  textColor='#3A6A3A' bgColor='rgba(156,168,128,0.1)' />
        </div>
      </div>
    );
  };

  return (
    <>
      <PageHeader
        title="Axes de compatibilité"
        sub="10 axes · échelle 1–7 (amis) ou 1–4 (quiz) · normalisés 0–10 en interne"
      />

      {/* Weight sliders — unchanged, reads from AXIS_META + localStorage */}
      <CompatibilityWeightsPanel />

      {/* Scoring explainer */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:24 }}>
        <div style={{ background:'rgba(201,168,76,0.08)', border:`0.5px solid rgba(201,168,76,0.3)`, borderRadius:C.radiusLg, padding:'12px 16px' }}>
          <p style={{ fontSize:12, fontWeight:600, color:'#92600A', margin:'0 0 4px' }}>Évaluation par les amis</p>
          <p style={{ fontSize:12, color:C.sub, margin:0, lineHeight:1.6 }}>
            Échelle 1–7 par axe, deux passages : <strong>Soi</strong> (comment est la personne) et <strong>Partenaire</strong> (ce qu'elle cherche). Normalisé 0–10 pour le moteur de compatibilité.
          </p>
        </div>
        <div style={{ background:'rgba(156,168,128,0.1)', border:`0.5px solid rgba(156,168,128,0.35)`, borderRadius:C.radiusLg, padding:'12px 16px' }}>
          <p style={{ fontSize:12, fontWeight:600, color:'#3A6A3A', margin:'0 0 4px' }}>Algorithme de matching</p>
          <p style={{ fontSize:12, color:C.sub, margin:0, lineHeight:1.6 }}>
            Score = Σ poids × alignement par axe. Alignement : 1 − |normA − normB| / 10. Les 4 axes inversés (🔄) ont leurs pôles affichés en sens inverse dans FriendRating pour éviter le biais "gauche = mauvais".
          </p>
        </div>
      </div>

      {/* Relational */}
      <div style={{ marginBottom:8 }}>
        <p style={{ fontSize:11, fontWeight:600, color:'#1B5FAA', letterSpacing:'0.08em', textTransform:'uppercase', margin:'0 0 10px', display:'flex', alignItems:'center', gap:8 }}>
          <span style={{ background:'rgba(122,184,196,0.15)', padding:'3px 10px', borderRadius:99 }}>Axes relationnels · 5 axes</span>
        </p>
        {relational.map(ax => <AxisCard key={ax} axKey={ax} />)}
      </div>

      {/* Identity */}
      <div>
        <p style={{ fontSize:11, fontWeight:600, color:'#92600A', letterSpacing:'0.08em', textTransform:'uppercase', margin:'0 0 10px', display:'flex', alignItems:'center', gap:8 }}>
          <span style={{ background:'rgba(201,168,76,0.15)', padding:'3px 10px', borderRadius:99 }}>Axes identitaires · 5 axes</span>
        </p>
        {identity.map(ax => <AxisCard key={ax} axKey={ax} />)}
      </div>
    </>
  );
}

// ── Hive Groups section ───────────────────────────────────────────────────
const PRESET_COLORS = ['#C9A84C','#1A7A4A','#A084C4','#7AADCA','#CA7A5B','#7AAA58','#7A7ACC','#CA9B5B','#5BAA7A','#C07A7A'];

function HiveGroupsTab({ users = [] }) {
  const [groups, setGroups] = useState(() => loadHiveGroups());
  const [editing, setEditing] = useState(null);

  const saveGroup = (form) => {
    const id = form.id || 'g_' + Date.now();
    const next = groups.find(g => g.id === form.id)
      ? groups.map(g => g.id === form.id ? { ...form, id } : g)
      : [...groups, { ...form, id, signal: 'category', enabled: true, visibleInCommunity: true }];
    setGroups(next);
    saveHiveGroups(next);
    setEditing(null);
  };

  const deleteGroup = (id) => {
    if (!window.confirm('Delete this group?')) return;
    const next = groups.filter(g => g.id !== id);
    setGroups(next);
    saveHiveGroups(next);
  };

  const toggleEnabled = (id) => {
    const next = groups.map(g => g.id === id ? { ...g, enabled: !g.enabled } : g);
    setGroups(next);
    saveHiveGroups(next);
  };

  // Compute member count per group
  const memberCounts = Object.fromEntries(
    groups.map(g => [g.id, getUsersForGroup(g, users).length])
  );

  return (
    <>
      <PageHeader
        title="Hive groups"
        sub={`${groups.filter(g=>g.enabled).length} active groups · membership via catégories d'intérêts`}
        action={<Btn variant="primary" onClick={() => setEditing({ emoji:'🐝', label:'', desc:'', color:C.accent, categoryIds:[] })}>+ New group</Btn>}
      />

      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))', gap:12 }}>
        {groups.map(g => {
          const count = memberCounts[g.id] ?? 0;
          const hasCats = g.categoryIds?.length > 0;
          return (
            <div key={g.id} style={{ background:C.surface, border:`0.5px solid ${g.enabled ? C.border : C.border}`, borderRadius:C.radiusLg, padding:16, opacity: g.enabled ? 1 : 0.5 }}>
              {/* Header */}
              <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10 }}>
                <div style={{ width:36, height:36, borderRadius:8, background: g.color ? g.color+'22' : 'rgba(201,168,76,0.12)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:18, flexShrink:0 }}>
                  {g.emoji || g.icon || '🐝'}
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <p style={{ fontSize:13, fontWeight:600, color:C.text, margin:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{g.label || g.id}</p>
                  <p style={{ fontSize:11, color:C.muted, margin:'1px 0 0' }}>{g.desc || g.description || '—'}</p>
                </div>
              </div>
              {/* Stats row */}
              <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10 }}>
                <span style={{ fontSize:11, fontWeight:600, color: count > 0 ? C.green : C.muted, background: count > 0 ? C.greenBg : C.bg, padding:'2px 8px', borderRadius:99 }}>
                  {count} membre{count !== 1 ? 's' : ''}
                </span>
                <span style={{ fontSize:11, color: hasCats ? C.accent : C.muted, background: hasCats ? 'rgba(201,168,76,0.1)' : C.bg, padding:'2px 8px', borderRadius:99 }}>
                  {hasCats ? `${g.categoryIds.length} critère${g.categoryIds.length > 1 ? 's' : ''}` : 'Aucun critère'}
                </span>
              </div>
              {/* Actions */}
              <div style={{ display:'flex', gap:6 }}>
                <Btn size="sm" onClick={() => setEditing({ ...g, categoryIds: g.categoryIds || [] })}>Modifier</Btn>
                <button
                  onClick={() => toggleEnabled(g.id)}
                  style={{ padding:'4px 10px', borderRadius:C.radius, border:`0.5px solid ${C.border}`, background:'transparent', color:C.sub, fontSize:11, cursor:'pointer' }}>
                  {g.enabled ? 'Désactiver' : 'Activer'}
                </button>
                <button
                  onClick={() => deleteGroup(g.id)}
                  style={{ padding:'4px 10px', borderRadius:C.radius, border:`0.5px solid ${C.red}20`, background:C.redBg, color:C.red, fontSize:11, cursor:'pointer', marginLeft:'auto' }}>
                  ✕
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Editor modal */}
      {editing && (
        <HiveGroupEditor
          group={editing}
          users={users}
          onSave={saveGroup}
          onClose={() => setEditing(null)}
        />
      )}
    </>
  );
}

function HiveGroupEditor({ group, users, onSave, onClose }) {
  const [form, setForm] = useState({ ...group, categoryIds: group.categoryIds || [] });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const selected = new Set(form.categoryIds);
  const handleCatChange = (newSet) => set('categoryIds', [...newSet]);

  const matchCount = users.filter(u =>
    selected.size > 0 && (u.interests || []).some(i => selected.has(i))
  ).length;

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:24 }}
         onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background:C.surface, borderRadius:C.radiusLg, border:`0.5px solid ${C.border}`, width:'100%', maxWidth:780, maxHeight:'92vh', overflow:'auto', display:'flex', flexDirection:'column' }}>
        {/* Modal header */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'16px 20px', borderBottom:`0.5px solid ${C.border}`, flexShrink:0 }}>
          <h2 style={{ margin:0, fontSize:16, fontWeight:500, color:C.text }}>
            {group.id ? 'Modifier le groupe' : 'Nouveau groupe'}
          </h2>
          <button onClick={onClose} style={{ background:'none', border:'none', color:C.muted, cursor:'pointer', fontSize:20, lineHeight:1, padding:4 }}>×</button>
        </div>

        <div style={{ display:'grid', gridTemplateColumns:'1fr 1.3fr', gap:0, flex:1, minHeight:0 }}>
          {/* Left — identity */}
          <div style={{ padding:20, borderRight:`0.5px solid ${C.border}`, overflowY:'auto' }}>
            <p style={{ fontSize:11, fontWeight:600, color:C.muted, textTransform:'uppercase', letterSpacing:'0.08em', margin:'0 0 14px' }}>Identité du groupe</p>

            <div style={{ display:'grid', gridTemplateColumns:'56px 1fr', gap:10, marginBottom:12 }}>
              <FormField label="Emoji">
                <input value={form.emoji||form.icon||''} onChange={e=>set('emoji',e.target.value)}
                  style={{ width:'100%', padding:'8px 6px', border:`0.5px solid ${C.borderMd}`, borderRadius:C.radius, fontSize:22, textAlign:'center', background:C.surface, outline:'none', boxSizing:'border-box' }}/>
              </FormField>
              <FormField label="Nom">
                <Input value={form.label||''} onChange={e=>set('label',e.target.value)} placeholder="Ex: Amateurs de plein air"/>
              </FormField>
            </div>

            <FormField label="Description">
              <textarea value={form.desc||form.description||''} onChange={e=>set('desc',e.target.value)} rows={2} placeholder="Ce groupe rassemble…"
                style={{ width:'100%', padding:'8px 10px', border:`0.5px solid ${C.borderMd}`, borderRadius:C.radius, fontSize:13, color:C.text, background:C.surface, resize:'vertical', outline:'none', boxSizing:'border-box' }}/>
            </FormField>

            <FormField label="Couleur">
              <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginTop:4 }}>
                {PRESET_COLORS.map(col => (
                  <button key={col} onClick={() => set('color', col)}
                    style={{ width:22, height:22, borderRadius:'50%', background:col, border: form.color===col ? `2.5px solid ${C.text}` : '2px solid transparent', cursor:'pointer', flexShrink:0 }}/>
                ))}
              </div>
            </FormField>

            {/* Live preview */}
            <div style={{ marginTop:16, padding:14, background:C.bg, borderRadius:C.radiusLg, border:`0.5px solid ${C.border}` }}>
              <p style={{ fontSize:11, fontWeight:600, color:C.muted, textTransform:'uppercase', letterSpacing:'0.08em', margin:'0 0 10px' }}>Aperçu</p>
              <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                <div style={{ width:40, height:40, borderRadius:10, background: (form.color||C.accent)+'22', display:'flex', alignItems:'center', justifyContent:'center', fontSize:20 }}>
                  {form.emoji || form.icon || '🐝'}
                </div>
                <div>
                  <p style={{ fontSize:13, fontWeight:600, color:C.text, margin:0 }}>{form.label || 'Nom du groupe'}</p>
                  <p style={{ fontSize:11, color:C.muted, margin:'2px 0 0' }}>
                    {selected.size > 0
                      ? <><span style={{ color: matchCount > 0 ? C.green : C.muted, fontWeight:600 }}>{matchCount}</span> membre{matchCount!==1?'s':''} · {selected.size} critère{selected.size>1?'s':''}</>
                      : 'Aucun critère défini'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right — category picker */}
          <div style={{ padding:20, overflowY:'auto' }}>
            <p style={{ fontSize:11, fontWeight:600, color:C.muted, textTransform:'uppercase', letterSpacing:'0.08em', margin:'0 0 14px' }}>
              Catégories d'intérêts
            </p>
            <p style={{ fontSize:12, color:C.sub, margin:'0 0 14px', lineHeight:1.5 }}>
              Les membres sont inclus automatiquement s'ils ont coché au moins un des intérêts sélectionnés.
            </p>
            <CategoryTreePicker
              selected={selected}
              onChange={handleCatChange}
              lang="fr"
              users={users}
              compact
            />
          </div>
        </div>

        {/* Footer */}
        <div style={{ display:'flex', justifyContent:'flex-end', gap:8, padding:'14px 20px', borderTop:`0.5px solid ${C.border}`, flexShrink:0 }}>
          <Btn onClick={onClose}>Annuler</Btn>
          <Btn variant="primary" onClick={() => onSave(form)} disabled={!form.label?.trim()}>
            {group.id ? 'Enregistrer' : 'Créer le groupe'}
          </Btn>
        </div>
      </div>
    </div>
  );
}


// ── Plan Features Tab ─────────────────────────────────────────────────────
function PlanFeaturesTab() {
  const [matrix, setMatrix] = useState(null);
  const [errMsg, setErrMsg] = useState(null);
  const limitTimers = useRef({});

  useEffect(() => {
    db.plan_features()?.select('plan,feature_key,enabled,limit_value')
      .then(({ data }) => {
        if (!data?.length) { setMatrix(DEFAULT_FEATURE_MATRIX); return; }
        const m = { free:{}, honey:{}, royal:{} };
        data.forEach(({ plan, feature_key, enabled, limit_value }) => {
          if (m[plan]) m[plan][feature_key] = { enabled, limit: limit_value };
        });
        setMatrix(m);
      });
  }, []);

  // Auto-save a single (plan, key) row.
  const persistCell = async (plan, key, entry) => {
    const { error } = await db.plan_features()?.upsert({
      plan, feature_key: key,
      enabled: !!entry.enabled,
      limit_value: entry.limit === null || entry.limit === undefined ? null : entry.limit,
    }, { onConflict: 'plan,feature_key' }) ?? {};
    if (error) {
      setErrMsg(`${plan}.${key}: ${error.message}`);
      setTimeout(() => setErrMsg(null), 4000);
    }
  };

  const toggle = (plan, key) => {
    setMatrix(prev => {
      const next = { ...prev, [plan]: { ...prev[plan], [key]: { ...prev[plan][key], enabled: !prev[plan][key].enabled } } };
      persistCell(plan, key, next[plan][key]);
      return next;
    });
  };

  const setLimit = (plan, key, val) => {
    const num = val === '' ? null : parseInt(val);
    const limit = isNaN(num) ? null : num;
    setMatrix(prev => {
      const next = { ...prev, [plan]: { ...prev[plan], [key]: { ...prev[plan][key], limit } } };
      // Debounce limit-input save to avoid per-keystroke writes
      const t = `${plan}:${key}`;
      clearTimeout(limitTimers.current[t]);
      limitTimers.current[t] = setTimeout(() => persistCell(plan, key, next[plan][key]), 600);
      return next;
    });
  };

  if (!matrix) return <p style={{ color:C.muted, padding:24 }}>Loading…</p>;

  const plans = ['free','honey','royal'];
  const planColors = { free:'#888', honey:'#C9A84C', royal:'#E2C06E' };
  const features   = Object.keys(FEATURE_LABELS);

  return (
    <>
      <p style={{ fontSize:13, color:C.muted, margin:'0 0 14px' }}>
        Toggle features and set limits per plan — each change is saved immediately (limits are debounced 600ms).
      </p>

      {errMsg && (
        <div style={{ background:'rgba(192,69,58,0.12)', border:`0.5px solid ${C.red}`, borderRadius:C.radius, padding:'8px 12px', marginBottom:12, fontSize:12, color:C.red }}>
          ⚠ {errMsg}
        </div>
      )}
      <div style={{ overflowX:'auto' }}>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
          <thead>
            <tr style={{ background:C.bg }}>
              <th style={{ padding:'10px 14px', textAlign:'left', fontWeight:500, color:C.sub, fontSize:12, borderBottom:`0.5px solid ${C.border}`, minWidth:200 }}>
                Feature
              </th>
              {plans.map(plan => (
                <th key={plan} style={{ padding:'10px 14px', textAlign:'center', fontWeight:600, fontSize:12, borderBottom:`0.5px solid ${C.border}`, color: planColors[plan], minWidth:120 }}>
                  {plan.charAt(0).toUpperCase() + plan.slice(1)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {features.map((key, i) => (
              <tr key={key} style={{ borderBottom:`0.5px solid ${C.border}`, background: i%2===0 ? C.surface : C.bg }}>
                <td style={{ padding:'9px 14px', color:C.text, fontWeight:400 }}>
                  {FEATURE_LABELS[key]}
                </td>
                {plans.map(plan => {
                  const entry = matrix[plan]?.[key] || { enabled:false, limit:0 };
                  return (
                    <td key={plan} style={{ padding:'8px 14px', textAlign:'center', verticalAlign:'middle' }}>
                      <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:4 }}>
                        <label style={{ display:'flex', alignItems:'center', cursor:'pointer', position:'relative' }}>
                          <input type="checkbox" checked={!!entry.enabled}
                            onChange={() => toggle(plan, key)}
                            style={{ opacity:0, width:0, height:0, position:'absolute' }}/>
                          <div style={{ width:34, height:20, borderRadius:99, background:entry.enabled ? planColors[plan] : C.borderMd, position:'relative', transition:'background 0.2s' }}>
                            <div style={{ position:'absolute', top:2, left:entry.enabled?15:2, width:16, height:16, borderRadius:'50%', background:'white', transition:'left 0.2s' }}/>
                          </div>
                        </label>
                        {entry.enabled && (
                          <input
                            type="number" min={0}
                            value={entry.limit === null ? '' : entry.limit}
                            onChange={e => setLimit(plan, key, e.target.value)}
                            placeholder="∞"
                            title="Empty = unlimited"
                            style={{ width:56, padding:'3px 6px', border:`0.5px solid ${C.border}`, borderRadius:4, fontSize:11, color:C.text, background:C.surface, textAlign:'center', outline:'none' }}
                          />
                        )}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p style={{ fontSize:11, color:C.muted, marginTop:12 }}>
        Empty limit field = unlimited. 0 = disabled. Changes are enforced server-side via Supabase RPC.
      </p>
    </>
  );
}


// ── Invitations & Waitlist Tab ────────────────────────────────────────────
// networkIdsJson accepted for API consistency; waitlist/invitations tables have no network_id column
function InvitationsTab({ networkId, networkIdsJson }) {
  const [waitlist,       setWaitlist]       = useState([]);
  const [invites,        setInvites]        = useState([]);
  const [loadingW,       setLoadingW]       = useState(true);
  const [loadingI,       setLoadingI]       = useState(true);
  const [activeSection,  setActiveSection]  = useState('waitlist');
  const [sendingTo,      setSendingTo]      = useState(null);
  const [rejectingId,    setRejectingId]    = useState(null);
  const [adminNotes,     setAdminNotes]     = useState({});  // id → note string
  const [email,          setEmail]          = useState('');
  const [role,           setRole]           = useState('user');
  const [sending,        setSending]        = useState(false);
  const [sentTo,         setSentTo]         = useState(null);

  const reloadInvites = () =>
    supabase?.from('invitations').select('recipient_email, invite_type, granted_role, is_founding, used_count, created_at')
      .order('created_at', { ascending:false }).limit(100)
      .then(({ data }) => { setInvites(data||[]); setLoadingI(false); });

  useEffect(() => {
    supabase?.from('waitlist').select('*').order('position').limit(100)
      .then(({ data }) => { setWaitlist(data||[]); setLoadingW(false); });
    reloadInvites();
  }, []);

  const doSend = async (addr, grantedRole) => {
    const code = crypto.randomUUID();
    const isFounder = grantedRole === 'founder';
    const { error } = await supabase?.from('invitations').insert({
      code,
      is_admin_code:   true,
      is_founding:     isFounder,
      invite_type:     grantedRole,
      granted_role:    grantedRole,
      plan_grant:      isFounder ? 'founder' : null,
      max_uses:        1,
      recipient_email: addr,
      note:            `Admin invite (${grantedRole}): ${addr}`,
    });
    if (error) { alert(error.message); return false; }
    return true;
  };

  const sendDirect = async () => {
    const addr = email.trim().toLowerCase();
    if (!addr) return;
    setSending(true);
    const ok = await doSend(addr, role);
    setSending(false);
    if (!ok) return;
    setSentTo(addr);
    setEmail('');
    setTimeout(() => setSentTo(null), 4000);
    reloadInvites();
  };

  const sendToWaitlist = async (entry) => {
    setSendingTo(entry.id);
    const ok = await doSend(entry.email, 'user');
    if (ok) {
      const note = adminNotes[entry.id] || null;
      await supabase?.from('waitlist').update({ invite_sent: true, status: 'approved', admin_note: note }).eq('id', entry.id);
      setWaitlist(prev => prev.map(w => w.id === entry.id ? { ...w, invite_sent: true, status: 'approved', admin_note: note } : w));
    }
    setSendingTo(null);
  };

  const rejectEntry = async (entry) => {
    setRejectingId(entry.id);
    const note = adminNotes[entry.id] || null;
    await supabase?.from('waitlist').update({ status: 'rejected', admin_note: note }).eq('id', entry.id);
    setWaitlist(prev => prev.map(w => w.id === entry.id ? { ...w, status: 'rejected', admin_note: note } : w));
    setRejectingId(null);
  };

  const roleLabel = { user:'User', cml:'CML', admin:'Admin', founder:'Founder' };

  return (
    <>
      <PageHeader
        title="Invitations"
        sub={`${waitlist.filter(w => !w.invite_sent).length} waitlist pending · ${invites.filter(i => i.used_count > 0).length} accepted`}
      />

      {/* Send invitation */}
      <div style={{ background:C.surface, border:`0.5px solid ${C.border}`, borderRadius:C.radiusLg, padding:'14px 16px', marginBottom:20 }}>
        <p style={{ fontSize:12, fontWeight:600, color:C.text, margin:'0 0 10px' }}>Send an invitation</p>
        <div style={{ display:'flex', gap:6, marginBottom:10 }}>
          {[['user','User'],['cml','CML'],['admin','Admin'],['founder','Founder']].map(([val,label]) => (
            <button key={val} onClick={() => setRole(val)} style={{ padding:'4px 12px', borderRadius:99, border:`0.5px solid ${role===val?C.accent:C.border}`, background:role===val?'rgba(201,168,76,0.12)':'transparent', color:role===val?C.accent:C.muted, fontSize:11, cursor:'pointer', fontWeight:role===val?600:400, transition:'all 0.15s' }}>
              {label}
            </button>
          ))}
        </div>
        <div style={{ display:'flex', gap:8 }}>
          <input
            value={email} onChange={e => setEmail(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && sendDirect()}
            placeholder="email@example.com"
            style={{ flex:1, padding:'7px 10px', background:C.bg, border:`0.5px solid ${C.border}`, borderRadius:C.radius, color:C.text, fontSize:12, outline:'none', fontFamily:'inherit' }}
          />
          <Btn variant="primary" onClick={sendDirect} disabled={sending || !email.trim()}>
            {sending ? 'Sending…' : 'Send'}
          </Btn>
        </div>
        {sentTo && (
          <p style={{ fontSize:11, color:C.green, margin:'8px 0 0', fontWeight:500 }}>
            ✓ Invitation sent to {sentTo} as {roleLabel[role]}
          </p>
        )}
      </div>

      {/* Section tabs */}
      <div style={{ display:'flex', gap:6, marginBottom:14 }}>
        {[['waitlist','Waitlist'],['sent','Sent']].map(([id,label]) => (
          <button key={id} onClick={() => setActiveSection(id)} style={{ padding:'5px 12px', borderRadius:C.radius, border:`0.5px solid ${activeSection===id?C.accent:C.border}`, background:activeSection===id?'rgba(201,168,76,0.1)':'transparent', color:activeSection===id?C.accent:C.sub, fontSize:12, cursor:'pointer', fontWeight:activeSection===id?500:400 }}>{label}</button>
        ))}
      </div>

      {activeSection === 'waitlist' ? (
        loadingW ? <p style={{ color:C.muted, padding:16 }}>Loading…</p> :
        waitlist.length === 0 ? <p style={{ color:C.muted, padding:16 }}>No one on the waitlist yet.</p> :
        <div style={{ background:C.surface, border:`0.5px solid ${C.border}`, borderRadius:C.radiusLg, overflow:'hidden' }}>
          {waitlist.map((entry, i) => {
            const status = entry.status || (entry.invite_sent ? 'approved' : 'pending');
            const isPending = status === 'pending' || (!entry.status && !entry.invite_sent);
            return (
              <div key={entry.id} style={{ padding:'12px 16px', borderBottom: i < waitlist.length-1 ? `0.5px solid ${C.border}` : 'none' }}>
                <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom: isPending ? 8 : 0 }}>
                  {entry.position != null && <span style={{ fontSize:11, color:C.muted, width:22, flexShrink:0, textAlign:'right' }}>#{entry.position}</span>}
                  <div style={{ flex:1, minWidth:0 }}>
                    <p style={{ fontSize:13, fontWeight:500, color:C.text, margin:'0 0 1px' }}>{entry.name || '—'}</p>
                    <p style={{ fontSize:11, color:C.muted, margin:0 }}>{entry.email}{entry.city ? ` · ${entry.city}` : ''}</p>
                    {entry.message && (
                      <p style={{ fontSize:11, color:C.sub, margin:'5px 0 0', fontStyle:'italic', borderLeft:`2px solid ${C.border}`, paddingLeft:8 }}>
                        "{entry.message}"
                      </p>
                    )}
                    {entry.admin_note && !isPending && (
                      <p style={{ fontSize:10, color:C.muted, margin:'4px 0 0' }}>Note: {entry.admin_note}</p>
                    )}
                  </div>
                  {!isPending && (
                    <Badge color={status === 'approved' ? 'green' : 'red'}>
                      {status === 'approved' ? 'Approved' : 'Rejected'}
                    </Badge>
                  )}
                </div>
                {isPending && (
                  <div style={{ display:'flex', flexDirection:'column', gap:6, marginLeft: entry.position != null ? 34 : 0 }}>
                    <textarea
                      value={adminNotes[entry.id] || ''}
                      onChange={e => setAdminNotes(prev => ({ ...prev, [entry.id]: e.target.value }))}
                      placeholder="Personal message (optional)…"
                      rows={2}
                      style={{ width:'100%', padding:'5px 8px', background:C.bg, border:`0.5px solid ${C.border}`, borderRadius:C.radius, color:C.text, fontSize:11, fontFamily:'inherit', resize:'none', outline:'none', boxSizing:'border-box' }}
                    />
                    <div style={{ display:'flex', gap:6 }}>
                      <Btn size="sm" variant="primary" onClick={() => sendToWaitlist(entry)} disabled={sendingTo === entry.id}>
                        {sendingTo === entry.id ? '…' : 'Approve & invite'}
                      </Btn>
                      <Btn size="sm" variant="danger" onClick={() => rejectEntry(entry)} disabled={rejectingId === entry.id}>
                        {rejectingId === entry.id ? '…' : 'Reject'}
                      </Btn>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        loadingI ? <p style={{ color:C.muted, padding:16 }}>Loading…</p> :
        invites.length === 0 ? <p style={{ color:C.muted, padding:16 }}>No invitations sent yet.</p> :
        <div style={{ background:C.surface, border:`0.5px solid ${C.border}`, borderRadius:C.radiusLg, overflow:'hidden' }}>
          {invites.map((inv, i) => (
            <div key={i} style={{ display:'flex', alignItems:'center', gap:10, padding:'9px 16px', borderBottom: i < invites.length-1 ? `0.5px solid ${C.border}` : 'none' }}>
              <div style={{ flex:1, minWidth:0 }}>
                <p style={{ fontSize:12, fontWeight:500, color: inv.used_count > 0 ? C.muted : C.text, margin:0 }}>{inv.recipient_email || '—'}</p>
                <p style={{ fontSize:11, color:C.muted, margin:0 }}>{ago(inv.created_at)}</p>
              </div>
              <Badge color={inv.is_founding ? 'amber' : 'default'}>{roleLabel[inv.granted_role] || inv.invite_type || 'User'}</Badge>
              <Badge color={inv.used_count > 0 ? 'green' : 'default'}>{inv.used_count > 0 ? 'Accepted' : 'Pending'}</Badge>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

// ── Category audiences ────────────────────────────────────────────────────
const AUDIENCES_KEY = 'beelove_audiences';
function loadAudiences() {
  try { return JSON.parse(localStorage.getItem(AUDIENCES_KEY) || '[]'); } catch { return []; }
}
function persistAudiences(list) {
  try { localStorage.setItem(AUDIENCES_KEY, JSON.stringify(list)); } catch {}
}

function AudienceRow({ audience: a, onDelete }) {
  const [open, setOpen] = useState(false);
  const labels = a.criteriaLabels || a.categoryIds;
  return (
    <div style={{ background:C.bg, borderRadius:C.radius, border:`0.5px solid ${C.border}` }}>
      <div style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 10px' }}>
        <span
          onClick={() => setOpen(o => !o)}
          style={{ fontSize:'0.6rem', color:C.muted, cursor:'pointer', userSelect:'none', width:12, flexShrink:0 }}>
          {open ? '▾' : '▸'}
        </span>
        <div style={{ flex:1, minWidth:0, cursor:'pointer' }} onClick={() => setOpen(o => !o)}>
          <p style={{ fontSize:13, fontWeight:500, color:C.text, margin:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{a.name}</p>
          <p style={{ fontSize:11, color:C.muted, margin:'2px 0 0' }}>
            {labels.length} criteria{a.createdAt ? ` · ${ago(a.createdAt)}` : ''}
          </p>
        </div>
        <ConfirmBtn size="sm" onConfirm={onDelete} confirmLabel="Delete?">✕</ConfirmBtn>
      </div>
      {open && (
        <div style={{ padding:'0 10px 10px 30px', display:'flex', flexWrap:'wrap', gap:4 }}>
          {labels.map((l, i) => (
            <span key={i} style={{ fontSize:11, color:C.accent, background:'rgba(201,168,76,0.1)', border:`0.5px solid rgba(201,168,76,0.3)`, padding:'2px 7px', borderRadius:99 }}>
              {l}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function CategoryAudiencesTab({ users = [] }) {
  const [selected, setSelected]   = useState(new Set());
  const [audiences, setAudiences] = useState(loadAudiences);
  const [nameInput, setNameInput] = useState('');
  const [flash, setFlash]         = useState('');
  // Rich user data for targeting match (interests + lifestyle fields not in main query)
  const [richUsers, setRichUsers] = useState([]);
  useEffect(() => {
    db.users()?.select('id,age,gender,plan,role,verified,invite_count,last_active,created_at,interests,diet,exercise,drinking,smoking,cannabis,pets,relationship_goal,love_language,religion,politics,education,education_field,mbti,zodiac,network_id,city,languages,quiz_answers')
      .is('deleted_at', null).limit(500)
      .then(({ data }) => { if (data) setRichUsers(data); });
  }, []);

  const save = () => {
    const name = nameInput.trim();
    if (!name || selected.size === 0) return;
    const criteriaLabels = [...selected].map(id => {
      const node = getNodeById(id);
      return node ? getLabel(node, 'fr') : id;
    });
    const next = [
      ...audiences.filter(a => a.name !== name),
      { name, categoryIds: [...selected], criteriaLabels, createdAt: Date.now() },
    ];
    setAudiences(next);
    persistAudiences(next);
    setSelected(new Set());
    setNameInput('');
    setFlash(`"${name}" saved — it now appears in the 🎯 Target Audiences section of the tree.`);
    setTimeout(() => setFlash(''), 4000);
  };

  const remove = (name) => {
    const next = audiences.filter(a => a.name !== name);
    setAudiences(next);
    persistAudiences(next);
  };

  return (
    <>
      <PageHeader title="Categories" sub="Browse interest categories · save named target audiences"/>
      <div style={{ display:'grid', gridTemplateColumns:'1.3fr 1fr', gap:24, alignItems:'start' }}>

        {/* Left — tree (includes 🎯 Target Audiences domain once audiences are saved) */}
        <CategoryTreePicker selected={selected} onChange={setSelected} lang="fr" />

        {/* Right — save panel + management */}
        <div style={{ display:'flex', flexDirection:'column', gap:14 }}>

          <div style={{ background:C.surface, border:`0.5px solid ${C.border}`, borderRadius:C.radiusLg, padding:16 }}>
            <p style={{ fontSize:13, fontWeight:600, color:C.text, margin:'0 0 4px' }}>Save as target audience</p>
            {(() => {
              // richUsers filtered to same population as the network-scoped users prop
              const userIds = new Set(users.map(u => u.id));
              const pool = richUsers.length > 0
                ? richUsers.filter(r => userIds.size === 0 || userIds.has(r.id))
                : users;
              const total = pool.length;
              const matched = selected.size > 0 ? countMatches(pool, [...selected]) : null;
              if (total === 0) return null;
              return (
                <div style={{ display:'flex', gap:12, alignItems:'center', marginBottom:10,
                  padding:'6px 10px', background:C.bg, borderRadius:C.radius, border:`0.5px solid ${C.border}` }}>
                  <span style={{ fontSize:11, color:C.muted }}>
                    Total: <strong style={{ color:C.text }}>{total}</strong>
                  </span>
                  {matched !== null && (
                    <>
                      <span style={{ fontSize:11, color:C.border }}>→</span>
                      <span style={{ fontSize:11, color:C.muted }}>
                        Matching: <strong style={{ color:C.accent }}>{matched}</strong>
                        <span style={{ marginLeft:4 }}>({Math.round(matched / total * 100)}%)</span>
                      </span>
                    </>
                  )}
                </div>
              );
            })()}
            <p style={{ fontSize:11, color:C.muted, margin:'0 0 12px' }}>
              {selected.size === 0
                ? 'Select categories in the tree, then name this audience.'
                : `${selected.size} criteria selected.`}
            </p>
            <div style={{ display:'flex', gap:8 }}>
              <input
                value={nameInput}
                onChange={e => setNameInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && save()}
                placeholder="e.g. Outdoor enthusiasts"
                style={{ flex:1, padding:'7px 10px', border:`0.5px solid ${C.borderMd}`, borderRadius:C.radius, fontSize:13, color:C.text, background:C.bg, outline:'none' }}
              />
              <Btn variant="primary" onClick={save} disabled={!nameInput.trim() || selected.size === 0}>Save</Btn>
            </div>
            {flash && <p style={{ fontSize:11, color:C.green, marginTop:8 }}>{flash}</p>}
          </div>

          {audiences.length > 0 && (
            <div style={{ background:C.surface, border:`0.5px solid ${C.border}`, borderRadius:C.radiusLg, padding:16 }}>
              <p style={{ fontSize:13, fontWeight:600, color:C.text, margin:'0 0 10px' }}>
                🎯 Target Audiences — {audiences.length}
              </p>
              <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                {audiences.map(a => (
                  <AudienceRow key={a.name} audience={a} onDelete={() => remove(a.name)} />
                ))}
              </div>
            </div>
          )}

        </div>
      </div>
    </>
  );
}

// ── Discovery Loop — Likes & Matches ─────────────────────────────────────
function DiscoveryLoopTab({ networkId }) {
  const [stats,   setStats]   = useState(null);
  const [likes,   setLikes]   = useState([]);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!supabase) return;
    Promise.all([
      supabase.from('likes').select('id', { count:'exact', head:true }),
      supabase.from('likes').select('id', { count:'exact', head:true }).eq('is_super', true),
      supabase.from('matches').select('id', { count:'exact', head:true }),
      supabase.from('matches').select('id', { count:'exact', head:true }).eq('status','accepted'),
      supabase.from('likes').select('to_user').limit(5000),
      supabase.from('matches').select('created_at,compat_score,status,ua:user_core!user_a(name),ub:user_core!user_b(name)')
        .order('created_at', { ascending:false }).limit(50),
    ]).then(async ([totalLikes, superLikes, totalMatches, accepted, likesRaw, recentMatches]) => {
      // Enrich likes with user names + network_id via users view
      let um = {};
      if (likesRaw?.data?.length) {
        const toUsers = [...new Set(likesRaw.data.map(l => l.to_user).filter(Boolean))];
        const { data: udata } = await supabase.from('users').select('id,name,network_id').in('id', toUsers);
        um = Object.fromEntries((udata || []).map(u => [u.id, u]));
      }
      // Top liked profiles
      const freq = {};
      (likesRaw?.data || []).forEach(l => {
        const u = um[l.to_user];
        const name = u?.name || l.to_user;
        if (!networkId || u?.network_id === networkId)
          freq[name] = (freq[name] || 0) + 1;
      });
      const topLiked = Object.entries(freq).sort((a,b)=>b[1]-a[1]).slice(0,15).map(([name,count])=>({name,count}));
      setStats({
        totalLikes:   totalLikes?.count  || 0,
        superLikes:   superLikes?.count  || 0,
        totalMatches: totalMatches?.count || 0,
        accepted:     accepted?.count    || 0,
        matchRate:    totalLikes?.count > 0 ? ((totalMatches?.count||0) / totalLikes.count * 100).toFixed(1) : 0,
      });
      setLikes(topLiked);
      setMatches(recentMatches?.data || []);
      setLoading(false);
    });
  }, [networkId]);

  if (loading) return <SkeletonTable cols={4} rows={6}/>;
  return (
    <>
      <PageHeader title="Likes & Matches" sub="Core discovery loop health"/>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:12, marginBottom:24 }}>
        <KpiCard label="Total likes"    value={fmt(stats.totalLikes)}   color={C.accent}/>
        <KpiCard label="Super-likes"    value={fmt(stats.superLikes)}   color={C.warn}/>
        <KpiCard label="Matches"        value={fmt(stats.totalMatches)} color={C.blue}/>
        <KpiCard label="Accepted"       value={fmt(stats.accepted)}     color={C.green}/>
        <KpiCard label="Match rate"     value={`${stats.matchRate}%`}   color={C.text} sub="likes → match"/>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
        {/* Top liked */}
        <div style={{ background:C.surface, border:`0.5px solid ${C.border}`, borderRadius:C.radiusLg, padding:16 }}>
          <p style={{ fontSize:13, fontWeight:600, color:C.text, margin:'0 0 12px' }}>Most liked profiles</p>
          {likes.length === 0 ? <p style={{color:C.muted,fontSize:12}}>No data</p> : likes.map((l,i) => {
            const pct = likes[0].count > 0 ? l.count / likes[0].count * 100 : 0;
            return (
              <div key={i} style={{ marginBottom:6 }}>
                <div style={{ display:'flex', justifyContent:'space-between', fontSize:12, marginBottom:2 }}>
                  <span style={{ color:C.text }}>{l.name}</span>
                  <span style={{ color:C.muted }}>{l.count} likes</span>
                </div>
                <div style={{ height:4, background:C.bg, borderRadius:2 }}>
                  <div style={{ width:`${pct}%`, height:'100%', background:C.accent, borderRadius:2 }}/>
                </div>
              </div>
            );
          })}
        </div>

        {/* Recent matches */}
        <div style={{ background:C.surface, border:`0.5px solid ${C.border}`, borderRadius:C.radiusLg, padding:16 }}>
          <p style={{ fontSize:13, fontWeight:600, color:C.text, margin:'0 0 12px' }}>Recent matches</p>
          <DataTable
            columns={[
              { key:'ua', label:'User A', render: v => v?.name || '—' },
              { key:'ub', label:'User B', render: v => v?.name || '—' },
              { key:'compat_score', label:'Score', render: v => v != null ? <Badge color="gold">{Number(v).toFixed(0)}%</Badge> : '—' },
              { key:'status',       label:'Status', render: v => <Badge color={v==='accepted'?'green':v==='declined'?'red':'default'}>{v}</Badge> },
              { key:'created_at',   label:'When',  render: v => ago(v) },
            ]}
            rows={matches}
            emptyMsg="No matches yet"
          />
        </div>
      </div>
    </>
  );
}

// ── Conversations / Messages moderation ───────────────────────────────────
function ConversationsAdminTab({ networkIdsJson }) {
  const [allConvos,   setAllConvos]   = useState([]);
  const [msgs,        setMsgs]        = useState([]);
  const [selected,    setSelected]    = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [search,      setSearch]      = useState('');
  const [msgHits,     setMsgHits]     = useState([]); // conversation_ids from message-content search
  const [searching,   setSearching]   = useState(false);

  useEffect(() => {
    const networkIds = networkIdsJson ? JSON.parse(networkIdsJson) : null;
    setLoading(true);
    supabase?.from('conversations')
      .select('id,mode,last_message,last_message_at,unread_a,unread_b,user_a,user_b')
      .order('last_message_at', { ascending:false }).limit(200)
      .then(async ({ data: convs }) => {
        if (!convs?.length) { setAllConvos([]); setLoading(false); return; }
        const ids = [...new Set(convs.flatMap(c => [c.user_a, c.user_b].filter(Boolean)))];
        const { data: udata } = await supabase.from('users').select('id,name,network_id').in('id', ids);
        const um = Object.fromEntries((udata || []).map(u => [u.id, u]));
        const enriched = convs.map(c => ({ ...c, ua: um[c.user_a]||null, ub: um[c.user_b]||null }));
        setAllConvos(networkIds ? enriched.filter(c => networkIds.includes(c.ua?.network_id) || networkIds.includes(c.ub?.network_id)) : enriched);
        setLoading(false);
      });
  }, [networkIdsJson]);

  // Search through message content when term >= 3 chars
  useEffect(() => {
    if (search.length < 3) { setMsgHits([]); return; }
    setSearching(true);
    supabase?.from('messages')
      .select('conversation_id')
      .ilike('text', `%${search}%`)
      .limit(200)
      .then(({ data }) => {
        setMsgHits([...new Set((data||[]).map(m => m.conversation_id))]);
        setSearching(false);
      });
  }, [search]);

  const openConvo = (c) => {
    setSelected(c);
    setMsgs([]);
    supabase?.from('messages')
      .select('id,text,status,created_at,sender:user_core!sender_id(name)')
      .eq('conversation_id', c.id)
      .order('created_at', { ascending:false }).limit(50)
      .then(({ data }) => setMsgs((data || []).reverse()));
  };

  const deleteMsg = async (msgId) => {
    await supabase?.from('messages').delete().eq('id', msgId);
    setMsgs(prev => prev.filter(m => m.id !== msgId));
  };

  const q = search.toLowerCase();
  const filtered = search.length < 3
    ? (q ? allConvos.filter(c =>
        (c.ua?.name||'').toLowerCase().includes(q) ||
        (c.ub?.name||'').toLowerCase().includes(q) ||
        (c.last_message||'').toLowerCase().includes(q)
      ) : allConvos)
    : allConvos.filter(c =>
        (c.ua?.name||'').toLowerCase().includes(q) ||
        (c.ub?.name||'').toLowerCase().includes(q) ||
        msgHits.includes(c.id)
      );

  const convos = filtered; // alias for template

  return (
    <>
      <PageHeader title="Messages" sub="Recherche dans les conversations et le contenu des messages" />
      <div style={{ display:'grid', gridTemplateColumns:'320px 1fr', gap:16, height:'calc(100vh - 140px)', minHeight:400 }}>
        {/* Conversation list */}
        <div style={{ background:C.surface, border:`0.5px solid ${C.border}`, borderRadius:C.radiusLg, display:'flex', flexDirection:'column', overflow:'hidden' }}>
          <div style={{ padding:10, borderBottom:`0.5px solid ${C.border}` }}>
            <SearchBar value={search} onChange={e=>setSearch(e.target.value)} placeholder="Nom ou mot-clé dans les messages…"/>
            {searching && <p style={{fontSize:10,color:C.muted,margin:'4px 0 0',padding:'0 2px'}}>Recherche en cours…</p>}
            {search.length >= 3 && !searching && <p style={{fontSize:10,color:C.accent,margin:'4px 0 0',padding:'0 2px'}}>{convos.length} conversation{convos.length!==1?'s':''} — {msgHits.length} via contenu</p>}
          </div>
          <div style={{ overflowY:'auto', flex:1 }}>
            {loading ? <p style={{padding:16,color:C.muted,fontSize:12}}>Loading…</p> : convos.length === 0 ? <p style={{padding:16,color:C.muted,fontSize:12}}>No conversations</p> : convos.map(c => (
              <button key={c.id} onClick={() => openConvo(c)}
                style={{ width:'100%', textAlign:'left', padding:'10px 12px', border:'none', borderBottom:`0.5px solid ${C.border}`, background:selected?.id===c.id?C.bg:'transparent', cursor:'pointer' }}>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:2 }}>
                  <span style={{ fontSize:12, fontWeight:500, color:C.text }}>{c.ua?.name} · {c.ub?.name}</span>
                  <span style={{ fontSize:10, color:C.muted }}>{c.last_message_at ? ago(c.last_message_at) : '—'}</span>
                </div>
                <p style={{ margin:0, fontSize:11, color:C.muted, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                  {msgHits.includes(c.id) && search.length>=3 ? <span style={{color:C.accent}}>💬 contient «{search}» · </span> : ''}{c.last_message || 'No message'}
                </p>
                {(c.unread_a > 0 || c.unread_b > 0) && (
                  <Badge color="blue">{c.unread_a + c.unread_b} unread</Badge>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Message thread */}
        <div style={{ background:C.surface, border:`0.5px solid ${C.border}`, borderRadius:C.radiusLg, display:'flex', flexDirection:'column', overflow:'hidden' }}>
          {!selected ? (
            <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100%', color:C.muted, fontSize:13 }}>Select a conversation</div>
          ) : (
            <>
              <div style={{ padding:'10px 14px', borderBottom:`0.5px solid ${C.border}`, display:'flex', alignItems:'center', gap:8 }}>
                <span style={{ fontSize:13, fontWeight:600, color:C.text }}>{selected.ua?.name} ↔ {selected.ub?.name}</span>
                <Badge color="default">{selected.mode}</Badge>
              </div>
              <div style={{ flex:1, overflowY:'auto', padding:12, display:'flex', flexDirection:'column', gap:6 }}>
                {msgs.length === 0 ? <p style={{color:C.muted,fontSize:12,textAlign:'center',marginTop:32}}>Loading messages…</p> : msgs.map(m => {
                  const isMatch = search.length >= 3 && m.text?.toLowerCase().includes(search.toLowerCase());
                  return (
                    <div key={m.id} style={{ display:'flex', gap:8, alignItems:'flex-start' }}>
                      <div style={{ flex:1, background:isMatch?'rgba(201,168,76,0.08)':C.bg, borderRadius:C.radius, padding:'7px 10px', fontSize:12, border:isMatch?`0.5px solid ${C.accent}40`:'none' }}>
                        <span style={{ fontWeight:600, color:C.sub, marginRight:6 }}>{m.sender?.name || '?'}</span>
                        <span style={{ color:C.text }}>{search.length >= 3 ? highlightText(m.text) : m.text}</span>
                        <span style={{ fontSize:10, color:C.muted, marginLeft:8 }}>{ago(m.created_at)}</span>
                      </div>
                      <IconBtn title="Delete" danger onClick={() => deleteMsg(m.id)}>✕</IconBtn>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}

// ── Referrals admin — friend suggestions ─────────────────────────────────
function ReferralsAdminTab({ networkIdsJson, networkId }) {
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter,  setFilter]  = useState('all');

  useEffect(() => {
    setLoading(true);
    supabase?.from('referrals')
      .select('id,referrer_id,referred_id,profile_id,message,created_at,seen_at')
      .order('created_at', { ascending:false }).limit(200)
      .then(async ({ data: refs }) => {
        if (!refs?.length) { setRows([]); setLoading(false); return; }
        const ids = [...new Set(refs.flatMap(r => [r.referrer_id, r.referred_id, r.profile_id].filter(Boolean)))];
        const { data: users } = await supabase.from('users').select('id,name,age,network_id').in('id', ids);
        const um = Object.fromEntries((users || []).map(u => [u.id, u]));
        const networkIds = networkIdsJson ? JSON.parse(networkIdsJson) : null;
        const enriched = refs.map(r => ({
          ...r,
          referrer: um[r.referrer_id] || null,
          referred: um[r.referred_id] || null,
          profile:  um[r.profile_id]  || null,
        }));
        setRows(networkIds ? enriched.filter(r => networkIds.includes(r.referrer?.network_id) || networkIds.includes(r.referred?.network_id)) : enriched);
        setLoading(false);
      });
  }, [networkIdsJson]);

  const filtered = rows.filter(r => filter === 'all' || (filter === 'unseen' && !r.seen_at) || (filter === 'seen' && r.seen_at));
  const seenRate = rows.length > 0 ? Math.round(rows.filter(r=>r.seen_at).length / rows.length * 100) : 0;

  return (
    <>
      <PageHeader title="Referrals" sub="Friend profile suggestions"/>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12, marginBottom:20 }}>
        <KpiCard label="Total sent"  value={fmt(rows.length)}                                 color={C.text}/>
        <KpiCard label="Seen"        value={fmt(rows.filter(r=>r.seen_at).length)}             color={C.green} sub={`${seenRate}% open rate`}/>
        <KpiCard label="Unseen"      value={fmt(rows.filter(r=>!r.seen_at).length)}            color={C.warn}/>
      </div>
      <div style={{ display:'flex', gap:10, marginBottom:12 }}>
        <Select value={filter} onChange={e=>setFilter(e.target.value)} options={[
          {value:'all',label:'All'},{value:'unseen',label:'Unseen'},{value:'seen',label:'Seen'}
        ]}/>
      </div>
      {loading ? <SkeletonTable cols={5} rows={6}/> : (
        <DataTable
          columns={[
            { key:'referrer', label:'From',    render: v => v ? <div><p style={{margin:0,fontSize:13}}>{v.name}</p>{!networkId && v.network_id && <p style={{margin:0,fontSize:10,color:C.muted}}>{netCity(v.network_id)}</p>}</div> : '—' },
            { key:'referred', label:'To',      render: v => v?.name || '—' },
            { key:'profile',  label:'Profile', render: v => v ? `${v.name}, ${v.age||'?'}` : '—' },
            { key:'message',  label:'Message', render: v => v ? <span title={v} style={{ maxWidth:200, overflow:'hidden', textOverflow:'ellipsis', display:'block', whiteSpace:'nowrap', fontSize:11, color:C.muted }}>{v}</span> : <span style={{color:C.muted,fontSize:11}}>—</span> },
            { key:'seen_at',  label:'Status',  render: v => <Badge color={v?'green':'amber'}>{v?'Seen':'Unseen'}</Badge> },
            { key:'created_at',label:'Sent',   render: v => ago(v) },
          ]}
          rows={filtered}
          emptyMsg="No referrals yet"
        />
      )}
    </>
  );
}

// ── Quiz Distribution — axis response histograms ──────────────────────────
function QuizDistributionTab({ networkId }) {
  const [answers, setAnswers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let q = db.users()?.select('quiz_answers,network_id').not('quiz_answers', 'is', null).limit(2000);
    if (networkId) q = q?.eq('network_id', networkId);
    q?.then(({ data }) => { setAnswers((data||[]).map(u=>u.quiz_answers).filter(Boolean)); setLoading(false); });
  }, [networkId]);

  if (loading) return <SkeletonTable cols={4} rows={5}/>;
  const total = answers.length;
  if (!total) return <><PageHeader title="Quiz Distribution" sub="No quiz data yet"/></>;

  const dist = QUIZ_AXES.map(axis => {
    const counts = [0,0,0,0];
    answers.forEach(qa => { const v = qa[axis]; if (v>=1&&v<=4) counts[v-1]++; });
    const filled = counts.reduce((s,c)=>s+c,0);
    const avg    = filled > 0 ? (counts.reduce((s,c,i)=>s+c*(i+1),0)/filled).toFixed(2) : '—';
    return { axis, counts, filled, avg };
  });
  const maxBar = 60;

  return (
    <>
      <PageHeader title="Quiz Distribution" sub={`${total} users with quiz answers`}/>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:16 }}>
        {dist.map(({ axis, counts, filled, avg }) => {
          const peak = Math.max(...counts, 1);
          return (
            <div key={axis} style={{ background:C.surface, border:`0.5px solid ${C.border}`, borderRadius:C.radiusLg, padding:16 }}>
              <div style={{ display:'flex', justifyContent:'space-between', marginBottom:10 }}>
                <span style={{ fontSize:13, fontWeight:600, color:C.text, textTransform:'capitalize' }}>{axis}</span>
                <span style={{ fontSize:11, color:C.muted }}>avg <strong style={{color:C.accent}}>{avg}</strong> · {filled}/{total} answered</span>
              </div>
              <div style={{ display:'flex', alignItems:'flex-end', gap:8, height:maxBar }}>
                {counts.map((count, i) => {
                  const barH = Math.max(4, (count/peak)*maxBar);
                  const rowPct = filled > 0 ? Math.round(count/filled*100) : 0;
                  return (
                    <div key={i} style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:3 }}>
                      <span style={{ fontSize:10, color:C.muted }}>{rowPct}%</span>
                      <div title={`${count} users`}
                        style={{ width:'100%', background:i===1||i===2?C.accent:'rgba(201,168,76,0.4)', borderRadius:'3px 3px 0 0', height:barH }}/>
                      <span style={{ fontSize:11, fontWeight:500, color:C.sub }}>{i+1}</span>
                    </div>
                  );
                })}
              </div>
              <div style={{ marginTop:6, height:4, background:C.bg, borderRadius:2 }}>
                <div style={{ width:`${Math.round(filled/total*100)}%`, height:'100%', background:C.green, borderRadius:2 }}/>
              </div>
              <p style={{ fontSize:10, color:C.muted, margin:'3px 0 0' }}>{Math.round(filled/total*100)}% of users answered this axis</p>
            </div>
          );
        })}
      </div>
    </>
  );
}

// ── Gamification — Hive leaderboard + badge audit ─────────────────────────
function GamificationTab({ users }) {
  const topScorers = [...users].sort((a,b) => (b.score||0) - (a.score||0)).slice(0, 20);
  const topVotes   = [...users].sort((a,b) => (b.votes||0)  - (a.votes||0)).slice(0, 20);
  const badgeDefs  = getBadges('en');

  // Badge audit: count how many of the admin users would earn each badge
  const badgeStats = badgeDefs.map(b => {
    const earned = users.filter(u => {
      try { return b.condition(u, {}); } catch { return false; }
    }).length;
    return { ...b, earned, pct: users.length > 0 ? Math.round(earned/users.length*100) : 0 };
  });

  return (
    <>
      <PageHeader title="Gamification" sub="Hive score leaderboard & badge audit"/>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:24 }}>
        <div style={{ background:C.surface, border:`0.5px solid ${C.border}`, borderRadius:C.radiusLg, padding:16 }}>
          <p style={{ fontSize:13, fontWeight:600, color:C.text, margin:'0 0 12px' }}>Top Hive scores</p>
          {topScorers.map((u,i) => (
            <div key={u.id} style={{ display:'flex', alignItems:'center', gap:8, padding:'5px 0', borderBottom:`0.5px solid ${C.border}` }}>
              <span style={{ fontSize:11, color:C.muted, width:18, textAlign:'right' }}>#{i+1}</span>
              <span style={{ flex:1, fontSize:12, fontWeight:500, color:C.text }}>{u.name}</span>
              <Badge color="gold">{u.score||0}</Badge>
              <span style={{ fontSize:10, color:C.muted }}>{u.votes||0} votes</span>
            </div>
          ))}
        </div>
        <div style={{ background:C.surface, border:`0.5px solid ${C.border}`, borderRadius:C.radiusLg, padding:16 }}>
          <p style={{ fontSize:13, fontWeight:600, color:C.text, margin:'0 0 12px' }}>Most rated (votes)</p>
          {topVotes.map((u,i) => (
            <div key={u.id} style={{ display:'flex', alignItems:'center', gap:8, padding:'5px 0', borderBottom:`0.5px solid ${C.border}` }}>
              <span style={{ fontSize:11, color:C.muted, width:18, textAlign:'right' }}>#{i+1}</span>
              <span style={{ flex:1, fontSize:12, fontWeight:500, color:C.text }}>{u.name}</span>
              <span style={{ fontSize:12, color:C.accent, fontWeight:600 }}>{u.votes||0}</span>
              <span style={{ fontSize:10, color:C.muted }}>votes</span>
            </div>
          ))}
        </div>
      </div>

      <div style={{ background:C.surface, border:`0.5px solid ${C.border}`, borderRadius:C.radiusLg, padding:16 }}>
        <p style={{ fontSize:13, fontWeight:600, color:C.text, margin:'0 0 12px' }}>Badge audit ({users.length} users in scope)</p>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:8 }}>
          {badgeStats.map(b => (
            <div key={b.id} style={{ padding:'8px 10px', background:C.bg, borderRadius:C.radius }}>
              <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
                <span style={{ fontSize:12, fontWeight:500, color:C.text }}>{b.name}</span>
                <span style={{ fontSize:12, color:C.accent, fontWeight:600 }}>{b.pct}%</span>
              </div>
              <div style={{ height:4, background:C.border, borderRadius:2 }}>
                <div style={{ width:`${b.pct}%`, height:'100%', background:C.accent, borderRadius:2 }}/>
              </div>
              <p style={{ fontSize:10, color:C.muted, margin:'3px 0 0' }}>{b.earned} / {users.length} earned · {b.pillar}</p>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

// ── Profile Completion Funnel ─────────────────────────────────────────────
function FunnelTab({ networkId }) {
  const [users,   setUsers]   = useState([]);
  const [loading, setLoading] = useState(true);
  const steps = getCompletionSteps('en');

  useEffect(() => {
    let q = db.users()?.select('photos,bio,prompt1_q,prompt1_a,prompt2_q,prompt2_a,interests,quiz_answers,votes,age,height,education,network_id').limit(2000);
    if (networkId) q = q?.eq('network_id', networkId);
    q?.then(({ data }) => { setUsers(data || []); setLoading(false); });
  }, [networkId]);

  if (loading) return <SkeletonTable cols={3} rows={7}/>;
  const total = users.length;
  if (!total) return <><PageHeader title="Completion Funnel" sub="No users in scope"/></>;

  const stepStats = steps.map(s => {
    const done = users.filter(u => { try { return s.check(u); } catch { return false; } }).length;
    return { ...s, done, pct: Math.round(done/total*100) };
  });

  // Sort by pct desc so we see bottlenecks at the bottom
  const sorted = [...stepStats].sort((a,b) => b.pct - a.pct);

  return (
    <>
      <PageHeader title="Completion Funnel" sub={`${total} users in scope`}/>
      <div style={{ background:C.surface, border:`0.5px solid ${C.border}`, borderRadius:C.radiusLg, padding:24, maxWidth:700 }}>
        {sorted.map((s, i) => (
          <div key={s.id} style={{ marginBottom:16 }}>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:5 }}>
              <div>
                <span style={{ fontSize:13, fontWeight:500, color:C.text }}>{s.label}</span>
                <span style={{ fontSize:11, color:C.muted, marginLeft:8 }}>+{s.points} pts</span>
              </div>
              <div style={{ textAlign:'right' }}>
                <span style={{ fontSize:13, fontWeight:600, color: s.pct >= 70 ? C.green : s.pct >= 40 ? C.warn : C.red }}>{s.pct}%</span>
                <span style={{ fontSize:11, color:C.muted, marginLeft:6 }}>{s.done}/{total}</span>
              </div>
            </div>
            <div style={{ height:10, background:C.bg, borderRadius:5, overflow:'hidden' }}>
              <div style={{
                width:`${s.pct}%`, height:'100%', borderRadius:5, transition:'width 0.5s',
                background: s.pct >= 70 ? C.green : s.pct >= 40 ? C.warn : C.red,
              }}/>
            </div>
          </div>
        ))}

        <div style={{ marginTop:20, paddingTop:16, borderTop:`0.5px solid ${C.border}` }}>
          <p style={{ fontSize:12, color:C.sub, margin:0 }}>
            🏆 Fully complete (all steps): <strong style={{ color:C.green }}>{users.filter(u => steps.every(s => { try{return s.check(u);}catch{return false;} })).length}</strong> users ({Math.round(users.filter(u => steps.every(s=>{try{return s.check(u);}catch{return false;}})).length/total*100)}%)
          </p>
        </div>
      </div>
    </>
  );
}

// ── Usage & Boosts ────────────────────────────────────────────────────────
function UsageTab({ networkId }) {
  const [usage,   setUsage]   = useState([]);
  const [boosts,  setBoosts]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState('');
  const today = new Date().toISOString().slice(0, 10);

  useEffect(() => {
    if (!supabase) return;
    Promise.all([
      supabase.from('user_daily_usage')
        .select('user_id,date,likes_count,super_likes_count,passes_count')
        .eq('date', today).order('likes_count', { ascending:false }).limit(100),
      supabase.from('user_boosts')
        .select('user_id,activated_at,expires_at,multiplier,u:user_core!user_id(name,plan)')
        .gt('expires_at', new Date().toISOString()).order('expires_at', { ascending:true }).limit(50),
    ]).then(async ([u, b]) => {
      const usageRows = u?.data || [];
      if (usageRows.length) {
        const ids = [...new Set(usageRows.map(r => r.user_id).filter(Boolean))];
        const { data: udata } = await supabase.from('users').select('id,name,plan,network_id').in('id', ids);
        const um = Object.fromEntries((udata || []).map(u => [u.id, u]));
        setUsage(usageRows.map(r => ({ ...r, u: um[r.user_id]||null })));
      } else {
        setUsage([]);
      }
      setBoosts(b?.data || []);
      setLoading(false);
    });
  }, [networkId, today]);

  const filteredUsage = usage.filter(r =>
    (!networkId || r.u?.network_id === networkId) &&
    (!search || (r.u?.name||'').toLowerCase().includes(search.toLowerCase()))
  );
  const totalLikesToday = filteredUsage.reduce((s,r)=>s+(r.likes_count||0), 0);
  const totalPassesToday = filteredUsage.reduce((s,r)=>s+(r.passes_count||0), 0);

  if (loading) return <SkeletonTable cols={4} rows={8}/>;
  return (
    <>
      <PageHeader title="Usage & Boosts" sub={`Today (${today}) — active boosts: ${boosts.length}`}/>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12, marginBottom:20 }}>
        <KpiCard label="Active users today" value={fmt(filteredUsage.length)}  color={C.text}/>
        <KpiCard label="Likes today"        value={fmt(totalLikesToday)}        color={C.accent}/>
        <KpiCard label="Passes today"       value={fmt(totalPassesToday)}       color={C.muted}/>
      </div>

      {boosts.length > 0 && (
        <div style={{ background:C.warnBg, border:`0.5px solid ${C.warn}`, borderRadius:C.radiusLg, padding:14, marginBottom:20 }}>
          <p style={{ fontSize:13, fontWeight:600, color:C.warn, margin:'0 0 8px' }}>⚡ Active boosts ({boosts.length})</p>
          <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
            {boosts.map(b => (
              <div key={b.user_id} style={{ background:C.surface, borderRadius:C.radius, padding:'5px 10px', fontSize:12, border:`0.5px solid ${C.warn}` }}>
                <strong style={{color:C.text}}>{b.u?.name}</strong>
                <span style={{ color:C.muted, marginLeft:6 }}>×{b.multiplier} · expires {ago(b.expires_at)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ display:'flex', gap:10, marginBottom:12 }}>
        <SearchBar value={search} onChange={e=>setSearch(e.target.value)} placeholder="Filter by name…"/>
      </div>
      <DataTable
        columns={[
          { key:'u',               label:'User',       render: v => <span style={{fontWeight:500}}>{v?.name||'—'}</span> },
          { key:'u',               label:'Plan',       render: v => <Badge color={v?.plan==='royal'?'gold':v?.plan==='honey'?'amber':'default'}>{v?.plan||'free'}</Badge> },
          { key:'likes_count',     label:'Likes',      render: v => <span style={{color:C.accent,fontWeight:600}}>{v||0}</span> },
          { key:'super_likes_count',label:'Super-likes',render:v=><span style={{color:C.warn}}>{v||0}</span>},
          { key:'passes_count',    label:'Passes',     render: v => v||0 },
        ]}
        rows={filteredUsage}
        emptyMsg="No usage data for today"
      />
    </>
  );
}

// ── Admin Partnership Docs — wraps CML Marketing Kit PDF for admin use ───
function AdminPartnerDocs({ networkId, netUsers, netCml, netSponsors, netEvents }) {
  const defaultMgr = netCml?.[0] || {};
  const computedEventsHosted = (netEvents||[]).filter(e => e.status === 'past' || new Date(e.date) < new Date()).length;

  const [managerName,   setManagerName]   = useState('');
  const [stage,         setStage]         = useState('');
  const [eventsHosted,  setEventsHosted]  = useState('');

  // Re-seed form when the selected network changes
  useEffect(() => {
    setManagerName(defaultMgr.user?.name || '');
    setStage(defaultMgr.stage || 'ambassador');
    setEventsHosted(String(computedEventsHosted));
  }, [networkId, netCml?.length]);

  if (!networkId) {
    return (
      <div style={{ padding:'32px', textAlign:'center', color:C.muted }}>
        <p style={{ fontSize:14 }}>Select a city from the sidebar to generate the partnership kit for that network.</p>
      </div>
    );
  }

  const cml = {
    name:    managerName,
    network: networkId,
    stage:   stage || 'ambassador',
    sponsors: netSponsors || [],
    stats: {
      membersTotal:  (netUsers||[]).length,
      membersActive: Math.round((netUsers||[]).length * 0.74),
      membersNew:    0,
      eventsHosted:  parseInt(eventsHosted, 10) || 0,
      matchesMade:   0,
    },
    revenue: { month: {}, total: {}, pending: 0, nextPayoutDate: '—' },
  };

  return (
    <div>
      <PageHeader title="Partnership Kit" sub="Customize then generate the PDF to share with potential partners" />
      <div style={{ background:C.surface, border:`0.5px solid ${C.border}`, borderRadius:C.radiusLg, padding:'16px 20px', marginBottom:20, display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:14 }}>
        <FormField label="Manager name">
          <Input
            value={managerName}
            onChange={e => setManagerName(e.target.value)}
            placeholder="e.g. Marie Tremblay"
          />
        </FormField>
        <FormField label="Stage">
          <Select
            value={stage}
            onChange={e => setStage(e.target.value)}
            options={[
              {value:'ambassador',  label:'Ambassador'},
              {value:'coordinator', label:'Coordinator'},
              {value:'director',    label:'Director'},
            ]}
          />
        </FormField>
        <FormField label="Events hosted">
          <Input
            type="number"
            min={0}
            value={eventsHosted}
            onChange={e => setEventsHosted(e.target.value)}
          />
        </FormField>
      </div>
      <CMLTabMarketing cml={cml} networkUsers={netUsers || []} />
    </div>
  );
}

// ── Main Admin component ──────────────────────────────────────────────────
export default function Admin() {
  const { currentUser } = useApp();

  const [authed, setAuthed]           = useState(false);
  const [tab, setTab]                 = useState('dashboard');
  const [users, setUsers]             = useState([]);
  const [events, setEvents]           = useState([]);
  const [sponsors, setSponsors]       = useState([]);
  const [cmlList, setCmlList]         = useState([]);
  const [networks, setNetworks]       = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedNetwork, setSelectedNetwork] = useState(null);
  const [pendingReports,  setPendingReports]  = useState(0);
  const [circlesCount,    setCirclesCount]    = useState(0);
  const [waitlistPending, setWaitlistPending] = useState(0);

  // Global search
  const [globalSearch, setGlobalSearch] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [jumpToUser, setJumpToUser] = useState(null);

  // Keyboard shortcut toast
  const [shortcutToast, setShortcutToast] = useState('');
  const gKeyRef = useRef(false);


  useEffect(() => {
    if (!authed || !supabase) return;
    Promise.all([
      db.users()?.select('id,email,name,age,gender,plan,role,verified,email_verified,city,country,network_id,created_at,score,votes,deleted_at').is('deleted_at',null).order('created_at',{ascending:false}).limit(500),
      db.events_full()?.select('*').order('date',{ascending:false}),
      db.sponsors()?.select('*').order('created_at',{ascending:false}),
      db.cml_managers()?.select('*,user:user_core!user_id(name,email)').order('joined_at',{ascending:false}),
      db.networks()?.select('*').order('label',{ascending:true}),
    ]).then(([u, e, s, c, n]) => {
      if (u?.data) setUsers(u.data);
      if (e?.data) setEvents(e.data);
      if (s?.data) setSponsors(s.data);
      if (c?.data) setCmlList(c.data);
      if (n?.data) setNetworks(n.data);
    });
    // Sidebar badge counts (network-agnostic)
    supabase?.from('reports').select('id', { count:'exact', head:true })
      .eq('status','pending')
      .then(({ count }) => setPendingReports(count || 0));
    supabase?.from('waitlist').select('id', { count:'exact', head:true })
      .eq('invite_sent', false)
      .then(({ count }) => setWaitlistPending(count || 0));
  }, [authed]);

  // Scouts badge — re-fetch when network filter changes
  useEffect(() => {
    if (!authed || !supabase) return;
    let q = supabase.from('scouts').select('id', { count:'exact', head:true });
    if (selectedNetwork) q = q.eq('network_id', selectedNetwork);
    q.then(({ count }) => setCirclesCount(count || 0));
  }, [authed, selectedNetwork]);

  // Keyboard shortcuts: G then key
  useEffect(() => {
    const SHORTCUTS = { d:'dashboard', u:'users', f:'features', e:'events_admin', m:'moderation', r:'revenue', i:'invitations', h:'hive_groups' };
    const down = (ev) => {
      if (ev.target.tagName === 'INPUT' || ev.target.tagName === 'TEXTAREA') return;
      if (ev.key === 'g' || ev.key === 'G') { gKeyRef.current = true; setTimeout(() => { gKeyRef.current = false; }, 1500); return; }
      if (gKeyRef.current && SHORTCUTS[ev.key.toLowerCase()]) {
        const dest = SHORTCUTS[ev.key.toLowerCase()];
        setTab(dest);
        const label = NAV_GROUPS.flatMap(g => g.items).find(i => i.id === dest)?.label || dest;
        setShortcutToast(`G → ${ev.key.toUpperCase()}  ·  ${label}`);
        setTimeout(() => setShortcutToast(''), 1800);
        gKeyRef.current = false;
      }
    };
    window.addEventListener('keydown', down);
    return () => window.removeEventListener('keydown', down);
  }, []);

  if (!currentUser || currentUser.role !== 'admin') {
    return (
      <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:C.bg }}>
        <p style={{ color:C.red, fontSize:14 }}>Access denied — admin role required.</p>
      </div>
    );
  }
  if (!authed) return <PinGate onUnlock={() => setAuthed(true)} />;

  // Derive the set of network ids matching the current country+city selection
  const countryNetIds = selectedCountry
    ? new Set(networks.filter(n => n.country === selectedCountry).map(n => n.id))
    : null;
  // nid = specific city selected; countryNetIds = all cities in selected country
  const nid = selectedNetwork; // passed to tabs that do their own queries
  // networkIdsJson: stable string dep for useEffect arrays inside tabs
  // null = no filter; otherwise JSON array of matching network ids
  const networkIdsJson = nid ? JSON.stringify([nid])
    : countryNetIds    ? JSON.stringify([...countryNetIds])
    : null;
  const filterByNet = (items, key = 'network_id') => {
    if (nid)            return items.filter(x => x[key] === nid);
    if (countryNetIds)  return items.filter(x => countryNetIds.has(x[key]));
    return items;
  };
  const netUsers    = filterByNet(users);
  const netEvents   = filterByNet(events);
  const netCml      = filterByNet(cmlList);
  const netSponsors = filterByNet(sponsors);

  const renderContent = () => {
    switch (tab) {
      case 'categories':         return <CategoryAudiencesTab users={netUsers} />;
      case 'dashboard':          return <DashboardTab users={netUsers} events={netEvents} />;
      case 'growth':             return <GrowthTab networkId={nid} setTab={setTab} />;
      case 'revenue':            return <RevenueTab networkId={nid} networkIdsJson={networkIdsJson} />;
      case 'invitations':        return <InvitationsTab networkId={nid} networkIdsJson={networkIdsJson} />;
      case 'users':              return <UsersTab users={netUsers} setUsers={setUsers} jumpToUser={jumpToUser} networkId={nid} />;
      case 'cml':                return <CMLTab cmlList={netCml} setCmlList={setCmlList} networkId={nid} />;
      case 'moderation':         return <ModerationTab networkId={nid} networkIdsJson={networkIdsJson} />;
      case 'events_admin':       return <EventsTab events={netEvents} setEvents={setEvents} currentUser={currentUser} networkId={nid} />;
      case 'scout_admin':      return <ScoutTab networkId={nid} />;
      case 'venues_admin':       return <VenuesTab networkId={nid} networkIdsJson={networkIdsJson} />;
      case 'notifications_admin':return <NotificationsAdminTab networkId={nid} networkIdsJson={networkIdsJson} />;
      case 'sponsors':           return <SponsorsTab sponsors={netSponsors} setSponsors={setSponsors} networkId={nid} />;
      case 'countries':          return <CountriesTab />;
      case 'networks':           return <NetworksTab />;
      case 'monetization':       return <MonetizationTab selectedCountry={selectedCountry} selectedNetwork={nid} />;
      case 'features':           return <ControlCenterTab />;
      case 'appconfig':          return <AppConfigTab />;
      case 'hive_groups':        return <HiveGroupsTab networkId={nid} users={netUsers} />;
      case 'axes':               return <CompatibilityAxesTab />;
      case 'ab_tests':           return <ABTestsTab />;
      case 'translations':       return <><PageHeader title="Translations" sub="Edit UI strings without deploying"/><TranslationsTab /></>;
      case 'content':            return <><PageHeader title="Site text" sub="Static page content"/><StaticPagesTab /></>;
      case 'discovery_loop':     return <DiscoveryLoopTab networkId={nid} />;
      case 'conversations_admin':return <ConversationsAdminTab networkIdsJson={networkIdsJson} />;
      case 'referrals_admin':    return <ReferralsAdminTab networkIdsJson={networkIdsJson} networkId={nid} />;
      case 'quiz_dist':          return <QuizDistributionTab networkId={nid} />;
      case 'gamification':       return <GamificationTab users={netUsers} />;
      case 'funnel':             return <FunnelTab networkId={nid} />;
      case 'usage':              return <UsageTab networkId={nid} />;
      case 'partnership_docs':   return <AdminPartnerDocs networkId={nid} netUsers={netUsers} netCml={netCml} netSponsors={netSponsors} netEvents={netEvents} />;
      default:                   return <p style={{ color:C.muted }}>Coming soon</p>;
    }
  };

  // Global search — nav items + users
  const allNavItems = NAV_GROUPS.flatMap(g => g.items);
  const BADGES = {
    users:         netUsers.length,
    events_admin:  netEvents.length,
    scout_admin: circlesCount,
    cml:           netCml.length,
    moderation:    pendingReports,
    sponsors:      netSponsors.length,
    invitations:   waitlistPending,
    hive_groups:   loadHiveGroups().filter(g => g.enabled !== false).length,
  };

  const searchResults = globalSearch.trim().length >= 1 ? [
    ...allNavItems.filter(i => i.label.toLowerCase().includes(globalSearch.toLowerCase())).slice(0,4).map(i => ({ type:'nav', ...i })),
    ...users.filter(u => (u.name||'').toLowerCase().includes(globalSearch.toLowerCase()) || (u.email||'').toLowerCase().includes(globalSearch.toLowerCase())).slice(0,5).map(u => ({ type:'user', ...u })),
  ] : [];

  const NavBtn = ({ item, indent = false }) => {
    const badge = BADGES[item.id];
    return (
      <button onClick={() => { setTab(item.id); setGlobalSearch(''); }}
        style={{
          width:'100%', display:'flex', alignItems:'center', gap:8, padding:'7px 8px',
          borderRadius:C.radius, border:'none', cursor:'pointer', textAlign:'left', fontSize:13,
          background: tab===item.id ? 'rgba(201,168,76,0.12)' : 'transparent',
          color:      tab===item.id ? C.accent : C.sub,
          fontWeight: tab===item.id ? 500 : 400,
          transition: 'background 0.1s, color 0.1s',
          paddingLeft: indent ? 14 : 8,
        }}>
        <NavIcon path={item.icon} />
        <span style={{ flex:1 }}>{item.label}</span>
        {badge > 0 && (
          <span style={{ fontSize:10, fontWeight:700, padding:'1px 5px', borderRadius:99,
            background: item.id === 'moderation' ? C.red : C.accent,
            color: '#fff', lineHeight:1.5, flexShrink:0 }}>
            {badge > 99 ? '99+' : badge}
          </span>
        )}
      </button>
    );
  };

  return (
    <div style={{ display:'flex', minHeight:'100vh', background:C.bg }}>
      {/* ── Sidebar ── */}
      <aside style={{ width:224, flexShrink:0, background:C.surface, borderRight:`0.5px solid ${C.border}`, display:'flex', flexDirection:'column', position:'sticky', top:0, height:'100vh', overflowY:'auto' }}>
        {/* Logo + network filter */}
        <div style={{ padding:'14px 16px 10px', borderBottom:`0.5px solid ${C.border}` }}>
          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10 }}>
            <span style={{ fontSize:20 }}>🐝</span>
            <div>
              <p style={{ fontSize:13, fontWeight:600, color:C.text, margin:0 }}>BeeLove</p>
              <p style={{ fontSize:11, color:C.muted, margin:0 }}>Admin</p>
            </div>
          </div>
          <NetworkFilter
            networks={networks}
            selectedCountry={selectedCountry}
            selectedNetwork={selectedNetwork}
            onCountryChange={setSelectedCountry}
            onNetworkChange={setSelectedNetwork}
          />
        </div>

        {/* Global search */}
        <div style={{ padding:'8px 10px', borderBottom:`0.5px solid ${C.border}`, position:'relative' }}>
          <div style={{ position:'relative' }}>
            <svg style={{ position:'absolute', left:8, top:'50%', transform:'translateY(-50%)', pointerEvents:'none' }} width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={C.muted} strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
            <input
              value={globalSearch}
              onChange={e => setGlobalSearch(e.target.value)}
              onFocus={() => setSearchFocused(true)}
              onBlur={() => setTimeout(() => setSearchFocused(false), 150)}
              placeholder="Rechercher…  (G+D/U/F…)"
              style={{ width:'100%', padding:'6px 8px 6px 26px', border:`0.5px solid ${C.border}`, borderRadius:C.radius, fontSize:12, color:C.text, background:C.bg, outline:'none', boxSizing:'border-box' }}
            />
            {globalSearch && <button onClick={() => setGlobalSearch('')} style={{ position:'absolute', right:6, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', color:C.muted, cursor:'pointer', fontSize:14, lineHeight:1, padding:2 }}>×</button>}
          </div>
          {/* Search dropdown */}
          {searchFocused && searchResults.length > 0 && (
            <div style={{ position:'absolute', left:10, right:10, top:'100%', zIndex:200, background:C.surface, border:`0.5px solid ${C.border}`, borderRadius:C.radiusLg, boxShadow:'0 4px 16px rgba(0,0,0,0.1)', overflow:'hidden', marginTop:4 }}>
              {searchResults.map((r, i) => (
                <button key={i}
                  onClick={() => {
                    if (r.type === 'nav') {
                      setTab(r.id);
                    } else {
                      setTab('users');
                      setJumpToUser(r.id);
                      setTimeout(() => setJumpToUser(null), 500); // reset after effect fires
                    }
                    setGlobalSearch('');
                    setSearchFocused(false);
                  }}
                  style={{ width:'100%', display:'flex', alignItems:'center', gap:8, padding:'8px 12px', border:'none', background:'transparent', cursor:'pointer', textAlign:'left', borderBottom:`0.5px solid ${C.border}` }}
                  onMouseEnter={e => e.currentTarget.style.background = C.bg}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <span style={{ fontSize:11, color:C.muted, width:28, flexShrink:0 }}>{r.type === 'nav' ? '↗' : '👤'}</span>
                  <div>
                    <p style={{ margin:0, fontSize:12, fontWeight:500, color:C.text }}>{r.type === 'nav' ? r.label : r.name}</p>
                    {r.type === 'user' && <p style={{ margin:0, fontSize:11, color:C.muted }}>{r.email}</p>}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Nav groups */}
        <nav style={{ flex:1, padding:'8px 10px', overflowY:'auto', display:'flex', flexDirection:'column' }}>
          {NAV_GROUPS.filter(g => g.scope !== 'global').map(group => {
            const activeNet = selectedNetwork ? networks.find(n => n.id === selectedNetwork) : null;
            const filterLabel = selectedCountry
              ? `${COUNTRY_FLAGS[selectedCountry] || ''} ${activeNet?.label || COUNTRY_LABELS[selectedCountry] || selectedCountry}`.trim()
              : null;
            return (
              <div key={group.label} style={{ marginBottom:4 }}>
                <div style={{ display:'flex', alignItems:'center', gap:4, padding:'8px 6px 4px' }}>
                  <span style={{ fontSize:10, fontWeight:600, color:C.muted, letterSpacing:'0.08em', textTransform:'uppercase', flex:1 }}>{group.label}</span>
                  {filterLabel
                    ? <span style={{ fontSize:9, color:C.accent, background:'rgba(201,168,76,0.13)', border:`0.5px solid rgba(201,168,76,0.3)`, padding:'1px 5px', borderRadius:99, fontWeight:600, whiteSpace:'nowrap' }}>{filterLabel}</span>
                    : <span title="Vue filtrée par pays / ville" style={{ fontSize:10, color:C.muted, opacity:0.35, lineHeight:1 }}>⌖</span>
                  }
                </div>
                {group.items.map(item => <NavBtn key={item.id} item={item} />)}
              </div>
            );
          })}
          <div style={{ flex:1 }}/>
          {NAV_GROUPS.filter(g => g.scope === 'global').map(group => (
            <div key={group.label} style={{ borderTop:`0.5px solid ${C.border}`, paddingTop:4, marginTop:4 }}>
              <div style={{ display:'flex', alignItems:'center', gap:4, padding:'8px 6px 4px' }}>
                <span style={{ fontSize:10, fontWeight:600, color:C.muted, letterSpacing:'0.08em', textTransform:'uppercase', flex:1 }}>{group.label}</span>
                <span title="Non affecté par le filtre pays / ville" style={{ fontSize:9, color:C.muted, opacity:0.4 }}>🌐</span>
              </div>
              {group.items.map(item => <NavBtn key={item.id} item={item} />)}
            </div>
          ))}
        </nav>

        {/* Bottom user */}
        <div style={{ padding:'12px 14px', borderTop:`0.5px solid ${C.border}` }}>
          <p style={{ fontSize:12, fontWeight:500, color:C.text, margin:0 }}>{currentUser?.name||'Admin'}</p>
          <p style={{ fontSize:11, color:C.muted, margin:'2px 0 0' }}>{currentUser?.email}</p>
        </div>
      </aside>

      {/* ── Main content ── */}
      <main style={{ flex:1, minWidth:0, overflowY:'auto', maxHeight:'100vh' }}>
        {/* Network context banner */}
        {(selectedCountry || nid) && (
          <div style={{ padding:'7px 32px', background:'rgba(201,168,76,0.08)', borderBottom:`0.5px solid rgba(201,168,76,0.3)`, display:'flex', alignItems:'center', gap:8 }}>
            <span style={{ fontSize:11, color:C.accent, fontWeight:700, letterSpacing:'0.07em', textTransform:'uppercase' }}>Réseau</span>
            <span style={{ width:1, height:12, background:'rgba(201,168,76,0.3)', flexShrink:0 }}/>
            {selectedCountry && (
              <span style={{ fontSize:12, color:C.text, fontWeight:500 }}>
                {COUNTRY_FLAGS[selectedCountry] || ''} {COUNTRY_LABELS[selectedCountry] || selectedCountry}
              </span>
            )}
            {selectedCountry && nid && <span style={{ fontSize:11, color:C.muted }}>›</span>}
            {nid && (
              <span style={{ fontSize:12, color:C.text, fontWeight:500 }}>
                {netLabel(networks.find(n => n.id === nid))}
              </span>
            )}
            <span style={{ fontSize:11, color:C.muted, marginLeft:4 }}>
              · {netUsers.length} membre{netUsers.length !== 1 ? 's' : ''}
            </span>
            <button onClick={() => { setSelectedCountry(null); setSelectedNetwork(null); }}
              style={{ marginLeft:'auto', fontSize:11, color:C.muted, background:'none', border:'none', cursor:'pointer', padding:'2px 6px', borderRadius:4 }}>✕ Effacer filtre</button>
          </div>
        )}
        <div style={{ padding:'28px 32px' }}>
          {renderContent()}
        </div>
      </main>
      {shortcutToast && <ShortcutToast label={shortcutToast} />}
    </div>
  );
}
